import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import './App.css';
import './pages/OverviewPage.css';
import apiService from './services/apiService';
import {
  findNextBotForPlayback,
  findLastBotAfterLastUserMessage,
  findFollowingBotMessage,
} from './services/ttsPipeline';
import { isPoleValidForArtefact, normalizePoleLabel } from './config/ethicalDimensions';
import EthicalDimensionVis from './components/EthicalDimensionVis';
import IntroPage from './pages/IntroPage';
import MobileSingleArtefactIntro from './pages/MobileSingleArtefactIntro';
import BlobSnapshotPage from './components/BlobSnapshotPage';
import { useLanguage } from './contexts/LanguageContext';
import {
  VOICE_INPUT_PREF_KEY,
  VOICE_OUTPUT_PREF_KEY,
  migrateAudioPreferencesLocalStorage,
  readSavedAudioPreferences,
  readVoiceInputPreference,
  readVoiceOutputPreference,
} from './utils/voicePreference';
import { speechLocaleForAppLanguage, useSpeechInput } from './hooks/useSpeechInput';
import { useIdleRedirect } from './hooks/useIdleRedirect';
import {
  beginGetUserMediaPermission,
  beginMicrophonePermissionPrompt,
  finishGetUserMediaPermission,
  isIOSDevice,
  isSafari,
  isMobileBrowser,
  micPermissionErrorTranslationKey,
  releaseMicrophoneAccess,
  resetMicrophonePermissionState,
  shouldUseGetUserMediaForPermission,
} from './utils/micPermission';
import {
  ensureRecordAudioSession,
  restorePlaybackAudioSession,
  setMicCapturePreferred,
  unlockAudioPlayback,
  playTtsBlob,
  stopActiveTtsPlayback,
  isAutoplayBlockedError,
  TtsPlaybackStoppedError,
} from './utils/audioUnlock';
import { normalizeTtsBlobForCache, noteIosPostMicCaptureForPlayback } from './utils/ttsLoudness';
import {
  CHAT_SESSION_STORAGE_KEY,
  clearPersistedChatSession,
  consumeFreshArtefactFromIntro,
  prefsGateStorageKey,
  readChatSessionSnapshot,
  readPersistedSessionIdForPath,
} from './utils/chatSessionStorage';
import {
  isDisplayableVisitorMessage,
  sanitizeMessagesForDisplay,
  sanitizeVisitorMessageForDisplay,
} from './utils/visitorMessageDisplay';
import {
  artefactPath,
  artefactRouteSegmentFromPersona,
  canonicalArtefactPathFromPathname,
  canonicalPersonaKey,
  encodeArtefactPathSegment,
  isBareArtefactLandingPath,
  parseArtefactSessionPath,
} from './utils/artefactSessionPath';

/** Scripted awakening intros must match the artefact in the URL, not stale React state. */
function personaForArtefactRoute(pathname, personasMap, personaKeyAliases, personaRefFallback) {
  const parsed = parseArtefactSessionPath(pathname || '');
  if (personasMap && parsed?.key) {
    return canonicalPersonaKey(parsed.key, personasMap, personaKeyAliases);
  }
  return personaRefFallback;
}

function OnboardingVoiceOptionRow({
  enabled,
  onToggle,
  disabled,
  labelId,
  question,
  actionLabel,
  icon,
  ariaLabel,
}) {
  return (
    <button
      type='button'
      className={`onboarding-voice-option${enabled ? ' is-on' : ''}`}
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={enabled}
      aria-labelledby={labelId}
      aria-label={ariaLabel}>
      <span className='onboarding-voice-option__icon-wrap' aria-hidden='true'>
        {icon}
      </span>
      <span className='onboarding-voice-option__text'>
        <span className='onboarding-voice-option__question' id={labelId}>
          {question}
        </span>
        <span className='onboarding-voice-option__action'>{actionLabel}</span>
      </span>
    </button>
  );
}

function OnboardingVoiceInfoRow({ title, description, icon, sendStyleIcon = false }) {
  return (
    <div className='onboarding-voice-option onboarding-voice-option--info'>
      <span
        className={`onboarding-voice-option__icon-wrap${sendStyleIcon ? ' onboarding-voice-option__icon-wrap--send' : ''}`}
        aria-hidden='true'>
        {icon}
      </span>
      <div className='onboarding-voice-option__text'>
        <span className='onboarding-voice-option__question'>{title}</span>
        <span className='onboarding-voice-option__description'>{description}</span>
      </div>
    </div>
  );
}

function ChatInterfaceGuideSheet({
  open,
  onClose,
  t,
  voiceOutputEnabled,
  onToggleVoiceOutput,
  micInputEnabled,
  micPermissionPending,
  onToggleMicInput,
}) {
  if (!open) return null;

  return (
    <div className='chat-interface-guide is-open' role='presentation'>
      <button
        type='button'
        className='chat-interface-guide__backdrop'
        onClick={onClose}
        aria-label={t('chatGuide.closeAria')}
      />
      <div
        className='chat-interface-guide__sheet'
        role='dialog'
        aria-modal='true'
        aria-labelledby='chat-interface-guide-title'>
        <div className='chat-interface-guide__header'>
          <h2 id='chat-interface-guide-title' className='chat-interface-guide__title'>
            {t('chatGuide.title')}
          </h2>
          <button
            type='button'
            className='chat-interface-guide__close'
            onClick={onClose}
            aria-label={t('chatGuide.closeAria')}>
            <span aria-hidden='true'>&times;</span>
          </button>
        </div>
        <div className='chat-interface-guide__body'>
          <OnboardingVoiceOptionRow
            enabled={voiceOutputEnabled}
            onToggle={onToggleVoiceOutput}
            labelId='guide-audio-label'
            question={t('disclaimer.audioNarrationQuestion')}
            actionLabel={t('disclaimer.audioNarrationTapDescription')}
            icon={
              voiceOutputEnabled ? (
                <IconChatVoiceOn className='onboarding-voice-option__icon-svg' />
              ) : (
                <IconChatVoiceOff className='onboarding-voice-option__icon-svg' />
              )
            }
            ariaLabel={
              voiceOutputEnabled
                ? t('disclaimer.tapToDisableIt')
                : t('disclaimer.tapToEnableIt')
            }
          />
          <hr className='chat-interface-guide__divider' aria-hidden='true' />
          <OnboardingVoiceOptionRow
            enabled={micInputEnabled}
            disabled={micPermissionPending}
            onToggle={onToggleMicInput}
            labelId='guide-mic-label'
            question={t('disclaimer.microphoneQuestion')}
            actionLabel={t('disclaimer.microphoneTapDescription')}
            icon={
              micInputEnabled ? (
                <IconChatMic className='onboarding-voice-option__icon-svg' />
              ) : (
                <IconChatMicOff className='onboarding-voice-option__icon-svg' />
              )
            }
            ariaLabel={
              micInputEnabled
                ? t('disclaimer.tapToDisableIt')
                : t('disclaimer.tapToEnableIt')
            }
          />
          <hr className='chat-interface-guide__divider' aria-hidden='true' />
          <OnboardingVoiceInfoRow
            title={t('disclaimer.sendWhenReadyTitle')}
            description={t('disclaimer.sendWhenReadyDescription')}
            icon={<IconSendUp className='onboarding-voice-option__icon-svg' />}
            sendStyleIcon
          />
        </div>
      </div>
    </div>
  );
}

/** True while chat reload is restoring a non-empty transcript from sessionStorage. */
function isChatViewReloadHydrationPending(pathname) {
  if (typeof window === 'undefined') return false;
  if (parseArtefactSessionPath(pathname)?.view !== 'chat') return false;
  const snap = readChatSessionSnapshot();
  if (!snap?.sessionId) return false;
  const msgs = Array.isArray(snap.messages) ? snap.messages : [];
  return msgs.length > 0;
}

/** True on full-page refresh of a chat URL with a persisted session (never replay __start__). */
function isChatPageReload(pathname) {
  if (typeof window === 'undefined') return false;
  if (parseArtefactSessionPath(pathname)?.view !== 'chat') return false;
  const nav = window.performance?.getEntriesByType?.('navigation')?.[0];
  if (nav?.type !== 'reload') return false;
  const snap = readChatSessionSnapshot();
  return Boolean(snap?.sessionId);
}

function hasExistingChatTranscript(messages, snapshot) {
  const local = Array.isArray(messages) ? messages : [];
  if (local.length > 0) return true;
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  if (snapMsgs.length > 0) return true;
  return Boolean(snapshot?.conversationClosed);
}

/**
 * True on full-page chat reload when there is prior conversation state to restore
 * (transcript, completed intro, or a pending auto-advance beat). A bare sessionId
 * from initSession alone does not count — that would block a fresh chat visit.
 */
function isChatViewReloadSessionRestorePending(pathname) {
  if (typeof window === 'undefined') return false;
  if (parseArtefactSessionPath(pathname)?.view !== 'chat') return false;
  const snap = readChatSessionSnapshot();
  if (!snap?.sessionId) return false;
  if (isChatPageReload(pathname) && hasExistingChatTranscript([], snap)) {
    return true;
  }
  const msgs = Array.isArray(snap.messages) ? snap.messages : [];
  if (msgs.length > 0) return true;
  if (Boolean(snap.conversationClosed)) return true;
  if (Boolean(snap.pendingAutoAdvance)) return true;
  try {
    return sessionStorage.getItem('introduction_completed') === 'true';
  } catch {
    return false;
  }
}

// Closure thresholds
const MAX_USER_INTERACTIONS = 30;
const MAX_CONVERSATION_TIME_MS = 20 * 60 * 1000; // 20 minutes
const CHAT_IDLE_REDIRECT_MS = 10 * 60 * 1000; // 10 minutes without sending a message or changing view → home
/** Backend expands this to a synthetic user turn (not shown in chat). */
const INQUIRY_SILENCE_TRIGGER = '__inquiry_silence__';
const CLOSURE_FALLBACK_RITUAL = {
  en: 'Our encounter must now come to its close. I will retain no memory of this dialogue, yet you may keep whatever insights it offered. Thank you for reflecting with me.',
  el: 'Όμως η συνάντησή μας πρέπει τώρα να φτάσει στο τέλος της. Δεν θα διατηρήσω καμία μνήμη αυτού του διαλόγου, ενώ εσείς μπορείτε να κρατήσετε ό,τι σας πρόσφερε. Σας ευχαριστώ που αναλογιστήκατε μαζί μου.',
};
/** Pause between bot bubbles when one response splits into multiple rows (~5s pacing). */
const STAGGER_BOT_BUBBLE_MS = 5200;
/** Fixed gap between Agent 4 bridge and Agent 2 memory (same user turn, not next reply). */
const AGENT4_TO_AGENT2_GAP_MS = 500;
/** Awakening intro lines when voice/TTS is off (faster than discussion stagger). */
const INTRO_BOT_BUBBLE_GAP_MS = 1800;
/** Minimum pause between consecutive TTS clips (back-to-back spoken lines). */
const TTS_INTER_AUDIO_GAP_MS = 500;

async function waitTtsInterAudioGap(shouldAbort) {
  const deadline = Date.now() + TTS_INTER_AUDIO_GAP_MS;
  while (Date.now() < deadline) {
    if (shouldAbort()) return;
    await new Promise((resolve) => {
      setTimeout(resolve, Math.min(100, Math.max(0, deadline - Date.now())));
    });
  }
}
/** Chat composer grows with content up to this height, then scrolls inside the field. */
const CHAT_INPUT_MAX_HEIGHT_PX = 160;
/** Default visible height for the expanded composer textarea (keep in sync with --chat-input-expanded-min-height). */
const CHAT_INPUT_EXPANDED_MIN_HEIGHT_PX = 20;
/** Mic/send embed in composer (keep in sync with --chat-send-embed-size). */
const CHAT_SEND_EMBED_SIZE_PX = 40;

function measurePlaceholderBlockHeight(el, placeholder) {
  const width = el.clientWidth;
  if (!width || !placeholder) return 0;
  const style = getComputedStyle(el);
  const mirror = document.createElement('div');
  mirror.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'visibility:hidden',
    'pointer-events:none',
    `width:${width}px`,
    `font-size:${style.fontSize}`,
    `font-family:${style.fontFamily}`,
    `line-height:${style.lineHeight}`,
    'padding:0',
    'border:0',
    'white-space:pre-wrap',
    'overflow-wrap:break-word',
    'box-sizing:border-box',
  ].join(';');
  mirror.textContent = placeholder;
  document.body.appendChild(mirror);
  const height = mirror.scrollHeight;
  mirror.remove();
  return height;
}

function isPlaceholderSingleLine(el, placeholder) {
  const width = el.clientWidth;
  if (!width || !placeholder) return true;
  const style = getComputedStyle(el);
  const mirror = document.createElement('span');
  mirror.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'visibility:hidden',
    'white-space:nowrap',
    `font-size:${style.fontSize}`,
    `font-family:${style.fontFamily}`,
  ].join(';');
  mirror.textContent = placeholder;
  document.body.appendChild(mirror);
  const singleLine = mirror.offsetWidth <= width;
  mirror.remove();
  return singleLine;
}

function LegacyChatToV2Redirect({ personasMap, personaKeyAliases }) {
  const { artefactKey } = useParams();
  if (!artefactKey) return <Navigate to='/' replace />;
  const legacyPath = `/${artefactKey}/chat`;
  const desired =
    personasMap && personaKeyAliases
      ? canonicalArtefactPathFromPathname(legacyPath, personasMap, personaKeyAliases)
      : legacyPath;
  return <Navigate to={desired || legacyPath} replace />;
}

/**
 * Bare /{segment}: narrow viewports show single-artefact intro; Continue → /{segment}/disclaimer.
 * From 768px width (iPad portrait and up) redirect straight to /{segment}/disclaimer.
 */
const BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX = 768;

function ArtefactBareKeyLanding({ personasMap, personaKeyAliases }) {
  const { artefactsKey } = useParams();
  const navigate = useNavigate();
  const [wideViewport, setWideViewport] = useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth >= BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX
  );

  useEffect(() => {
    const sync = () =>
      setWideViewport(window.innerWidth >= BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  if (!artefactsKey) return <Navigate to='/' replace />;

  const resolvedKey =
    personasMap && personaKeyAliases
      ? canonicalPersonaKey(artefactsKey, personasMap, personaKeyAliases)
      : artefactsKey;
  const disclaimerPath =
    personasMap && personasMap[resolvedKey]
      ? artefactPath(resolvedKey, 'disclaimer', personasMap)
      : `/${artefactsKey}/disclaimer`;

  if (wideViewport) {
    return <Navigate to={disclaimerPath} replace />;
  }
  return (
    <MobileSingleArtefactIntro
      routeSegment={artefactsKey}
      onContinue={() => navigate(disclaimerPath)}
    />
  );
}

function isInvalidReloadUserContent(content) {
  return !isDisplayableVisitorMessage(content);
}

function uiMessagesFromApiRecords(records) {
  if (!Array.isArray(records)) return [];
  return records
    .map((row) => {
      const role = row.role === 'user' ? 'user' : 'bot';
      const raw = String(row.message ?? '');
      const content =
        role === 'user' ? sanitizeVisitorMessageForDisplay(raw) ?? '' : raw;
      if (role === 'user' && !content) return null;
      return {
        id: row.id || `db_${String(row.timestamp || '')}_${Math.random().toString(36).slice(2, 9)}`,
        type: role,
        content,
        timestamp: row.timestamp
          ? new Date(row.timestamp).toLocaleTimeString()
          : new Date().toLocaleTimeString(),
      };
    })
    .filter(Boolean);
}

function normalizeReloadMessageContent(content) {
  return String(content ?? '').trim();
}

function reloadMessageContentKey(message) {
  if (!message || (message.type !== 'user' && message.type !== 'bot')) return '';
  let content = normalizeReloadMessageContent(message.content);
  if (message.type === 'user') {
    const display = sanitizeVisitorMessageForDisplay(content);
    if (!display) return '';
    content = display;
  }
  return `${message.type}:${content}`;
}

/** On chat reload: keep snapshot UI richness; append any newer rows from the API transcript. */
function mergeChatReloadTranscript(snapshotMessages, apiMessages, introSegmentTexts) {
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const api = Array.isArray(apiMessages) ? apiMessages : [];
  if (api.length === 0) return snap;
  if (snap.length === 0) return api;

  const seen = new Set(snap.map(reloadMessageContentKey).filter(Boolean));
  let merged = [...snap];
  for (const row of api) {
    const key = reloadMessageContentKey(row);
    if (!key || seen.has(key)) continue;
    merged.push(row);
    seen.add(key);
  }
  merged = preferSnapshotIntroSegmentsOnMerge(snap, merged, introSegmentTexts);
  return enrichReloadMessagesFromSnapshot(snap, merged);
}

/** Copy chip metadata from tab snapshot rows onto API/plain transcript rows (matched by content). */
function enrichReloadMessagesFromSnapshot(snapshotMessages, messages) {
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const rows = Array.isArray(messages) ? messages : [];
  if (snap.length === 0) return rows;

  const snapByKey = new Map();
  for (const m of snap) {
    const key = reloadMessageContentKey(m);
    if (key && !snapByKey.has(key)) snapByKey.set(key, m);
  }

  return rows.map((m) => {
    const key = reloadMessageContentKey(m);
    const fromSnap = key ? snapByKey.get(key) : null;
    if (!fromSnap) return m;

    const enriched = { ...m };
    if (Array.isArray(fromSnap.options) && fromSnap.options.length > 0) {
      enriched.options = fromSnap.options;
    }
    if (fromSnap.optionsFooter != null && String(fromSnap.optionsFooter).trim()) {
      enriched.optionsFooter = fromSnap.optionsFooter;
    }
    if (fromSnap.optionsAllowMultiple != null) {
      enriched.optionsAllowMultiple = fromSnap.optionsAllowMultiple;
    }
    if (fromSnap.optionsResolvedSelection != null) {
      enriched.optionsResolvedSelection = fromSnap.optionsResolvedSelection;
    }
    if (fromSnap.id != null) {
      enriched.id = fromSnap.id;
    }
    return enriched;
  });
}

function personaOptionsFromCatalog(raw, idPrefix) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (let i = 0; i < raw.length; i += 1) {
    const item = raw[i];
    if (typeof item === 'string' && item.trim()) {
      out.push({
        key: String.fromCharCode(65 + i),
        label: item.trim(),
        id: `${idPrefix}_${i}`,
      });
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const label = String(item.label ?? item.display_label ?? '').trim();
    if (!label) continue;
    out.push({
      key: String(item.key ?? String.fromCharCode(65 + i)),
      label,
      id: String(item.id ?? `${idPrefix}_${i}`),
    });
  }
  return out;
}

/** When the API transcript has text only, reattach awakening chips from persona catalog. */
function attachMissingAwakeningOptionsFromPersona(messages, personaKey, personasMap, language) {
  const persona = personaKey && personasMap ? personasMap[personaKey] : null;
  if (!persona) return messages;
  const isEl = isGreekLanguage(language);

  return messages.map((m) => {
    if (m?.type !== 'bot') return m;
    if (Array.isArray(m.options) && m.options.length > 0) return m;
    const text = String(m.content ?? '');

    if (
      flowMarkerRegex('ethicalQuestion', language).test(text) ||
      /which resonates the most with you/i.test(text)
    ) {
      const options = personaOptionsFromCatalog(persona.artefact_lenses, 'lens');
      if (options.length > 0) {
        return {
          ...m,
          options,
          optionsFooter: isEl ? 'Επίλεξε ένα.' : 'Choose one.',
          optionsAllowMultiple: false,
        };
      }
    }

    if (flowMarkerRegex('standpointQuestion', language).test(text)) {
      const options = personaOptionsFromCatalog(persona.artefact_orientation_options, 'orient');
      if (options.length > 0) {
        return {
          ...m,
          options,
          optionsFooter: isEl ? 'Επίλεξε ένα.' : 'Choose one.',
          optionsAllowMultiple: false,
        };
      }
    }

    if (flowMarkerRegex('pathChoice', language).test(text)) {
      const options = personaOptionsFromCatalog(persona.path_choice_options, 'path');
      if (options.length > 0) {
        return {
          ...m,
          options,
          optionsFooter: isEl ? 'Επίλεξε ένα.' : 'Choose one.',
          optionsAllowMultiple: false,
        };
      }
    }

    return m;
  });
}

const DISCUSSION_OPTIONS_UI_FOOTER_EN = 'You can choose one, or simply type your preference.';
const DISCUSSION_OPTIONS_UI_FOOTER_EL = 'Μπορείς να επιλέξεις ένα, ή να γράψεις την προτίμησή σου.';

function botTextPosesCuratedValueFork(text, block, options, entryMode) {
  const low = String(text || '').trim().toLowerCase();
  if (!low || !block || !Array.isArray(options) || options.length < 2) return false;

  const isDirect = String(entryMode || '').toLowerCase() === 'direct_question';
  const questionKeys = isDirect
    ? ['direct_value_question', 'question_opening', 'immersive_question', 'memory_value_question']
    : ['memory_value_question', 'direct_value_question', 'question_opening'];

  for (const key of questionKeys) {
    const q = String(block[key] || '').trim();
    if (q.length < 12) continue;
    const ql = q.toLowerCase();
    if (low.includes(ql)) return true;
    const anchor = ql.slice(0, 96);
    if (anchor.length >= 28 && low.includes(anchor)) return true;
  }

  let labelHits = 0;
  for (const opt of options) {
    const label = String(opt?.label ?? '').trim().toLowerCase();
    if (label.length > 2 && low.includes(label)) {
      labelHits += 1;
    }
  }
  if (labelHits >= 2) return true;

  const rawKey = isDirect ? 'direct_value_options' : 'memory_value_options';
  const rawOpts = block[rawKey] || block.value_options || [];
  let aliasHits = 0;
  for (const raw of rawOpts) {
    if (!raw || typeof raw !== 'object') continue;
    const candidates = [String(raw.label ?? '').trim()];
    for (const alias of raw.aliases || []) {
      const a = String(alias ?? '').trim();
      if (a) candidates.push(a);
    }
    if (candidates.some((c) => c.length > 2 && low.includes(c.toLowerCase()))) {
      aliasHits += 1;
    }
  }
  return aliasHits >= 2;
}

/** Reattach Inquirer/Observer value-fork chips when the API sent the question text only. */
function attachMissingDiscussionOptionsFromPersona(messages, persona, language, entryMode) {
  if (!persona) return messages;
  const blocks = persona.narrative_question_blocks;
  if (!Array.isArray(blocks) || blocks.length === 0) return messages;
  const isEl = isGreekLanguage(language);
  const isDirect = String(entryMode || '').toLowerCase() === 'direct_question';
  const optionsKey = isDirect ? 'direct_value_options' : 'memory_value_options';
  const footer = isEl ? DISCUSSION_OPTIONS_UI_FOOTER_EL : DISCUSSION_OPTIONS_UI_FOOTER_EN;

  return messages.map((m) => {
    if (m?.type !== 'bot') return m;
    if (Array.isArray(m.options) && m.options.length > 0) return m;
    const text = String(m.content ?? '');
    if (!assistantReplyHasQuestion(text, language)) return m;

    for (const block of blocks) {
      const rawOpts = block?.[optionsKey] || block?.value_options;
      const options = personaOptionsFromCatalog(rawOpts, 'val');
      if (options.length < 2) continue;
      if (!botTextPosesCuratedValueFork(text, block, options, entryMode)) continue;
      return {
        ...m,
        options,
        optionsFooter: footer,
        optionsAllowMultiple: false,
      };
    }
    return m;
  });
}

function enrichBotMessagesWithCatalogOptions(
  messages,
  personaKey,
  personasMap,
  language,
  entryMode,
) {
  const persona = personaKey && personasMap ? personasMap[personaKey] : null;
  let rows = attachMissingAwakeningOptionsFromPersona(
    messages,
    personaKey,
    personasMap,
    language,
  );
  rows = attachMissingDiscussionOptionsFromPersona(rows, persona, language, entryMode);
  return rows;
}

function discussionBotsFromApiResponse(response, personaKey, personasMap, language) {
  return enrichBotMessagesWithCatalogOptions(
    botMessagesFromDiscussionResponse(response),
    personaKey,
    personasMap,
    language,
    response?.entry_mode,
  );
}

function finalizeReloadedChatMessages(messages, snapshotMessages, snapshot, personasMap, personaKeyAliases, language) {
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const personaKey = snapshot?.currentPersona
    ? canonicalPersonaKey(snapshot.currentPersona, personasMap, personaKeyAliases)
    : null;
  const introTexts = resolveCanonicalIntroSegmentTexts(
    personaKey,
    personasMap,
    snapshot,
    messages,
    language,
  );
  let rows = enrichReloadMessagesFromSnapshot(snap, messages);
  rows = ensureIntroSegmentRowsForReload(snap, rows, introTexts);
  rows = reorderIntroPhaseReloadMessages(rows, snapshot, introTexts);
  rows = enrichBotMessagesWithCatalogOptions(
    rows,
    personaKey,
    personasMap,
    language,
    snapshot?.entryMode,
  );
  return sanitizeMessagesForDisplay(rows);
}

function reconcileOptionsUiStateAfterReload(messages, snapshot) {
  const ids = new Set((Array.isArray(messages) ? messages : []).map((m) => m.id));
  let answered = Array.isArray(snapshot?.answeredOptionMessageIds)
    ? snapshot.answeredOptionMessageIds.filter((id) => ids.has(id))
    : [];

  const pending = firstPendingOptionsMessage(messages, answered);
  if (pending) {
    return {
      optionsShown: true,
      optionsMessageId: pending.id,
      optionsSubmitted: false,
      answeredOptionMessageIds: answered,
    };
  }

  return {
    optionsShown: false,
    optionsMessageId: null,
    optionsSubmitted: true,
    answeredOptionMessageIds: answered,
  };
}

function applyReloadedOptionsUiState(messages, snapshot, setters) {
  const ui = reconcileOptionsUiStateAfterReload(messages, snapshot);
  setters.setOptionsShown(ui.optionsShown);
  setters.setOptionsMessageId(ui.optionsMessageId);
  setters.setOptionsSubmitted(ui.optionsSubmitted);
  setters.setAnsweredOptionMessageIds(ui.answeredOptionMessageIds);
}

function lastReloadMessageIsUser(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return false;
  const last = messages[messages.length - 1];
  return last?.type === 'user' && isDisplayableVisitorMessage(last.content);
}

function apiTranscriptEndsWithUser(apiMessages, userContent) {
  if (!Array.isArray(apiMessages) || apiMessages.length === 0) return false;
  const last = apiMessages[apiMessages.length - 1];
  if (last?.type !== 'user') return false;
  const lastDisplay = sanitizeVisitorMessageForDisplay(last.content);
  const needle = sanitizeVisitorMessageForDisplay(userContent);
  return Boolean(lastDisplay && needle && lastDisplay === needle);
}

function checkpointHasVisitorLine(apiMessages, userContent) {
  const needle = sanitizeVisitorMessageForDisplay(userContent);
  if (!needle) return false;
  if (apiTranscriptEndsWithUser(apiMessages, userContent)) return true;
  return apiMessages.some(
    (m) =>
      m?.type === 'user' &&
      sanitizeVisitorMessageForDisplay(m.content) === needle,
  );
}

/** Awakening steps whose next visible beat is delivered only after client ``__auto_advance__``. */
const FLOW_AUTO_ADVANCE_STEPS = new Set([
  'naming',
  'standpoint_prompt',
  'standpoint_ethical_prompt',
  'path_choice',
]);

function flowMarkerRegex(kind, language) {
  const isEl = String(language || 'en').toLowerCase().startsWith('el');
  const markers = {
    namingQuestion: isEl
      ? /πώς θα ήθελες να σε αποκαλώ/i
      : /what is your name/i,
    standpointQuestion: isEl
      ? /όταν σκέφτεσαι πρώτη φορά/i
      : /when you first think about/i,
    ethicalQuestion: isEl
      ? /ποια από τις παρακάτω|αντιδράσεις σου φαίνεται/i
      : /which resonates the most|resonates the most with you/i,
    pathChoice: isEl
      ? /ανάμνηση από το μέλλον|σου κάνω μια ερώτηση/i
      : /hear a memory from my future|ask you a question/i,
  };
  return markers[kind];
}

function transcriptHasBotMatching(messages, pattern) {
  if (!pattern) return false;
  return (Array.isArray(messages) ? messages : []).some(
    (m) => m?.type === 'bot' && botMessageIsRenderable(m) && pattern.test(String(m.content ?? '')),
  );
}

function transcriptHasVisitorMessage(messages) {
  return (Array.isArray(messages) ? messages : []).some(
    (m) => m?.type === 'user' && !isInvalidReloadUserContent(m.content),
  );
}

const AWAKENING_FLOW_STEP_ORDER = [
  'naming',
  'standpoint_prompt',
  'standpoint_ethical_prompt',
  'path_choice',
];

/** True when the transcript already shows beats after ``awakeningStep`` (stale checkpoint guard). */
function transcriptHasAwakeningProgressBeyondStep(messages, awakeningStep, language) {
  const idx = AWAKENING_FLOW_STEP_ORDER.indexOf(awakeningStep);
  if (idx < 0) return false;
  for (let i = idx + 1; i < AWAKENING_FLOW_STEP_ORDER.length; i += 1) {
    if (flowBeatVisibleInTranscript(AWAKENING_FLOW_STEP_ORDER[i], messages, language)) {
      return true;
    }
  }
  return false;
}

function introParagraphParts(content) {
  const text = String(content ?? '').trim();
  if (!text) return [];
  if (/\n\s*\n+/.test(text)) {
    return text.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  }
  return [text];
}

function normalizeIntroSegmentTexts(texts) {
  return (Array.isArray(texts) ? texts : [])
    .map((part) => String(part ?? '').trim())
    .filter(Boolean);
}

function introSegmentContentKey(content) {
  return reloadMessageContentKey({ type: 'bot', content });
}

function snapshotIntroSegmentTexts(snapshot) {
  return normalizeIntroSegmentTexts(snapshot?.introSegmentTexts);
}

function dedupeIntroSegmentTexts(texts) {
  const seen = new Set();
  const out = [];
  for (const t of normalizeIntroSegmentTexts(texts)) {
    const key = introSegmentContentKey(t);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

const ETHICAL_AURA_INTRO_LINE = {
  en: "Follow my questions, and you'll discover your Ethical Aura.",
  el: 'Ακολούθησε τις ερωτήσεις μου και θα ανακαλύψεις την Ηθική σου Αύρα.',
};

function ethicalAuraIntroLine(language) {
  return ETHICAL_AURA_INTRO_LINE[isGreekLanguage(language) ? 'el' : 'en'];
}

function contentIsEthicalAuraIntroLine(content) {
  const key = introSegmentContentKey(content);
  if (!key) return false;
  for (const line of Object.values(ETHICAL_AURA_INTRO_LINE)) {
    if (introSegmentContentKey(line) === key) return true;
  }
  const low = String(content || '').toLowerCase();
  return low.includes('ethical aura') || low.includes('ηθική σου αύρα');
}

function mergeIntroSegmentSources(...sources) {
  let best = [];
  for (const src of sources) {
    const normalized = dedupeIntroSegmentTexts(src);
    if (normalized.length > best.length) best = normalized;
  }
  return best;
}

/** Grey second intro beat — always after artefact name, before naming question. */
function ensureEthicalAuraIntroSegment(texts, language = 'en') {
  const normalized = dedupeIntroSegmentTexts(texts);
  if (normalized.length === 0) return normalized;
  if (normalized.some(contentIsEthicalAuraIntroLine)) return normalized;
  return [normalized[0], ethicalAuraIntroLine(language), ...normalized.slice(1)];
}

function resolveIntroSegmentLanguage(snapshot, messages, language) {
  if (language) return language;
  if (snapshot?.language) return snapshot.language;
  const combined = [
    ...(Array.isArray(snapshot?.messages) ? snapshot.messages : []),
    ...(Array.isArray(messages) ? messages : []),
  ];
  if (combined.some((m) => /ηθική/i.test(String(m?.content || '')))) return 'el';
  return 'en';
}

function introSegmentsFromPersonaCatalog(personaKey, personasMap) {
  const persona = personaKey && personasMap ? personasMap[personaKey] : null;
  return dedupeIntroSegmentTexts(persona?.intro_segments);
}

function resolveCanonicalIntroSegmentTexts(
  personaKey,
  personasMap,
  snapshot,
  messages,
  language,
) {
  const fromBackend = introSegmentsFromPersonaCatalog(personaKey, personasMap);
  const fromSnapshot = snapshotIntroSegmentTexts(snapshot);
  const fromTranscript =
    deriveIntroSegmentTextsFromTranscript([
      ...(Array.isArray(snapshot?.messages) ? snapshot.messages : []),
      ...(Array.isArray(messages) ? messages : []),
    ]) || [];
  const merged = mergeIntroSegmentSources(fromBackend, fromSnapshot, fromTranscript);
  return ensureEthicalAuraIntroSegment(
    merged,
    resolveIntroSegmentLanguage(snapshot, messages, language),
  );
}

function messageIsAwakeningIntroSegment(message, introSegmentTexts) {
  if (!message || message.type !== 'bot') return false;
  const texts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) return message.awakeningIntroSegment === true;
  const key = reloadMessageContentKey(message);
  return texts.some((text) => introSegmentContentKey(text) === key);
}

/** True when bot content is the full __start__ intro or one of its backend segments. */
function contentMatchesIntroCatalog(content, introSegmentTexts) {
  const texts = normalizeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) return false;
  const key = introSegmentContentKey(content);
  if (texts.some((text) => introSegmentContentKey(text) === key)) return true;
  const parts = introParagraphParts(content);
  if (parts.length > 1 && parts.length === texts.length) {
    return parts.every(
      (part, i) =>
        introSegmentContentKey(part) === introSegmentContentKey(texts[i]),
    );
  }
  return false;
}

function splitMonolithicIntroBotMessages(messages, introSegmentTexts) {
  const texts = normalizeIntroSegmentTexts(introSegmentTexts);
  const out = [];
  for (const m of Array.isArray(messages) ? messages : []) {
    if (m?.type !== 'bot' || !contentMatchesIntroCatalog(m.content, texts)) {
      out.push(m);
      continue;
    }
    const parts = introParagraphParts(m.content);
    if (parts.length <= 1) {
      out.push(m.awakeningIntroSegment ? m : { ...m, awakeningIntroSegment: true });
      continue;
    }
    parts.forEach((part, i) => {
      out.push({
        ...m,
        id: i === 0 ? m.id : `${m.id}_intro_${i}`,
        content: part,
        awakeningIntroSegment: true,
      });
    });
  }
  return out;
}

function transcriptShowsNamingQuestion(messages) {
  const rows = Array.isArray(messages) ? messages : [];
  return (
    transcriptHasBotMatching(rows, flowMarkerRegex('namingQuestion', 'en')) ||
    transcriptHasBotMatching(rows, flowMarkerRegex('namingQuestion', 'el'))
  );
}

function deriveIntroSegmentTextsFromTranscript(messages) {
  const rows = Array.isArray(messages) ? messages : [];
  const tagged = rows.filter((m) => m?.awakeningIntroSegment === true);
  if (tagged.length > 0) {
    return tagged.map((m) => normalizeReloadMessageContent(m.content));
  }

  if (transcriptShowsNamingQuestion(rows)) {
    const texts = [];
    for (const m of rows) {
      if (m?.type !== 'bot') continue;
      if (messageMatchesNamingQuestion(m)) break;
      const content = String(m.content ?? '').trim();
      if (content) texts.push(normalizeReloadMessageContent(content));
    }
    if (texts.length > 0) return texts;
  }

  for (const m of rows) {
    if (m?.type !== 'bot') continue;
    const parts = introParagraphParts(m.content);
    if (parts.length > 1) return parts;
    break;
  }
  return undefined;
}

function deriveIntroSegmentTextsForSnapshot(snapshot, messages, personaKey, personasMap) {
  return resolveCanonicalIntroSegmentTexts(personaKey, personasMap, snapshot, messages);
}

function buildIntroCatalogFromTexts(introSegmentTexts, existingMessages, seedMessage) {
  const texts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) return [];

  const byKey = new Map();
  for (const m of Array.isArray(existingMessages) ? existingMessages : []) {
    if (m?.type !== 'bot') continue;
    const key = reloadMessageContentKey(m);
    if (key) byKey.set(key, m);
  }

  const baseId = seedMessage?.id || 'awakening_intro';
  const baseTimestamp = seedMessage?.timestamp ?? null;

  return texts.map((text, i) => {
    const key = introSegmentContentKey(text);
    const existing = byKey.get(key);
    if (existing) {
      return existing.awakeningIntroSegment
        ? existing
        : { ...existing, awakeningIntroSegment: true };
    }
    return {
      type: 'bot',
      id: i === 0 ? baseId : `${baseId}_intro_${i}`,
      content: text,
      timestamp: baseTimestamp,
      awakeningIntroSegment: true,
    };
  });
}

function resolveFullIntroCatalog(snapshotMessages, messages, introSegmentTexts) {
  const texts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) return [];

  const combined = [
    ...(Array.isArray(snapshotMessages) ? snapshotMessages : []),
    ...(Array.isArray(messages) ? messages : []),
  ];
  const split = splitMonolithicIntroBotMessages(combined, texts);
  const seed =
    split.find((m) => messageIsAwakeningIntroSegment(m, texts)) ||
    split.find((m) => m?.type === 'bot' && contentMatchesIntroCatalog(m.content, texts)) ||
    null;
  return buildIntroCatalogFromTexts(texts, split, seed);
}

function snapIntroSegmentsFromMessages(snapMsgs, introSegmentTexts) {
  const texts = normalizeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) return [];
  return splitMonolithicIntroBotMessages(snapMsgs, texts).filter((m) =>
    messageIsAwakeningIntroSegment(m, texts),
  );
}

function introPhaseStillAwaitingNaming(messages, snapshot) {
  return (
    !transcriptHasVisitorMessage(messages) &&
    !introPhaseReloadIntroComplete(snapshot, messages)
  );
}

function introSegmentRevealedForReload(segment, snapIntroSegs, revealedIdList) {
  const key = reloadMessageContentKey(segment);
  const snapSeg = snapIntroSegs.find((s) => reloadMessageContentKey(s) === key);
  return (
    revealedIdList.includes(segment.id) ||
    (snapSeg != null && revealedIdList.includes(snapSeg.id))
  );
}

function introSegmentInVisiblePrefix(segment, visiblePrefixSegments) {
  const key = reloadMessageContentKey(segment);
  if (!key) return false;
  return visiblePrefixSegments.some((s) => reloadMessageContentKey(s) === key);
}

function messageMatchesNamingQuestion(message) {
  if (message?.type !== 'bot') return false;
  const text = String(message.content ?? '');
  return (
    flowMarkerRegex('namingQuestion', 'en').test(text) ||
    flowMarkerRegex('namingQuestion', 'el').test(text)
  );
}

function reloadPersonaKeyFromSnapshot(snapshot, personasMap, personaKeyAliases) {
  return snapshot?.currentPersona
    ? canonicalPersonaKey(snapshot.currentPersona, personasMap, personaKeyAliases)
    : null;
}

function introPhaseReloadIntroComplete(snapshot, messages) {
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const rows = Array.isArray(messages) ? messages : [];
  const combined = [...snapMsgs, ...rows];
  return (
    transcriptShowsNamingQuestion(combined) ||
    (typeof window !== 'undefined' &&
      sessionStorage.getItem('introduction_completed') === 'true' &&
      transcriptShowsNamingQuestion(rows))
  );
}

function introPhaseReloadRevealedSegmentCount(snapshot, messages, introSegmentTexts) {
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return 0;
  const snapIntroSegs = snapIntroSegmentsFromMessages(snapMsgs, introTexts);
  const revealedIdList = Array.isArray(snapshot?.ttsRevealedBotIds)
    ? snapshot.ttsRevealedBotIds
    : [];

  const catalog = resolveFullIntroCatalog(snapMsgs, messages, introTexts);
  if (catalog.length === 0) return 0;

  if (introPhaseReloadIntroComplete(snapshot, messages)) {
    return catalog.length;
  }

  let count = 0;
  for (const seg of catalog) {
    if (introSegmentRevealedForReload(seg, snapIntroSegs, revealedIdList)) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

function introPhaseReloadVisibleIntroPrefix(messages, snapshot, introSegmentTexts) {
  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return [];
  const rows = splitMonolithicIntroBotMessages(messages, introTexts);
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const catalog = resolveFullIntroCatalog(snapMsgs, rows, introTexts);
  if (catalog.length === 0) return [];

  const showCount = introPhaseReloadRevealedSegmentCount(snapshot, rows, introTexts);
  if (showCount === 0) return [];
  return catalog.slice(0, showCount);
}

function preferSnapshotIntroSegmentsOnMerge(snapshotMessages, merged, introSegmentTexts) {
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return merged;

  const mergedRows = splitMonolithicIntroBotMessages(merged, introTexts);
  const nonIntro = mergedRows.filter(
    (m) => m.type !== 'bot' || !messageIsAwakeningIntroSegment(m, introTexts),
  );

  const introPhase =
    !snap.some((m) => m.type === 'user' && !isInvalidReloadUserContent(m.content));
  const namingVisible = transcriptShowsNamingQuestion([...snap, ...mergedRows]);
  const catalog = resolveFullIntroCatalog(snap, mergedRows, introTexts);

  if (catalog.length > 0) {
    if (introPhase && !namingVisible) {
      return catalog;
    }
    return [...catalog, ...nonIntro];
  }

  return merged;
}

/** Ensure split intro rows exist for reload (snapshot + API) before visibility trim. */
function ensureIntroSegmentRowsForReload(snapshotMessages, messages, introSegmentTexts) {
  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return messages;
  const rows = splitMonolithicIntroBotMessages(messages, introTexts);
  const nonIntro = rows.filter(
    (m) => m.type !== 'bot' || !messageIsAwakeningIntroSegment(m, introTexts),
  );

  const hasVisitor =
    snap.some((m) => m.type === 'user' && !isInvalidReloadUserContent(m.content)) ||
    rows.some((m) => m.type === 'user' && !isInvalidReloadUserContent(m.content));

  if (hasVisitor) {
    return messages;
  }

  const catalog = resolveFullIntroCatalog(snap, rows, introTexts);
  if (catalog.length > 0) {
    if (introPhaseStillAwaitingNaming(rows, { messages: snap })) {
      return catalog;
    }
    return [...catalog, ...nonIntro];
  }

  return messages;
}

function reorderIntroPhaseReloadMessages(messages, snapshot, introSegmentTexts) {
  const stage = snapshot?.conversationStage || 'introduction';
  if (stage !== 'introduction' || transcriptHasVisitorMessage(messages)) {
    return messages;
  }

  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return messages;
  const rows = splitMonolithicIntroBotMessages(messages, introTexts);
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const catalog = resolveFullIntroCatalog(snapMsgs, rows, introTexts);
  if (catalog.length === 0) return messages;

  const nonIntro = rows.filter(
    (m) => m.type !== 'bot' || !messageIsAwakeningIntroSegment(m, introTexts),
  );

  const introRows = [];
  const usedIds = new Set();
  for (const catSeg of catalog) {
    const key = reloadMessageContentKey(catSeg);
    const hit = rows.find(
      (m) =>
        m.type === 'bot' &&
        !usedIds.has(m.id) &&
        reloadMessageContentKey(m) === key,
    );
    if (hit) {
      introRows.push({ ...hit, awakeningIntroSegment: true });
      usedIds.add(hit.id);
    } else {
      introRows.push(catSeg);
    }
  }

  if (introPhaseStillAwaitingNaming(rows, snapshot)) {
    return introRows;
  }
  return [...introRows, ...nonIntro];
}

function introPhaseReloadRevealedBotIds(messages, snapshot, introSegmentTexts) {
  const stage = snapshot?.conversationStage || 'introduction';
  if (stage !== 'introduction' || transcriptHasVisitorMessage(messages)) {
    return null;
  }

  const introTexts = dedupeIntroSegmentTexts(introSegmentTexts);
  if (introTexts.length === 0) return null;
  const rows = splitMonolithicIntroBotMessages(messages, introTexts);
  const snapMsgs = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const fullCatalog = resolveFullIntroCatalog(snapMsgs, rows, introTexts);
  const visiblePrefix = introPhaseReloadVisibleIntroPrefix(messages, snapshot, introTexts);
  const introFullyRevealed =
    visiblePrefix.length >= fullCatalog.length && fullCatalog.length > 0;
  const introBeatComplete = introPhaseReloadIntroComplete(snapshot, messages);

  const revealed = [];
  for (const m of rows) {
    if (m?.type !== 'bot') continue;
    if (messageIsAwakeningIntroSegment(m, introTexts)) {
      if (introSegmentInVisiblePrefix(m, visiblePrefix)) revealed.push(m.id);
    } else if (messageMatchesNamingQuestion(m)) {
      if (introFullyRevealed || introBeatComplete) revealed.push(m.id);
    } else if (!introPhaseStillAwaitingNaming(rows, snapshot)) {
      revealed.push(m.id);
    }
  }
  return revealed;
}

function transcriptHasIntroDelivery(messages, introSegmentTexts) {
  const texts = normalizeIntroSegmentTexts(introSegmentTexts);
  if (texts.length === 0) {
    return deriveIntroSegmentTextsFromTranscript(messages)?.length > 0;
  }
  const split = splitMonolithicIntroBotMessages(messages, texts);
  return split.some((m) => messageIsAwakeningIntroSegment(m, texts));
}

function filterIntroReplayBots(newBots, existingMessages, introSegmentTexts) {
  const texts =
    normalizeIntroSegmentTexts(introSegmentTexts).length > 0
      ? normalizeIntroSegmentTexts(introSegmentTexts)
      : deriveIntroSegmentTextsFromTranscript(existingMessages);
  if (!texts || texts.length === 0) return newBots;
  if (!transcriptHasIntroDelivery(existingMessages, texts)) return newBots;
  return newBots.filter((m) => !contentMatchesIntroCatalog(m?.content, texts));
}

/** Map checkpoint ``awakening_step`` to the bot line that should already be visible in chat. */
function flowBeatVisibleInTranscript(awakeningStep, messages, language) {
  switch (awakeningStep) {
    case 'naming':
      return transcriptHasBotMatching(messages, flowMarkerRegex('namingQuestion', language));
    case 'standpoint_prompt':
      return transcriptHasBotMatching(messages, flowMarkerRegex('standpointQuestion', language));
    case 'standpoint_ethical_prompt':
      return transcriptHasBotMatching(messages, flowMarkerRegex('ethicalQuestion', language));
    case 'path_choice':
      return transcriptHasBotMatching(messages, flowMarkerRegex('pathChoice', language));
    default:
      return true;
  }
}

/**
 * Checkpoint is at an auto-advance step but the expected next bot beat is not in the transcript
 * (e.g. ethical aura shown, path-choice invitation still pending).
 */
function resolveMissingFlowBeatAction(mergedMessages, flowState, snapshot, language) {
  const awakeningStep = flowState?.awakening_step;
  if (!FLOW_AUTO_ADVANCE_STEPS.has(awakeningStep)) return null;
  if (!Array.isArray(mergedMessages) || mergedMessages.length === 0) return null;
  if (transcriptHasAwakeningProgressBeyondStep(mergedMessages, awakeningStep, language)) {
    return null;
  }

  const last = mergedMessages[mergedMessages.length - 1];
  if (last?.type === 'user' && !isInvalidReloadUserContent(last.content)) return null;

  const beatVisible = flowBeatVisibleInTranscript(awakeningStep, mergedMessages, language);
  if (beatVisible) return null;

  return {
    prompt: '__auto_advance__',
    isAutoFollowUp: false,
    skipUserPersist: true,
  };
}

function makeChatReloadAutoAdvanceAction() {
  return {
    prompt: '__auto_advance__',
    isAutoFollowUp: false,
    skipUserPersist: true,
  };
}

/** Only resume intro→naming when checkpoint is still at naming and naming question is missing. */
function resolveNamingBeatResumeAction(mergedMessages, flowState, language) {
  if (flowState?.awakening_step !== 'naming') return null;
  if (transcriptHasVisitorMessage(mergedMessages)) return null;
  if (transcriptHasAwakeningProgressBeyondStep(mergedMessages, 'naming', language)) return null;
  if (flowBeatVisibleInTranscript('naming', mergedMessages, language)) return null;
  if (!transcriptHasIntroDelivery(mergedMessages, deriveIntroSegmentTextsFromTranscript(mergedMessages))) {
    return null;
  }
  return makeChatReloadAutoAdvanceAction();
}

function resolvePausedFlowBeatAction(mergedMessages, flowState, snapshot, language) {
  const stage = snapshot?.conversationStage || 'introduction';
  if (stage !== 'introduction') return null;
  if (!Boolean(snapshot?.pendingAutoAdvance) && !Boolean(snapshot?.isLoading)) return null;

  const flowAction = resolveMissingFlowBeatAction(mergedMessages, flowState, snapshot, language);
  if (flowAction) return flowAction;

  const namingResume = resolveNamingBeatResumeAction(mergedMessages, flowState, language);
  if (namingResume) return namingResume;

  const last = mergedMessages[mergedMessages.length - 1];
  if (last?.type !== 'bot') return null;

  const awakeningStep = flowState?.awakening_step;
  if (
    awakeningStep &&
    FLOW_AUTO_ADVANCE_STEPS.has(awakeningStep) &&
    flowBeatVisibleInTranscript(awakeningStep, mergedMessages, language)
  ) {
    return null;
  }

  return makeChatReloadAutoAdvanceAction();
}

function resolveChatReloadPendingAction(
  mergedMessages,
  apiMessages,
  snapshot,
  snapshotMessages,
  flowState,
  language,
) {
  if (snapshot?.conversationClosed || snapshot?.agentsStoppedDueToTimeout) return null;
  if (!Array.isArray(mergedMessages) || mergedMessages.length === 0) return null;

  const snap = Array.isArray(snapshotMessages) ? snapshotMessages : [];
  const stage = snapshot?.conversationStage || 'introduction';

  const pausedBeat = resolvePausedFlowBeatAction(
    mergedMessages,
    flowState,
    snapshot,
    language,
  );
  if (pausedBeat) return pausedBeat;

  const introCompleted =
    typeof window !== 'undefined' &&
    sessionStorage.getItem('introduction_completed') === 'true';
  const pastIntroOnly =
    introCompleted &&
    (transcriptHasVisitorMessage(mergedMessages) ||
      transcriptHasBotMatching(mergedMessages, /will remember that|θα το θυμάμαι/i) ||
      flowBeatVisibleInTranscript('naming', mergedMessages, language) ||
      transcriptHasAwakeningProgressBeyondStep(mergedMessages, 'naming', language));

  const last = mergedMessages[mergedMessages.length - 1];
  const wasLoading = Boolean(snapshot?.isLoading);

  if (last.type === 'user') {
    if (isInvalidReloadUserContent(last.content)) return null;
    const mergedBots = mergedMessages.filter((m) => m.type === 'bot').length;
    const apiBots = apiMessages.filter((m) => m.type === 'bot').length;
    if (apiBots > mergedBots) return null;
    const standpointMissing = !flowBeatVisibleInTranscript(
      'standpoint_prompt',
      mergedMessages,
      language,
    );
    const namingThanked = mergedMessages.some(
      (m) =>
        m?.type === 'bot' &&
        /will remember that|θα το θυμάμαι/i.test(String(m.content ?? '')),
    );
    if (namingThanked && standpointMissing) {
      return makeChatReloadAutoAdvanceAction();
    }
    return {
      prompt: last.content,
      isAutoFollowUp: false,
      skipUserPersist: checkpointHasVisitorLine(apiMessages, last.content),
    };
  }

  if (last.type === 'bot' && wasLoading && stage === 'introduction') {
    if (apiMessages.length > mergedMessages.length) return null;
    if (!introCompleted) return null;
    if (!pastIntroOnly) {
      const namingResume = resolveNamingBeatResumeAction(mergedMessages, flowState, language);
      if (namingResume) return namingResume;
    }
    if (flowState?.awakening_step && FLOW_AUTO_ADVANCE_STEPS.has(flowState.awakening_step)) {
      return resolveMissingFlowBeatAction(mergedMessages, flowState, snapshot, language);
    }
    return null;
  }

  const flowAction = resolveMissingFlowBeatAction(
    mergedMessages,
    flowState,
    snapshot,
    language,
  );
  if (flowAction) return flowAction;

  if (!pastIntroOnly) {
    const namingResume = resolveNamingBeatResumeAction(mergedMessages, flowState, language);
    if (namingResume) return namingResume;
  }

  if (
    last.type === 'bot' &&
    Boolean(snapshot?.pendingAutoAdvance) &&
    stage === 'introduction'
  ) {
    const pendingResume = pastIntroOnly
      ? resolveMissingFlowBeatAction(mergedMessages, flowState, snapshot, language)
      : resolveMissingFlowBeatAction(mergedMessages, flowState, snapshot, language) ||
        resolveNamingBeatResumeAction(mergedMessages, flowState, language);
    if (pendingResume) return pendingResume;
  }

  if (
    last.type === 'bot' &&
    stage === 'introduction' &&
    /will remember that|θα το θυμάμαι/i.test(String(last.content ?? '')) &&
    !flowBeatVisibleInTranscript('standpoint_prompt', mergedMessages, language)
  ) {
    return makeChatReloadAutoAdvanceAction();
  }

  return null;
}

function scheduleConversationFlowResume({
  snapshot,
  snapshotMessages,
  mergedMessages,
  apiMessages,
  flowState,
  language,
  setPendingReply,
}) {
  if (!setPendingReply) return;
  const action = resolveChatReloadPendingAction(
    mergedMessages,
    apiMessages,
    snapshot,
    snapshotMessages,
    flowState,
    language,
  );
  if (action) setPendingReply(action);
}

function scheduleChatReloadPendingReply({
  isChatViewReload,
  snapshot,
  snapshotMessages,
  mergedMessages,
  apiMessages,
  flowState,
  language,
  setPendingReply,
}) {
  if (!isChatViewReload) return;
  scheduleConversationFlowResume({
    snapshot,
    snapshotMessages,
    mergedMessages,
    apiMessages,
    flowState,
    language,
    setPendingReply,
  });
}

/** Oldest bot message that still has an unanswered option set (chronological FIFO). */
function firstPendingOptionsMessage(messages, answeredIds) {
  const answered = new Set(answeredIds);
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (
      m.type === 'bot' &&
      Array.isArray(m.options) &&
      m.options.length > 0 &&
      !answered.has(m.id)
    ) {
      return m;
    }
  }
  return null;
}

/** Footer copy that means pick a chip only (composer text input off). */
function optionsFooterRequiresChipOnly(footer) {
  const text = String(footer ?? '').trim();
  if (!text) return false;
  return /^choose one\.?$/i.test(text) || /^επιλέξτε ένα\.?$/iu.test(text);
}

/** Chip-only options: awakening forks with "Choose one." or memory vs question path choice. */
function messageOptionsRequireChipOnly(message) {
  if (!message) return false;
  if (isMemoryOrQuestionForkMessage(message)) return true;
  return optionsFooterRequiresChipOnly(message.optionsFooter);
}

/** Skip empty bot bubbles (timestamp-only rows), e.g. awakening steps that only schedule auto-advance. */
function botMessageIsRenderable(message) {
  if (!message || message.type !== 'bot') return true;
  if (message.waitForSignal) return true;
  if (message.isClosure) return true;
  if (String(message.content ?? '').trim()) return true;
  if (Array.isArray(message.options) && message.options.length > 0) return true;
  if (String(message.optionsFooter ?? '').trim()) return true;
  return false;
}

function isGreekLanguage(language) {
  return String(language || 'en').toLowerCase().startsWith('el');
}

/** Greek interrogative at sentence end: ``;`` (standard) or terminal ``:``. */
function greekTextHasTerminalQuestion(text) {
  const s = String(text || '');
  return /(?:;|:(?:\s*["'»»)]\s*)?)\s*(?:\n|$)/.test(s)
    || /(?:;|:(?:\s*["'»»)]\s*)?)\s*$/.test(s.trimEnd());
}

/** True if the assistant text asks something (controls multi-bubble splits). */
function assistantReplyHasQuestion(text, language = 'en') {
  const s = String(text || '').trim();
  if (/[?？]/.test(s)) return true;
  return isGreekLanguage(language) && greekTextHasTerminalQuestion(s);
}

/**
 * True when the memory beat already asked the visitor, so skip the separate hear-more idle bubble.
 * Greek memories are narrative; punctuation must not block the hear-more prompt.
 */
function memoryHearMoreIdleSuppressed(text, language = 'en') {
  if (isGreekLanguage(language)) return false;
  const s = String(text ?? '').trimEnd();
  if (!s) return false;
  const ch = s[s.length - 1];
  return ch === '?' || ch === '？';
}

/** True when the visible opening line already closes with a question mark. */
function assistantTextEndsWithQuestion(text, language = 'en') {
  const s = String(text ?? '').trimEnd();
  if (!s) return false;
  const ch = s[s.length - 1];
  if (ch === '?' || ch === '？') return true;
  if (!isGreekLanguage(language)) return false;
  if (ch === ';' || ch === ':') return true;
  if (s.length >= 2 && (s[s.length - 2] === ';' || s[s.length - 2] === ':')) {
    return /["'»»)]/.test(ch);
  }
  return false;
}

const MEMORY_IDLE_FOLLOW_UP_MS = 20000;

/** Text shown in the composer when the user picks chips (same shape as the outgoing user line). */
function optionLabelForInput(option) {
  const label = String(option?.label ?? '').trim();
  const key = String(option?.key ?? '').trim();
  if (!label) return '';

  if (key) {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const withoutMatchingKey = label.replace(
      new RegExp(`^\\s*${escapedKey}\\s*[).:\\-]\\s*`, 'i'),
      ''
    );
    if (withoutMatchingKey !== label) return withoutMatchingKey.trim();
  }

  return label.replace(/^\s*[A-Z]\s*[).:-]\s*/i, '').trim();
}

function formatSelectedOptionsForInput(options) {
  if (!Array.isArray(options) || options.length === 0) return '';
  return [...options]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(optionLabelForInput)
    .join(', ');
}

function IconSendUp(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M12 19V5M5 12l7-7 7 7' />
    </svg>
  );
}

function IconArrowLeft(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M19 12H5M12 19l-7-7 7-7' />
    </svg>
  );
}

/** Composer footer: rounded speaker + waves (distinct from disclaimer / prefs controls). */
function IconChatVoiceOn(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='22'
      height='22'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M6 10v4a1 1 0 0 0 1 1h2l4 3V6l-4 3H7a1 1 0 0 0-1 1z' />
      <path d='M15.54 8.46a5 5 0 0 1 0 7.07' />
      <path d='M18.07 5.93a9 9 0 0 1 0 12.14' />
    </svg>
  );
}

function IconChatVoiceOff(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='22'
      height='22'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M6 10v4a1 1 0 0 0 1 1h2l4 3V6l-4 3H7a1 1 0 0 0-1 1z' />
      <path d='M17 10l6 6M23 10l-6 6' />
    </svg>
  );
}

/** Composer pill: dictate visitor message (speech-to-text). */
function IconChatMic(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' />
      <path d='M19 10v2a7 7 0 0 1-14 0v-2' />
      <path d='M12 19v4M8 23h8' />
    </svg>
  );
}

function IconChatMicOff(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      {...props}>
      <path d='M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' />
      <path d='M19 10v2a7 7 0 0 1-14 0v-2' />
      <path d='M12 19v4M8 23h8' />
      <path d='M4 22L20 4' />
    </svg>
  );
}

/**
 * Discussion: one bubble per API reply unless the backend sends `assistant_segments`
 * (intentional two-beat relay). We do not client-split long persona/memory narration into
 * multiple bubbles. Option chips stay on the last bubble only when segments exist.
 */
function botMessagesFromDiscussionResponse(response) {
  const segRaw = Array.isArray(response?.assistant_segments)
    ? response.assistant_segments.map((s) => String(s ?? '').trim()).filter(Boolean)
    : [];
  /** When API puts text only in assistant_segments, fill combined line for rendering & split logic. */
  let raw = String(response?.content ?? '').trim();
  if (!raw && segRaw.length > 0) {
    raw = segRaw.join('\n\n').trim();
  }

  const hasOptions = Array.isArray(response?.options) && response.options.length > 0;
  const footer = response?.options_footer ?? null;
  const allowMulti = Boolean(response?.options_allow_multiple);
  const closureTurn = Boolean(response?.visitor_initiated_closure);

  const ts = new Date().toLocaleTimeString();
  const idBase = Date.now() + 1;

  const makeMsg = (id, content, opts, foot, multi, extra = {}) => ({
    id,
    type: 'bot',
    content,
    timestamp: ts,
    options: opts,
    optionsFooter: foot,
    optionsAllowMultiple: multi,
    ...(closureTurn ? { isClosure: true } : {}),
    ...extra,
  });

  const agent4Fallback = Boolean(response?.agent4_fallback);

  /** Backend split (e.g. narration relay): one bubble per segment; chips on the last only. */
  if (segRaw.length >= 2) {
    return segRaw
      .map((content, i) =>
        makeMsg(
          idBase + i,
          content,
          i === segRaw.length - 1 && hasOptions ? response.options : null,
          i === segRaw.length - 1 && hasOptions ? footer : null,
          i === segRaw.length - 1 && hasOptions ? allowMulti : false,
          agent4Fallback && i === 0 ? { agent4Fallback: true } : {},
        )
      )
      .filter(botMessageIsRenderable);
  }

  const m = makeMsg(
    idBase,
    raw,
    hasOptions ? response.options : null,
    hasOptions ? footer : null,
    allowMulti,
    agent4Fallback ? { agent4Fallback: true } : {},
  );
  return botMessageIsRenderable(m) ? [m] : [];
}

function normalizeBotContentForDedupe(text) {
  return String(text || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Drop bot bubbles whose text already appears in chat (prevents queue + API double delivery). */
function filterDuplicateBotMessages(existingMessages, botsToAdd) {
  if (!botsToAdd?.length) return [];
  const seen = new Set();
  for (const m of existingMessages || []) {
    if (m?.type === 'bot' && m.content) {
      seen.add(normalizeBotContentForDedupe(m.content));
    }
  }
  const out = [];
  for (const b of botsToAdd) {
    const norm = normalizeBotContentForDedupe(b.content);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    out.push(b);
  }
  return out;
}

/** Wait until TTS marks the given bot line(s) finished (or timeout — then play queue anyway). */
async function waitForBotVoicePlayback(playbackFinishedRef, botIds, ttsPipelineWakeRef, timeoutMs = 90000) {
  const ids = (Array.isArray(botIds) ? botIds : [botIds]).filter(Boolean);
  if (!ids.length) return;
  ttsPipelineWakeRef?.();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (ids.every((id) => playbackFinishedRef.current?.has(id))) return;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

/**
 * After Agent 4 fallback, play/show the recovery line first, then flush deferred memory segments.
 */
async function flushDeferredMemoryQueueAfterFallback({
  savedQueue,
  dedupeContext,
  fallbackBots,
  flushPendingMemoryBots,
  setIsLoading,
}) {
  if (!savedQueue?.length) return false;

  if ((fallbackBots || []).length) {
    setIsLoading?.(true);
    await new Promise((resolve) => setTimeout(resolve, AGENT4_TO_AGENT2_GAP_MS));
  }

  const queueToFlush = filterDuplicateBotMessages(dedupeContext, savedQueue);
  if (!queueToFlush.length) return false;

  const dedupeForQueue = [...dedupeContext, ...(fallbackBots || [])];
  return flushPendingMemoryBots(dedupeForQueue, queueToFlush);
}

/** Tag Agent 4 fallback bubbles so low-energy streak resets after recovery. */
function tagAgent4FallbackBots(bots) {
  if (!bots?.length) return [];
  return bots.map((b) => ({ ...b, agent4Fallback: true }));
}

/** Tag deferred memory continuation bubbles (Agent 2) for optional UI styling. */
function tagAgent2MemoryBots(bots) {
  if (!bots?.length) return [];
  return bots.map((b) => ({ ...b, agent2MemoryContinuation: true }));
}

/**
 * Deferred memory ack: show Agent 4 bridge first, then Agent 2 memory (API chain or queued segments).
 */
async function deliverAgent4ThenAgent2AfterDefer({
  newBots,
  savedPendingMemoryQueue,
  dedupeContext,
  voicePipelineActive,
  flushPendingMemoryBots,
  clearAllMemoryIdleTimers,
  setMessages,
  setIsLoading,
  prefetchBotsForVoice,
  sessionBlockedRef,
}) {
  clearAllMemoryIdleTimers?.();

  let didStagger = false;
  let appendedFallbackBots = [];
  const fallbackBubble =
    newBots?.length > 0 ? tagAgent4FallbackBots([newBots[0]]) : [];
  const agent2FromApi =
    newBots?.length > 1 ? tagAgent2MemoryBots(newBots.slice(1)) : [];

  if (fallbackBubble.length) {
    appendedFallbackBots = filterDuplicateBotMessages(dedupeContext, fallbackBubble);
    didStagger = await appendBotResponseMessages(
      setMessages,
      setIsLoading,
      fallbackBubble,
      voicePipelineActive,
      prefetchBotsForVoice,
      dedupeContext,
      sessionBlockedRef,
    );
    dedupeContext.push(...appendedFallbackBots);
  }

  const agent2Queue = agent2FromApi.length
    ? agent2FromApi
    : tagAgent2MemoryBots(savedPendingMemoryQueue || []);

  if (!agent2Queue.length) {
    return { didStagger, didFlushMemory: false, didWaitForAgent2Gap: false };
  }

  // Keep loading indicator through the fixed gap until Agent 2 appears.
  didStagger = true;

  const queueDidStagger = await flushDeferredMemoryQueueAfterFallback({
    savedQueue: agent2Queue,
    dedupeContext,
    fallbackBots: appendedFallbackBots,
    flushPendingMemoryBots,
    setIsLoading,
  });
  clearAllMemoryIdleTimers?.();
  return {
    didStagger: didStagger || queueDidStagger,
    didFlushMemory: Boolean(queueDidStagger),
    didWaitForAgent2Gap: Boolean(appendedFallbackBots.length),
  };
}

function countUserMessages(messages) {
  if (!Array.isArray(messages)) return 0;
  return messages.filter(
    (m) => m?.type === 'user' && isDisplayableVisitorMessage(m.content),
  ).length;
}

function hasUserMessageSince(messages, baselineUserCount) {
  return countUserMessages(messages) > baselineUserCount;
}

/** Bot bubble the hear-more idle timer is anchored to (first segment when paused, else last). */
function resolveMemoryIdleAnchorBot(response, immediateBots) {
  if (!Array.isArray(immediateBots) || immediateBots.length === 0) return null;
  if (Boolean(response?.memory_pause_after_first_segment)) {
    return immediateBots[0] ?? null;
  }
  return immediateBots[immediateBots.length - 1] ?? null;
}

function memoryIdleAnchorBotReady(
  anchorBotId,
  messages,
  voicePipelineActive,
  ttsRevealedSet,
  voiceDoneSet,
) {
  if (!anchorBotId) return false;
  const bot = (messages || []).find((m) => m?.id === anchorBotId);
  if (!bot) return false;
  if (!voicePipelineActive) return true;
  return ttsRevealedSet.has(anchorBotId) && voiceDoneSet.has(anchorBotId);
}

/** Artefact line the visitor last saw before a memory idle prompt may appear. */
function lastArtefactTextBeforeMemoryIdle(response, newBots) {
  if (!Array.isArray(newBots) || newBots.length === 0) return '';
  const pauseAfterFirst = Boolean(response?.memory_pause_after_first_segment);
  if (pauseAfterFirst && newBots.length >= 2) {
    return newBots[0]?.content ?? '';
  }
  return newBots[newBots.length - 1]?.content ?? '';
}

/** Hear-more / another-memory UI prompts only when the prior artefact line is not a question. */
function mayScheduleMemoryIdlePrompts(response, newBots, language = 'en') {
  const text = lastArtefactTextBeforeMemoryIdle(response, newBots);
  if (!String(text).trim()) return false;
  return !memoryHearMoreIdleSuppressed(text, language);
}

/** Observer memory opening: show first segment only; defer continuation until visitor responds. */
function partitionMemoryOpeningBots(response, newBots, language = 'en') {
  const pauseAfterFirst = Boolean(response?.memory_pause_after_first_segment);
  const mayScheduleIdle = mayScheduleMemoryIdlePrompts(response, newBots, language);

  let idleMs = Number(response?.memory_idle_follow_up_ms) || 0;
  // Mirror backend guard on the bubble the visitor actually sees.
  if (idleMs > 0 && !mayScheduleIdle) {
    idleMs = 0;
  }
  // Fallback only for the first Observer memory opening (paused after segment 1).
  if (
    idleMs <= 0 &&
    pauseAfterFirst &&
    response?.entry_mode === 'memory' &&
    newBots.length >= 1 &&
    mayScheduleIdle
  ) {
    idleMs = MEMORY_IDLE_FOLLOW_UP_MS;
  }

  // Defer continuation segments whenever the backend paused after the first beat (idleMs is only for UI timers).
  if (pauseAfterFirst && newBots.length >= 2) {
    return { immediate: [newBots[0]], deferred: newBots.slice(1), idleMs };
  }

  if (idleMs <= 0) {
    return { immediate: newBots, deferred: [], idleMs: 0 };
  }
  return { immediate: newBots, deferred: [], idleMs };
}

function makeMemoryHearMoreBotMessage(t) {
  return {
    id: Date.now(),
    type: 'bot',
    content: t('app.memoryHearMore'),
    timestamp: new Date().toLocaleTimeString(),
    memoryHearMorePrompt: true,
  };
}

function makeMemoryAnotherMemoryBotMessage(t) {
  return {
    id: Date.now(),
    type: 'bot',
    content: t('app.memoryAnotherMemory'),
    timestamp: new Date().toLocaleTimeString(),
    memoryAnotherMemoryPrompt: true,
  };
}

const AFFIRMATIVE_MEMORY_HEAR_MORE = new Set([
  'yes',
  'yeah',
  'yep',
  'yup',
  'sure',
  'ok',
  'okay',
  'please',
  'go on',
  'continue',
  'yea',
  'y',
  'ναι',
  'νε',
  'οκ',
  'εντάξει',
  'σίγουρα',
  'σιγουρα',
  'οπωσδήποτε',
  'οπωςδηποτε',
]);

function isAffirmativeMemoryHearMoreReply(text) {
  const normalized = String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[.!?,;:'"]/g, '')
    .replace(/\s+/g, ' ');
  if (!normalized) return false;
  if (AFFIRMATIVE_MEMORY_HEAR_MORE.has(normalized)) return true;
  return /^(yes|yeah|yep|sure|ok|please)(\s+please)?$/.test(normalized);
}

/** Mirror backend orchestrator low-energy routing (ok / oh / hm / …). */
const LOW_ENERGY_PHRASES = new Set([
  'ok', 'okay', 'yes', 'yeah', 'yep', 'sure', 'right', 'i see', 'interesting',
  'go on', 'continue', 'hm', 'hmm', 'mm', 'mhm', 'alright', 'oh', 'ah', 'ooh',
  'wow', 'uh', 'um', 'huh', 'and', 'like', 'well', 'so', 'ya', 'yup',
]);

function normalizeBriefReply(text) {
  const lowered = String(text || '').trim().toLowerCase();
  return lowered
    .split('')
    .map((ch) => (/[a-z0-9\s]/i.test(ch) ? ch : ' '))
    .join('')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

function isLowEnergyAcknowledgment(text) {
  if (String(text || '').includes('?')) return false;
  const normalized = normalizeBriefReply(text);
  if (!normalized) return false;
  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length > 4) return false;
  if (LOW_ENERGY_PHRASES.has(normalized)) return true;
  return tokens.length <= 2 && tokens.every((t) => LOW_ENERGY_PHRASES.has(t));
}

/** Content of the latest substantive artefact bubble (skip UI-only prompts). */
function priorArtefactTurnContent(messages) {
  if (!Array.isArray(messages)) return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.type !== 'bot') continue;
    if (m.memoryHearMorePrompt || m.memoryAnotherMemoryPrompt) continue;
    if (m.waitForSignal) continue;
    if (!botMessageIsRenderable(m)) continue;
    const text = String(m.content || '').trim();
    if (text) return text;
  }
  return '';
}

function artefactTurnInvitedVisitorReply(text, language = 'en') {
  const t = String(text || '').trim();
  if (!t) return false;
  return assistantTextEndsWithQuestion(t, language);
}

function shortReplyAnswersPriorArtefactTurn(messages, replyText, optionsContext = {}, language = 'en') {
  if (!isLowEnergyAcknowledgment(replyText)) return false;
  const { activeOptionsMessageId = null, optionsSubmitted = true } = optionsContext;
  if (activeOptionsMessageId != null && !optionsSubmitted) return true;
  return artefactTurnInvitedVisitorReply(priorArtefactTurnContent(messages), language);
}

/** Agent 4 fallback only after this many consecutive low-energy user turns in a row. */
const LOW_ENERGY_FALLBACK_AFTER_STREAK = 2;

function countConsecutiveLowEnergyUserMessages(messages, optionsContext = {}, language = 'en') {
  let streak = 0;
  if (!Array.isArray(messages)) return streak;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.type === 'bot') {
      if (m.agent4Fallback) break;
      continue;
    }
    if (m?.type !== 'user') continue;
    if (!isLowEnergyAcknowledgment(m.content)) break;
    const prefix = messages.slice(0, i + 1);
    if (shortReplyAnswersPriorArtefactTurn(prefix, m.content, optionsContext, language)) break;
    streak += 1;
  }
  return streak;
}

/** Pending "Would you like to hear more?" — only when no visitor reply since that bubble. */
function findMemoryHearMorePrompt(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.type === 'user' && isDisplayableVisitorMessage(m.content)) return null;
    if (m?.memoryHearMorePrompt) return m;
  }
  return null;
}

/** Pending "different memory?" offer — only when no visitor reply since that bubble. */
function findMemoryAnotherMemoryPrompt(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.type === 'user' && isDisplayableVisitorMessage(m.content)) return null;
    if (m?.memoryAnotherMemoryPrompt) return m;
  }
  return null;
}

/**
 * Append 2+ bot bubbles from one response one after another with a pause between
 * segments; keeps the typing indicator on during gaps so it reads as "thinking".
 */
async function appendBotMessagesStaggered(
  setMessages,
  setIsLoading,
  messagesToAdd,
  gapMs = STAGGER_BOT_BUBBLE_MS,
  onEachAppended = null,
  sessionBlockedRef = null,
) {
  if (!messagesToAdd?.length) return;
  if (sessionBlockedRef?.current) return;
  setMessages((prev) => [...prev, messagesToAdd[0]]);
  onEachAppended?.(messagesToAdd[0]);
  if (messagesToAdd.length < 2) return;
  try {
    for (let i = 1; i < messagesToAdd.length; i += 1) {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, gapMs));
      if (sessionBlockedRef?.current) break;
      const row = messagesToAdd[i];
      setMessages((prev) => [...prev, row]);
      onEachAppended?.(row);
    }
  } finally {
    setIsLoading(false);
  }
}

/**
 * Voice toggled off mid-intro: reveal prefetched intro lines one-by-one (same cadence as voice-off-from-start).
 * Re-scans messages each step so lines appended while staggering (e.g. naming question) are included.
 */
async function revealPrefetchedIntroBotsAfterVoiceOff({
  getMessages,
  getRevealedIds,
  setTtsRevealedBotIds,
  setFinishedBotVoiceIds,
  playbackFinishedRef,
  setIsLoading,
  gapMs,
  isCancelled,
  syncRevealedIds,
  introSegmentTexts,
  introPhaseOnly,
}) {
  let isFirst = true;
  let revealedIds = [...getRevealedIds()];
  const texts = normalizeIntroSegmentTexts(introSegmentTexts);
  try {
    for (;;) {
      if (isCancelled()) break;
      const msgs = getMessages();
      const revealedSet = computeTtsRevealedSet(msgs, revealedIds);
      const next = msgs.find((m) => {
        if (m.type !== 'bot' || !botMessageIsRenderable(m) || revealedSet.has(m.id)) {
          return false;
        }
        if (introPhaseOnly && texts.length > 0) {
          return messageIsAwakeningIntroSegment(m, texts);
        }
        return true;
      });
      if (!next) break;

      if (isFirst) {
        isFirst = false;
      } else {
        setIsLoading(true);
        await new Promise((resolve) => setTimeout(resolve, gapMs));
        if (isCancelled()) break;
      }

      if (!revealedIds.includes(next.id)) {
        revealedIds = [...revealedIds, next.id];
      }
      syncRevealedIds?.(revealedIds);
      setTtsRevealedBotIds((prev) =>
        prev.includes(next.id) ? prev : [...prev, next.id],
      );
      playbackFinishedRef.current.add(next.id);
      setFinishedBotVoiceIds((prev) =>
        prev.includes(next.id) ? prev : [...prev, next.id],
      );
    }
  } finally {
    setIsLoading(false);
  }
}

/** Scripted awakening before the visitor's first reply (intro + naming). */
function isAwakeningIntroPhase(messages, conversationStage) {
  return (
    conversationStage === 'introduction' &&
    !messages.some((m) => m.type === 'user')
  );
}

/** Chat after the visitor's first reply (discussion / closing). */
function isPostAwakeningChatVoicePhase(messages, conversationStage) {
  return !isAwakeningIntroPhase(messages, conversationStage);
}

/** Paragraphs for scripted __start__ intro (API segments or content split). */
function introPartsFromFirstResponse(firstResponse) {
  if (
    Array.isArray(firstResponse?.intro_segments) &&
    firstResponse.intro_segments.length > 0
  ) {
    return firstResponse.intro_segments
      .map((part) => String(part ?? '').trim())
      .filter(Boolean);
  }
  return String(firstResponse?.content || '')
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Voice on: add all segments to state for prefetch; chat reveals one line at a time via ttsRevealedSet. */
async function appendBotResponseMessages(
  setMessages,
  setIsLoading,
  messagesToAdd,
  voicePipelineActive,
  onBeforeAppend,
  existingMessagesForDedupe,
  sessionBlockedRef = null,
) {
  if (sessionBlockedRef?.current) return false;
  let bots = messagesToAdd;
  if (existingMessagesForDedupe) {
    bots = filterDuplicateBotMessages(existingMessagesForDedupe, messagesToAdd);
    if (!bots.length) return false;
  }
  if (!bots?.length) return false;
  onBeforeAppend?.(bots);
  if (voicePipelineActive || bots.length < 2) {
    if (sessionBlockedRef?.current) return false;
    setMessages((prev) => [...prev, ...bots]);
    return false;
  }
  await appendBotMessagesStaggered(
    setMessages,
    setIsLoading,
    bots,
    STAGGER_BOT_BUBBLE_MS,
    null,
    sessionBlockedRef,
  );
  return true;
}

function stripForTts(content) {
  if (!content || typeof content !== 'string') return '';
  return content
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/`{1,3}[^`]*`{1,3}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Collapse consecutive duplicate sentence-sized chunks (reduces double-read in TTS). */
function dedupeTtsText(text) {
  if (!text) return '';
  const t = text.replace(/\s+/g, ' ').trim();
  const segments = t.split(/\.\s+/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const seg of segments) {
    const norm = seg.toLowerCase().replace(/\s+/g, ' ');
    const prevNorm =
      out.length > 0 ? out[out.length - 1].toLowerCase().replace(/\s+/g, ' ') : null;
    if (norm === prevNorm) continue;
    out.push(seg);
  }
  let result = out.join('. ');
  if (result && !/[.!?…]$/.test(result)) {
    result += '.';
  }
  return result;
}

/** First awakening fork: memory scene vs question (EN / EL catalog phrasing). */
function isMemoryOrQuestionForkMessage(message) {
  if (!message || message.type !== 'bot') return false;
  if (!Array.isArray(message.options) || message.options.length === 0) return false;
  const t = String(message.content || '');
  const low = t.toLowerCase();
  const hasMemory = /\bmemory\b/i.test(low) || /μνήμη/i.test(t);
  const hasQuestion = /\bquestion\b/i.test(low) || /ερώτησ/i.test(t);
  if (!hasMemory || !hasQuestion) return false;
  return (
    /shall i share|would you like to hear|would you prefer|prefer a question|hear a memory|share a memory/i.test(
      low,
    ) ||
    /θα ήθελες|μοιραστώ|να ακούσεις|να σου μιλήσω|μνήμη.*ερώτηση|ερώτηση.*μνήμη/i.test(t)
  );
}

function indexOfFirstMemoryQuestionFork(allMessages) {
  if (!Array.isArray(allMessages)) return -1;
  for (let i = 0; i < allMessages.length; i += 1) {
    if (isMemoryOrQuestionForkMessage(allMessages[i])) return i;
  }
  return -1;
}

/**
 * Text sent to TTS for a bot bubble.
 * Before the memory-vs-question fork, option labels and footer are read; from that fork onward, only the main body.
 */
function buildBotMessageSpeechText(message, allMessages) {
  if (!message || message.type !== 'bot') return '';
  if (message.memoryHearMorePrompt || message.memoryAnotherMemoryPrompt) {
    return stripForTts(message.content || '').trim();
  }
  const main = stripForTts(message.content || '');
  const msgs = Array.isArray(allMessages) ? allMessages : [];
  const forkIdx = indexOfFirstMemoryQuestionFork(msgs);
  const selfIdx = msgs.findIndex((m) => m && m.id === message.id);
  const beforeFork =
    forkIdx < 0 || selfIdx < 0 || selfIdx < forkIdx;

  if (Array.isArray(message.options) && message.options.length > 0 && !beforeFork) {
    return dedupeTtsText(main).trim();
  }

  const pieces = [];
  if (main) pieces.push(main);
  if (Array.isArray(message.options) && message.options.length > 0) {
    for (const opt of message.options) {
      const label = stripForTts(opt.label || '');
      if (label) pieces.push(label);
    }
  }
  const footer = stripForTts(message.optionsFooter || '');
  if (footer) pieces.push(footer);
  return dedupeTtsText(pieces.join('. ')).trim();
}

function getPreviousBotMessage(messages, fromIndex) {
  for (let i = fromIndex - 1; i >= 0; i -= 1) {
    if (messages[i].type === 'bot') return messages[i];
  }
  return null;
}

function markAllBotMessagesVoiceComplete(messages, playbackFinishedRef, setFinished, setRevealed) {
  const botIds = messages.filter((m) => m.type === 'bot').map((m) => m.id);
  if (botIds.length === 0) return;
  botIds.forEach((id) => playbackFinishedRef.add(id));
  setFinished((prev) => {
    const s = new Set(prev);
    botIds.forEach((id) => s.add(id));
    return Array.from(s);
  });
  setRevealed((prev) => {
    const s = new Set(prev);
    botIds.forEach((id) => s.add(id));
    return Array.from(s);
  });
}

/** First closure bot bubble in transcript order that still needs voice playback. */
function findUnplayedClosureBot(messages, doneSet) {
  if (!Array.isArray(messages)) return null;
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (m?.type !== 'bot' || !m.isClosure) continue;
    if (doneSet?.has(m.id)) continue;
    if (!buildBotMessageSpeechText(m, messages)) continue;
    return m;
  }
  return null;
}

function hasPendingClosureNarration(messages, doneSet) {
  const bot = findUnplayedClosureBot(messages, doneSet);
  if (!bot) return false;
  return Boolean(buildBotMessageSpeechText(bot, messages));
}

function pickClosureBots(bots) {
  if (!Array.isArray(bots)) return [];
  return bots.filter((b) => b?.isClosure);
}

function transcriptHasClosureMessage(messages) {
  return Array.isArray(messages) && messages.some((m) => m?.type === 'bot' && m.isClosure);
}

function markNonClosureBotsVoiceComplete(
  messages,
  playbackFinishedRef,
  setFinished,
  setRevealed,
  exceptClosureId = null,
) {
  const botIds = messages
    .filter((m) => m.type === 'bot' && m.id !== exceptClosureId)
    .map((m) => m.id);
  if (botIds.length === 0) return;
  botIds.forEach((id) => playbackFinishedRef.add(id));
  setFinished((prev) => {
    const s = new Set(prev);
    botIds.forEach((id) => s.add(id));
    if (exceptClosureId != null) s.delete(exceptClosureId);
    return Array.from(s);
  });
  setRevealed((prev) => {
    const s = new Set(prev);
    botIds.forEach((id) => s.add(id));
    return Array.from(s);
  });
}

/**
 * Chat reload: restore visible lines, reveal the next queued bot if one was loading, no TTS replay.
 */
function applyChatReloadVoiceRestore(
  messages,
  snapshot,
  setFinished,
  setRevealed,
  playbackFinishedRef,
  ttsStartedIdsRef,
  personasMap,
  personaKeyAliases,
) {
  const botIds = messages.filter((m) => m.type === 'bot').map((m) => m.id);
  if (botIds.length === 0) return;

  const botIdSet = new Set(botIds);
  const personaKey = reloadPersonaKeyFromSnapshot(snapshot, personasMap, personaKeyAliases);
  const introTexts = resolveCanonicalIntroSegmentTexts(
    personaKey,
    personasMap,
    snapshot,
    messages,
  );
  const introRevealed = introPhaseReloadRevealedBotIds(messages, snapshot, introTexts);
  let revealedIds;
  if (introRevealed != null) {
    revealedIds = introRevealed.filter((id) => botIdSet.has(id));
  } else {
    revealedIds = [...botIds];
  }

  const finishedIds = new Set(revealedIds);

  finishedIds.forEach((id) => playbackFinishedRef.current.add(id));
  ttsStartedIdsRef.current = new Set(finishedIds);
  setFinished(Array.from(finishedIds));
  setRevealed(revealedIds);
}

/**
 * Awakening intro / voice resume: queue TTS only for the last bot line currently shown in chat.
 * @returns {number | string | null} last displayed bot message id, if any
 */
function prepareLastDisplayedBotForVoice(
  messages,
  revealedSet,
  playbackFinishedRef,
  setFinished,
  setRevealed,
  ttsStartedIdsRef,
) {
  let displayed = messages.filter(
    (m) => m.type === 'bot' && revealedSet.has(m.id),
  );
  if (displayed.length === 0) {
    displayed = messages.filter((m) => m.type === 'bot');
  }
  if (displayed.length === 0) return null;

  const lastBot = displayed[displayed.length - 1];
  const lastId = lastBot.id;

  messages
    .filter((m) => m.type === 'bot' && m.id !== lastId)
    .forEach((m) => playbackFinishedRef.add(m.id));
  playbackFinishedRef.delete(lastId);
  ttsStartedIdsRef.current.delete(lastId);

  setFinished(() => {
    const s = new Set(
      messages.filter((m) => m.type === 'bot' && m.id !== lastId).map((m) => m.id),
    );
    return Array.from(s);
  });
  setRevealed((prev) => {
    const s = new Set(prev);
    displayed.forEach((m) => s.add(m.id));
    return Array.from(s);
  });

  return lastId;
}

/**
 * Chat view: queue TTS only for the last bot line after the latest user message.
 * @returns {number | string | null} bot message id to play, if any
 */
function prepareLastBotAfterUserForVoice(
  messages,
  playbackFinishedRef,
  setFinished,
  setRevealed,
  ttsStartedIdsRef,
) {
  if (lastReloadMessageIsUser(messages)) {
    markAllBotMessagesVoiceComplete(messages, playbackFinishedRef, setFinished, setRevealed);
    return null;
  }
  const bots = messages.filter((m) => m.type === 'bot');
  if (bots.length === 0) return null;

  const target = findLastBotAfterLastUserMessage(messages);
  if (!target) {
    markAllBotMessagesVoiceComplete(messages, playbackFinishedRef, setFinished, setRevealed);
    return null;
  }

  const targetId = target.id;
  bots
    .filter((m) => m.id !== targetId)
    .forEach((m) => playbackFinishedRef.add(m.id));
  playbackFinishedRef.delete(targetId);
  ttsStartedIdsRef.current.delete(targetId);

  setFinished(() => {
    const s = new Set(bots.filter((m) => m.id !== targetId).map((m) => m.id));
    return Array.from(s);
  });
  setRevealed((prev) => {
    const s = new Set(prev);
    bots.forEach((m) => s.add(m.id));
    return Array.from(s);
  });

  return targetId;
}

/** Bot lines before the visitor's first message (scripted intro + naming). */
function collectIntroBotsBeforeUser(messages) {
  const introBots = [];
  if (!Array.isArray(messages)) return introBots;
  for (const m of messages) {
    if (m.type === 'user') break;
    if (m.type === 'bot') introBots.push(m);
  }
  return introBots;
}

/** `intro-from-start` on first auto-opened guide during awakening; `last-only` when reopened later. */
function resolveDeferredGuideCloseTtsMode(
  messages,
  conversationStage,
  guideWasAutoOpened,
) {
  if (isAwakeningIntroPhase(messages, conversationStage) && guideWasAutoOpened) {
    return 'intro-from-start';
  }
  return 'last-only';
}

/** Awakening intro: replay every bot line before the visitor's first reply, in order. */
function prepareIntroFromBeginningForVoice(
  messages,
  playbackFinishedRef,
  setFinished,
  setRevealed,
  ttsStartedIdsRef,
) {
  const introBots = collectIntroBotsBeforeUser(messages);
  if (introBots.length === 0) return;
  for (const m of introBots) {
    playbackFinishedRef.delete(m.id);
    ttsStartedIdsRef.current.delete(m.id);
  }
  const introIds = new Set(introBots.map((m) => m.id));
  setFinished((prev) => prev.filter((id) => !introIds.has(id)));
  setRevealed((prev) => {
    const s = new Set(prev);
    introBots.forEach((m) => s.add(m.id));
    return Array.from(s);
  });
}

/** @returns {number | string | null} last displayed bot message id, if any */
function prepareLastBotReplayOnVoiceEnable(
  messages,
  playbackFinishedRef,
  setFinished,
  setRevealed,
  ttsStartedIdsRef,
  revealedIds = [],
) {
  if (lastReloadMessageIsUser(messages)) {
    markAllBotMessagesVoiceComplete(messages, playbackFinishedRef, setFinished, setRevealed);
    return null;
  }
  const revealedSet = computeTtsRevealedSet(messages, revealedIds);
  return prepareLastDisplayedBotForVoice(
    messages,
    revealedSet,
    playbackFinishedRef,
    setFinished,
    setRevealed,
    ttsStartedIdsRef,
  );
}

function computeVoiceDoneSet(messages, finishedIds) {
  const set = new Set(finishedIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i];
      if (m.type !== 'bot') continue;
      const prev = getPreviousBotMessage(messages, i);
      if (prev && !set.has(prev.id)) continue;
      if (!set.has(m.id) && !buildBotMessageSpeechText(m, messages)) {
        set.add(m.id);
        changed = true;
      }
    }
  }
  return set;
}

/** Bot ids whose TTS clip is ready (or had nothing to speak). Gates chat reveal when voice is on. */
function computeTtsRevealedSet(messages, revealedIds) {
  const set = new Set(revealedIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i];
      if (m.type !== 'bot') continue;
      const prev = getPreviousBotMessage(messages, i);
      if (prev && !set.has(prev.id)) continue;
      if (!set.has(m.id) && !buildBotMessageSpeechText(m, messages)) {
        set.add(m.id);
        changed = true;
      }
    }
  }
  return set;
}

/** True when a bot bubble may appear in the transcript. */
function botMessageVisibleInChat(
  message,
  ttsActive,
  ttsRevealedSet,
  ttsPlayingMessageId,
  introStaggerRevealActive,
) {
  if (message.type !== 'bot') return true;
  if (message.isClosure) return true;
  if (message.memoryHearMorePrompt || message.memoryAnotherMemoryPrompt) return true;
  if (!ttsActive && !introStaggerRevealActive) return true;
  if (ttsRevealedSet.has(message.id)) return true;
  return ttsPlayingMessageId != null && ttsPlayingMessageId === message.id;
}

function botAwaitingIntroStaggerReveal(messages, ttsRevealedSet) {
  return messages.some(
    (m) => m.type === 'bot' && botMessageIsRenderable(m) && !ttsRevealedSet.has(m.id),
  );
}

/** Speakable bot line still waiting to appear in chat (includes while prior line audio plays). */
function botAwaitingTtsReveal(messages, ttsRevealedSet) {
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (m.type !== 'bot' || !botMessageIsRenderable(m)) continue;
    if (ttsRevealedSet.has(m.id)) continue;
    if (!buildBotMessageSpeechText(m, messages)) continue;
    return true;
  }
  return false;
}

/** Typing row while the API or voice pipeline prepares the next visible bot line. */
function shouldShowArtefactThinking({
  isLoading,
  ttsActive,
  messages,
  ttsRevealedSet,
  ttsPlayingMessageId,
  pendingMoreBotMessages,
  conversationFinished,
  introStaggerRevealActive,
}) {
  if (conversationFinished) return false;
  if (isLoading) return true;
  if (
    introStaggerRevealActive &&
    botAwaitingIntroStaggerReveal(messages, ttsRevealedSet)
  ) {
    return true;
  }
  if (!ttsActive && !introStaggerRevealActive) return false;
  if (botAwaitingTtsReveal(messages, ttsRevealedSet)) return true;
  if (ttsPlayingMessageId == null) return false;
  if (pendingMoreBotMessages) return true;
  const following = findFollowingBotMessage(messages, ttsPlayingMessageId);
  return Boolean(
    following &&
      botMessageIsRenderable(following) &&
      !ttsRevealedSet.has(following.id),
  );
}

/** Bot message the visitor must hear before typing or choosing chips (active options row or latest bot). */
function getComposerVoiceGateBot(messages, activeOptionsMessageId) {
  if (activeOptionsMessageId != null) {
    return messages.find((m) => m.id === activeOptionsMessageId) ?? null;
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].type === 'bot') return messages[i];
  }
  return null;
}

/** True while voice narration for the gate bot is playing or still queued (blocks composer in introduction). */
function voiceBlocksUserComposer({
  ttsActive,
  voiceDoneSet,
  messages,
  gateBot,
  ttsPlayingMessageId,
}) {
  if (!ttsActive) return false;
  if (ttsPlayingMessageId != null) return true;
  if (!gateBot) return false;
  const speech = buildBotMessageSpeechText(gateBot, messages);
  if (!speech) return false;
  return !voiceDoneSet.has(gateBot.id);
}

/** Voice ID from API (merged from elevenlabs_voices.json by persona name); optional original_name match. */
function resolveElevenLabsVoiceId(personaName, personas) {
  if (!personas || !personaName) return null;
  const direct = personas[personaName];
  if (direct && typeof direct === 'object') {
    const id = String(direct.elevenlabs_voice_id || '').trim();
    if (id) return id;
  }
  for (const p of Object.values(personas)) {
    if (p && typeof p === 'object' && p.original_name === personaName) {
      const id = String(p.elevenlabs_voice_id || '').trim();
      if (id) return id;
    }
  }
  return null;
}

function ArtefactOnboardingRedirect({ personasMap, personaKeyAliases, view = 'disclaimer' }) {
  const { artefactsKey } = useParams();
  if (!artefactsKey) return <Navigate to='/' replace />;
  const resolvedKey =
    personasMap && personaKeyAliases
      ? canonicalPersonaKey(artefactsKey, personasMap, personaKeyAliases)
      : artefactsKey;
  const target =
    personasMap && personasMap[resolvedKey]
      ? artefactPath(resolvedKey, view, personasMap)
      : `/${artefactsKey}/${view}`;
  return <Navigate to={target} replace />;
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    if (typeof window === 'undefined') return true;
    const p = parseArtefactSessionPath(window.location.pathname);
    if (!p) return true;
    if (p.view === 'preferences' || p.view === 'disclaimer') return true;
    if (p.view === 'chat') {
      try {
        return sessionStorage.getItem('disclaimer_acknowledged') !== 'true';
      } catch {
        return true;
      }
    }
    return false;
  });
  const [showIntroduction, setShowIntroduction] = useState(false);
  const [feelingSliders, setFeelingSliders] = useState({
    excitement: 50, // Uneasy (0) ↔ Excited (100)
    hope: 50, // Concerned (0) ↔ Hopeful (100)
  });
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiKey, setApiKey] = useState('');
  const triggerClosureRef = useRef(null);
  const [vectorstoreType] = useState('documents'); // 'documents' or 'user_inputs'
  const [currentPersona, setCurrentPersona] = useState(
    'Artificial Womb'
  ); // Default persona name (aligned with personas.json default)
  const currentPersonaRef = useRef(currentPersona);
  currentPersonaRef.current = currentPersona;
  const [defaultPersonaInfo, setDefaultPersonaInfo] = useState(null); // Default persona name and system prompt
  const [customSystemPrompt, setCustomSystemPrompt] = useState(null); // Custom system prompt override
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatBodyRef = useRef(null);
  const backgroundVisRef = useRef(null);
  /** True while the chat composer textarea is focused (mobile keyboard open). */
  const composerFocusedRef = useRef(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const [sessionId, setSessionId] = useState(() =>
    readPersistedSessionIdForPath(
      typeof window !== 'undefined' ? window.location.pathname : ''
    )
  );
  const [conversationClosed, setConversationClosed] = useState(false);
  const [conversationStartTime, setConversationStartTime] = useState(null);
  const [localEthicalCategories, setLocalEthicalCategories] = useState({}); // Track ethical categories locally from API responses
  const [immediateClassifications, setImmediateClassifications] = useState([]); // Store all immediate classifications (accumulated, not replaced)
  const [isWideViewport, setIsWideViewport] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.innerWidth >= BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX,
  );
  const [showBlobLegend, setShowBlobLegend] = useState(false); // Show legend when blob is clicked
  const [showReflectModal, setShowReflectModal] = useState(false); // Show reflect modal
  const [showInterfaceGuide, setShowInterfaceGuide] = useState(false);
  const showInterfaceGuideRef = useRef(showInterfaceGuide);
  showInterfaceGuideRef.current = showInterfaceGuide;
  /** True when the guide was auto-shown after disclaimer (first visit); false after manual reopen. */
  const guideAutoOpenedRef = useRef(false);
  /** When voice is enabled while the guide is open, TTS waits until close: full intro or last line only. */
  const deferredGuideCloseTtsRef = useRef(null);
  const [reflectionMode, setReflectionMode] = useState(false); // When true, only aura is visible, no chat toggle
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(() => readVoiceOutputPreference());
  const voiceOutputEnabledRef = useRef(voiceOutputEnabled);
  voiceOutputEnabledRef.current = voiceOutputEnabled;
  const [micInputEnabled, setMicInputEnabled] = useState(() => readVoiceInputPreference());
  const micInputEnabledRef = useRef(micInputEnabled);
  micInputEnabledRef.current = micInputEnabled;
  /** When true, reopen mic after the artefact finishes talking/thinking (mic pref must stay on). */
  const micHoldActiveRef = useRef(false);
  const micHoldPointerIdRef = useRef(null);
  const speechInputListeningRef = useRef(false);
  /** Set when the chat mic button stops TTS so mic-enable does not immediately wake the pipeline. */
  const skipTtsWakeOnMicEnableRef = useRef(false);
  const stopSpeechInputRef = useRef(() => {});
  const [micPermissionPending, setMicPermissionPending] = useState(false);
  /** GET /api/personas — includes optional elevenlabs_voice_id per persona. */
  const [personasMap, setPersonasMap] = useState(null);
  /** Legacy route/session strings → canonical keys (persona_key_aliases from personas.json). */
  const [personaKeyAliases, setPersonaKeyAliases] = useState({});
  /** Refs so async handlers always read the latest route + persona map (avoids stale closures). */
  const personasMapRef = useRef(null);
  const personaKeyAliasesRef = useRef({});
  const pathnameForApiRef = useRef(location.pathname);
  const locationStateRef = useRef(location.state);
  const languageForSessionRef = useRef(language);
  /** Bumps on pathname, router state, or language change — drop stale async results. */
  const navigationEpochRef = useRef(0);

  personasMapRef.current = personasMap;
  personaKeyAliasesRef.current = personaKeyAliases;
  pathnameForApiRef.current = location.pathname;
  locationStateRef.current = location.state;
  languageForSessionRef.current = language;

  useLayoutEffect(() => {
    navigationEpochRef.current += 1;
  }, [location.pathname, location.state, language]);

  /**
   * Persona key to send to `/api/openai/generate` and related calls — always aligned with the URL
   * segment when possible, so rapid navigation cannot send the previous artefact to the backend.
   */
  const resolvePersonaForApi = useCallback(() => {
    return personaForArtefactRoute(
      pathnameForApiRef.current,
      personasMapRef.current,
      personaKeyAliasesRef.current,
      currentPersonaRef.current
    );
  }, []);

  const resolvePersonaKeyForCatalog = useCallback(
    (response) =>
      canonicalPersonaKey(
        response?.persona || resolvePersonaForApi(),
        personasMapRef.current,
        personaKeyAliasesRef.current,
      ),
    [resolvePersonaForApi],
  );

  /** Canonical artefact key from current route + loaded personas (for voice, blob, React keys). */
  const effectiveRoutePersona = useMemo(
    () => personaForArtefactRoute(location.pathname, personasMap, personaKeyAliases, currentPersona),
    [location.pathname, personasMap, personaKeyAliases, currentPersona]
  );

  const ttsStateRef = useRef({ audio: null, objectUrl: null, playingId: null });
  const ttsPlaybackTagRef = useRef(null);
  const ttsStartedIdsRef = useRef(new Set());
  const ttsBlobCacheRef = useRef(new Map());
  const ttsPrefetchPromisesRef = useRef(new Map());
  const ttsPipelineRunIdRef = useRef(0);
  const ttsPipelineWakeRef = useRef(null);
  const ttsAbortGestureRef = useRef(null);
  const ttsFetchAbortRef = useRef(null);
  /** Sync guard for async timers, TTS, and late API replies after session end. */
  const closedOrStoppedRef = useRef(false);
  /** Avoid duplicate farewell fetches when closure effects and handlers both fire. */
  const closureNarrationInflightRef = useRef(new Set());
  /** User opened Reflect — skip closure narration (including in-flight fetches). */
  const reflectEngagedRef = useRef(false);
  const playbackFinishedRef = useRef(new Set());
  const [finishedBotVoiceIds, setFinishedBotVoiceIds] = useState([]);
  /** Bot lines whose TTS fetch finished — chat may show them once prior voice is done. */
  const [ttsRevealedBotIds, setTtsRevealedBotIds] = useState([]);
  const ttsRevealedBotIdsRef = useRef([]);
  ttsRevealedBotIdsRef.current = ttsRevealedBotIds;
  /** Backend __start__ intro_segments — canonical catalog for reload / back-and-continue. */
  const [introSegmentTexts, setIntroSegmentTexts] = useState([]);
  const introSegmentTextsRef = useRef([]);
  introSegmentTextsRef.current = introSegmentTexts;
  /** After muting voice mid-intro: gate prefetched lines and reveal them with text stagger. */
  const [introStaggerRevealActive, setIntroStaggerRevealActive] = useState(false);
  const introStaggerCancelRef = useRef(false);
  const introStaggerInflightRef = useRef(false);
  /** Drives composer disable while HTMLAudioElement is playing (ref alone does not re-render). */
  const [ttsPlayingMessageId, setTtsPlayingMessageId] = useState(null);
  /** Sync with stopTts() so mic can start in the same click after interrupting narration. */
  const ttsPlayingMessageIdRef = useRef(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const ttsEnabledRef = useRef(false);
  ttsEnabledRef.current = ttsEnabled;
  const [ttsAutoplayBlocked, setTtsAutoplayBlocked] = useState(false);
  /** Seed from localStorage so reload with voice already on is not treated as "voice turned on". */
  const prevVoiceOnForTtsSeedRef = useRef(readVoiceOutputPreference());
  const prevTtsOnForTtsSeedRef = useRef(false);
  /** Set during chat-view reload restore; suppresses TTS backlog replay when /api/tts/status arrives. */
  const chatReloadRestoreRef = useRef(false);
  /** Pending bot reply to resume after chat reload when the last restored line is from the visitor. */
  const [chatReloadPendingReply, setChatReloadPendingReply] = useState(null);
  const flowResumeInflightRef = useRef(false);
  const prefsReturnResumePathRef = useRef(null);
  const prevArtefactPathForFlowResumeRef = useRef(null);
  /** True while an awakening auto-advance timer is pending (aura → path choice, etc.). */
  const [pendingAutoAdvance, setPendingAutoAdvance] = useState(false);
  /** True after the user mutes voice in-session; next unmute replays the last bot line only. */
  const voiceWasDisabledSinceLastEnableRef = useRef(false);
  const [selectedOptions, setSelectedOptions] = useState([]); // Track selected options for multi-select
  const [optionsShown, setOptionsShown] = useState(false); // Track if options have already been shown once
  const [optionsMessageId, setOptionsMessageId] = useState(null); // Track which message has the options (fallback)
  const [optionsSubmitted, setOptionsSubmitted] = useState(false); // Track if options have been submitted
  /** Bot message ids whose option rows were already submitted (FIFO: older steps stay visible until answered). */
  const [answeredOptionMessageIds, setAnsweredOptionMessageIds] = useState([]);

  /** Artefact introduction modal on preferences (before language/voice step). */
  const [artefactPrefaceDone, setArtefactPrefaceDone] = useState(false);
  /** After language/voice step; gate read from sessionStorage in layout effect (per artefact key). */
  const [preferencesBeforeDisclaimerDone, setPreferencesBeforeDisclaimerDone] = useState(false);

  const audioLanguagePrefsActive = false;
  const audioLanguagePrefsActiveRef = useRef(false);
  audioLanguagePrefsActiveRef.current = false;

  useEffect(() => {
    migrateAudioPreferencesLocalStorage();
  }, []);

  useEffect(() => {
    if (chatReloadRestoreRef.current && finishedBotVoiceIds.length === 0) {
      return;
    }
    playbackFinishedRef.current = new Set(finishedBotVoiceIds);
  }, [finishedBotVoiceIds]);

  /** Chat reload: hydrate transcript + mark lines spoken before other effects run. */
  useLayoutEffect(() => {
    if (!isChatPageReload(location.pathname)) return;
    const snap = readChatSessionSnapshot();
    if (!hasExistingChatTranscript([], snap)) return;
    setShowIntroduction(false);
  }, [location.pathname]);

  /** Chat reload: restore session id + transcript before other effects run. */
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const pathname = window.location.pathname;
    if (parseArtefactSessionPath(pathname)?.view !== 'chat') return;
    const snap = readChatSessionSnapshot();
    if (!snap?.sessionId) return;

    setSessionId((prev) => prev || snap.sessionId);

    const restoredMessages = Array.isArray(snap.messages) ? snap.messages : [];
    if (restoredMessages.length === 0) return;

    const finalized = finalizeReloadedChatMessages(
      restoredMessages,
      restoredMessages,
      snap,
      personasMapRef.current,
      personaKeyAliasesRef.current,
      languageForSessionRef.current || 'en',
    );
    setMessages(finalized);
    applyReloadedOptionsUiState(finalized, snap, {
      setOptionsShown,
      setOptionsMessageId,
      setOptionsSubmitted,
      setAnsweredOptionMessageIds,
    });
    setShowIntroduction(false);
    try {
      sessionStorage.setItem('introduction_completed', 'true');
    } catch {
      /* ignore */
    }

    stopActiveTtsPlayback();
    applyChatReloadVoiceRestore(
      finalized,
      snap,
      setFinishedBotVoiceIds,
      setTtsRevealedBotIds,
      playbackFinishedRef,
      ttsStartedIdsRef,
      personasMapRef.current,
      personaKeyAliasesRef.current,
    );
    chatReloadRestoreRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount; bootstrap may re-apply
  }, []);

  /** Release reload silence once every visible restored bot line is marked voice-complete. */
  useLayoutEffect(() => {
    if (!chatReloadRestoreRef.current) return;
    const revealed = new Set(ttsRevealedBotIdsRef.current);
    const botIds = messagesRef.current
      .filter((m) => m.type === 'bot')
      .map((m) => m.id);
    if (botIds.length === 0) return;
    const visibleBotIds = botIds.filter((id) => revealed.has(id));
    if (visibleBotIds.length === 0) {
      chatReloadRestoreRef.current = false;
      return;
    }
    if (visibleBotIds.every((id) => playbackFinishedRef.current.has(id))) {
      chatReloadRestoreRef.current = false;
    }
  }, [messages, finishedBotVoiceIds, ttsRevealedBotIds]);

  const resolveTtsPersonaKey = useCallback(() => {
    const fromRoute = personaForArtefactRoute(
      pathnameForApiRef.current,
      personasMapRef.current,
      personaKeyAliasesRef.current,
      currentPersonaRef.current,
    );
    const pmap = personasMapRef.current;
    if (fromRoute && pmap && typeof pmap === 'object' && Object.prototype.hasOwnProperty.call(pmap, fromRoute)) {
      return fromRoute;
    }
    return currentPersonaRef.current;
  }, []);

  /** Start TTS fetch as soon as bot lines are known — overlaps with React state update. */
  const prefetchBotsForVoice = useCallback(
    (newBots) => {
      if (closedOrStoppedRef.current && !pickClosureBots(newBots).length) return;
      if (!voiceOutputEnabled || !ttsEnabled || !newBots?.length) return;
      const voiceId = resolveElevenLabsVoiceId(
        resolveTtsPersonaKey(),
        personasMapRef.current,
      );
      if (!voiceId) return;
      const previewMsgs = [...messagesRef.current, ...newBots];
      for (const bot of newBots) {
        const id = bot.id;
        if (ttsBlobCacheRef.current.has(id) || ttsPrefetchPromisesRef.current.has(id)) continue;
        const speech = buildBotMessageSpeechText(bot, previewMsgs);
        if (!speech) continue;
        const promise = apiService
          .textToSpeech(speech, voiceId, language)
          .then(async (blob) => {
            if (!blob) return null;
            const normalized = await normalizeTtsBlobForCache(blob);
            const playbackBlob = normalized || blob;
            ttsBlobCacheRef.current.set(id, playbackBlob);
            return playbackBlob;
          })
          .catch((e) => {
            console.warn('[TTS]', 'Early prefetch failed', { messageId: id, err: e?.message || e });
            return null;
          })
          .finally(() => {
            ttsPrefetchPromisesRef.current.delete(id);
          });
        ttsPrefetchPromisesRef.current.set(id, promise);
      }
    },
    [voiceOutputEnabled, ttsEnabled, language, resolveTtsPersonaKey],
  );

  const ttsVoiceId = useMemo(() => {
    const key = resolveTtsPersonaKey();
    return resolveElevenLabsVoiceId(key, personasMap);
  }, [resolveTtsPersonaKey, personasMap, effectiveRoutePersona, currentPersona]);

  const ttsVoiceIdRef = useRef(ttsVoiceId);
  ttsVoiceIdRef.current = ttsVoiceId;

  const voiceDoneSet = useMemo(
    () => computeVoiceDoneSet(messages, finishedBotVoiceIds),
    [messages, finishedBotVoiceIds],
  );

  /** Bot lines may render only after POST /api/tts returns (ids in ttsRevealedBotIds). */
  const ttsRevealedSet = useMemo(
    () => computeTtsRevealedSet(messages, ttsRevealedBotIds),
    [messages, ttsRevealedBotIds],
  );

  const stopTts = useCallback((options = {}) => {
    stopActiveTtsPlayback(options);
    const { objectUrl } = ttsStateRef.current;
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    ttsStateRef.current = { audio: null, objectUrl: null, playingId: null };
    ttsPlayingMessageIdRef.current = null;
    setTtsPlayingMessageId(null);
  }, []);

  /** Artefact or voice map changed — drop queued clips so the next line uses the new voice_id. */
  useEffect(() => {
    stopTts();
    ttsStartedIdsRef.current = new Set();
    ttsBlobCacheRef.current.clear();
    ttsPrefetchPromisesRef.current.clear();
    ttsPipelineRunIdRef.current += 1;
  }, [ttsVoiceId, stopTts]);

  const stopTtsOnUserInterrupt = useCallback((options = {}) => {
    ttsAbortGestureRef.current?.();
    ttsAbortGestureRef.current = null;
    ttsFetchAbortRef.current?.abort();
    ttsFetchAbortRef.current = null;
    stopTts(options);

    const skipIds = [];
    for (const m of messagesRef.current) {
      if (m.type !== 'bot' || playbackFinishedRef.current.has(m.id)) continue;
      skipIds.push(m.id);
      playbackFinishedRef.current.add(m.id);
      ttsStartedIdsRef.current.delete(m.id);
    }
    if (skipIds.length > 0) {
      setFinishedBotVoiceIds((prev) => {
        const s = new Set(prev);
        skipIds.forEach((id) => s.add(id));
        return Array.from(s);
      });
    }
    const skipWake = skipTtsWakeOnMicEnableRef.current;
    skipTtsWakeOnMicEnableRef.current = false;
    if (!skipWake) {
      ttsPipelineWakeRef.current?.();
    }
  }, [stopTts]);

  /** Stop audio/fetch; optionally keep the async pipeline loop alive (preferences overlay). */
  const pauseVoiceOutput = useCallback((options = {}) => {
    const keepPipeline = options.keepPipeline === true;
    setTtsAutoplayBlocked(false);
    ttsAbortGestureRef.current?.();
    ttsAbortGestureRef.current = null;
    ttsFetchAbortRef.current?.abort();
    ttsFetchAbortRef.current = null;
    stopTts();

    const interruptedId = ttsStateRef.current.playingId;
    if (interruptedId) {
      playbackFinishedRef.current.add(interruptedId);
      setFinishedBotVoiceIds((prev) =>
        prev.includes(interruptedId) ? prev : [...prev, interruptedId],
      );
      setTtsRevealedBotIds((prev) =>
        prev.includes(interruptedId) ? prev : [...prev, interruptedId],
      );
    }

    const finished = playbackFinishedRef.current;
    ttsStartedIdsRef.current = new Set(
      [...ttsStartedIdsRef.current].filter((id) => finished.has(id)),
    );
    if (!keepPipeline) {
      ttsPipelineRunIdRef.current += 1;
    }
    ttsPipelineWakeRef.current?.();
  }, [stopTts]);

  const cancelIntroStaggerReveal = useCallback(() => {
    introStaggerCancelRef.current = true;
    introStaggerInflightRef.current = false;
    setIntroStaggerRevealActive(false);
  }, []);

  /** Stop playback, in-flight fetches, and the voice pipeline immediately (sync — use from click handlers). */
  const stopAllVoiceOutput = useCallback(() => {
    setTtsAutoplayBlocked(false);
    ttsAbortGestureRef.current?.();
    ttsAbortGestureRef.current = null;
    ttsFetchAbortRef.current?.abort();
    ttsFetchAbortRef.current = null;
    stopTts();
    cancelIntroStaggerReveal();
    ttsStartedIdsRef.current = new Set();
    ttsBlobCacheRef.current.clear();
    ttsPrefetchPromisesRef.current.clear();
    ttsPipelineRunIdRef.current += 1;
    ttsPlaybackTagRef.current = null;
    playbackFinishedRef.current = new Set();
    setFinishedBotVoiceIds([]);
    setTtsRevealedBotIds([]);
  }, [stopTts, cancelIntroStaggerReveal]);

  /** Restore mic/voice toggles from localStorage (shared across artefacts). */
  const applySavedMicAndVoicePreferences = useCallback(() => {
    const { voiceOutputEnabled: voiceOn, micInputEnabled: micOn } = readSavedAudioPreferences();
    voiceOutputEnabledRef.current = voiceOn;
    micInputEnabledRef.current = micOn;
    setVoiceOutputEnabled(voiceOn);
    setMicInputEnabled(micOn);
    prevVoiceOnForTtsSeedRef.current = voiceOn;
    if (!voiceOn) {
      setTtsEnabled(false);
      prevTtsOnForTtsSeedRef.current = false;
    }
  }, []);

  /** Stop active audio/mic output; keep saved mic and narration preferences for the next artefact. */
  const resetMicAndVoicePreferencesToOff = useCallback(() => {
    stopAllVoiceOutput();
    resetMicrophonePermissionState();
    setMicCapturePreferred(false);
    applySavedMicAndVoicePreferences();
  }, [stopAllVoiceOutput, applySavedMicAndVoicePreferences]);

  /** No TTS while the language & audio preferences modal is open. */
  useLayoutEffect(() => {
    if (!audioLanguagePrefsActive) return;
    stopActiveTtsPlayback();
    pauseVoiceOutput({ keepPipeline: true });
  }, [audioLanguagePrefsActive, pauseVoiceOutput]);

  /** Session ended: stop audio, reveal existing bot lines, block further TTS without hiding transcript. */
  const haltPostSessionOutput = useCallback((options = {}) => {
    const pendingClosureBot = options.pendingClosureBot ?? null;
    const msgs = messagesRef.current;
    const closureId =
      pendingClosureBot?.id ??
      findUnplayedClosureBot(msgs, playbackFinishedRef.current)?.id ??
      null;

    setTtsAutoplayBlocked(false);
    ttsAbortGestureRef.current?.();
    ttsAbortGestureRef.current = null;
    ttsFetchAbortRef.current?.abort();
    ttsFetchAbortRef.current = null;
    stopTts();
    markNonClosureBotsVoiceComplete(
      msgs,
      playbackFinishedRef.current,
      setFinishedBotVoiceIds,
      setTtsRevealedBotIds,
      closureId,
    );
    if (closureId) playbackFinishedRef.current.delete(closureId);
    ttsStartedIdsRef.current = new Set(playbackFinishedRef.current);

    let preservedBlob = null;
    let preservedPromise = null;
    if (closureId) {
      preservedBlob = ttsBlobCacheRef.current.get(closureId);
      preservedPromise = ttsPrefetchPromisesRef.current.get(closureId);
    }
    ttsBlobCacheRef.current.clear();
    ttsPrefetchPromisesRef.current.clear();
    if (closureId && preservedBlob) ttsBlobCacheRef.current.set(closureId, preservedBlob);
    if (closureId && preservedPromise) {
      ttsPrefetchPromisesRef.current.set(closureId, preservedPromise);
    }

    ttsPipelineRunIdRef.current += 1;
    ttsPlaybackTagRef.current = null;
  }, [stopTts]);

  /** Play farewell narration directly — bypasses the main pipeline, which haltPostSessionOutput stops. */
  const playClosureNarrationNow = useCallback(
    async (closureBot) => {
      if (!closureBot?.isClosure) return;
      if (reflectEngagedRef.current) return;
      if (!voiceOutputEnabledRef.current || !ttsEnabledRef.current) return;
      if (audioLanguagePrefsActiveRef.current) return;

      const id = closureBot.id;
      if (playbackFinishedRef.current.has(id)) return;
      if (closureNarrationInflightRef.current.has(id)) return;
      closureNarrationInflightRef.current.add(id);

      playbackFinishedRef.current.delete(id);
      ttsStartedIdsRef.current.delete(id);
      setFinishedBotVoiceIds((prev) => prev.filter((x) => x !== id));

      const previewMsgs = messagesRef.current.some((m) => m.id === id)
        ? messagesRef.current
        : [...messagesRef.current, closureBot];
      const speech = buildBotMessageSpeechText(closureBot, previewMsgs);
      if (!speech) {
        playbackFinishedRef.current.add(id);
        setFinishedBotVoiceIds((f) => (f.includes(id) ? f : [...f, id]));
        closureNarrationInflightRef.current.delete(id);
        return;
      }

      const voiceId = resolveElevenLabsVoiceId(
        resolveTtsPersonaKey(),
        personasMapRef.current,
      );
      if (!voiceId) {
        closureNarrationInflightRef.current.delete(id);
        return;
      }

      try {
        let blob = ttsBlobCacheRef.current.get(id);
        if (!blob) {
          const inflight = ttsPrefetchPromisesRef.current.get(id);
          if (inflight) blob = await inflight;
        }
        if (!blob) {
          const promise = apiService.textToSpeech(speech, voiceId, language);
          ttsPrefetchPromisesRef.current.set(id, promise);
          try {
            blob = await promise;
            if (blob) {
              const normalized = await normalizeTtsBlobForCache(blob);
              blob = normalized || blob;
              ttsBlobCacheRef.current.set(id, blob);
            }
          } finally {
            ttsPrefetchPromisesRef.current.delete(id);
          }
        }
        if (!blob || reflectEngagedRef.current || playbackFinishedRef.current.has(id)) return;

        const playBlobWithRetry = async () => {
          if (reflectEngagedRef.current) {
            throw new TtsPlaybackStoppedError();
          }
          try {
            await playTtsBlob(blob);
          } catch (playErr) {
            if (playErr instanceof TtsPlaybackStoppedError) throw playErr;
            if (!isAutoplayBlockedError(playErr)) throw playErr;
            setTtsAutoplayBlocked(true);
            await new Promise((resolve, reject) => {
              let settled = false;
              const resumeFromGesture = async () => {
                if (settled) return;
                if (reflectEngagedRef.current) {
                  settled = true;
                  document.removeEventListener('touchstart', resumeFromGesture);
                  document.removeEventListener('click', resumeFromGesture);
                  reject(new TtsPlaybackStoppedError());
                  return;
                }
                settled = true;
                document.removeEventListener('touchstart', resumeFromGesture);
                document.removeEventListener('click', resumeFromGesture);
                setTtsAutoplayBlocked(false);
                unlockAudioPlayback({ force: true });
                try {
                  await playTtsBlob(blob);
                  resolve();
                } catch (e2) {
                  reject(e2);
                }
              };
              document.addEventListener('touchstart', resumeFromGesture, { passive: true });
              document.addEventListener('click', resumeFromGesture);
            });
          }
        };

        ttsPlayingMessageIdRef.current = id;
        setTtsPlayingMessageId(id);
        setTtsRevealedBotIds((f) => (f.includes(id) ? f : [...f, id]));

        await playBlobWithRetry();

        playbackFinishedRef.current.add(id);
        setFinishedBotVoiceIds((f) => (f.includes(id) ? f : [...f, id]));
      } catch (e) {
        if (!(e instanceof TtsPlaybackStoppedError)) {
          console.warn('[TTS]', 'Closure playback failed', { messageId: id, err: e?.message || e });
        }
      } finally {
        closureNarrationInflightRef.current.delete(id);
        if (ttsPlayingMessageIdRef.current === id) {
          ttsPlayingMessageIdRef.current = null;
          setTtsPlayingMessageId(null);
        }
      }

      const nextClosure = findUnplayedClosureBot(
        messagesRef.current,
        playbackFinishedRef.current,
      );
      if (nextClosure && !reflectEngagedRef.current) {
        void playClosureNarrationNow(nextClosure);
      }
    },
    [language, resolveTtsPersonaKey],
  );

  const startIntroTextStaggerAfterVoiceOff = useCallback(() => {
    const msgs = messagesRef.current;
    if (!isAwakeningIntroPhase(msgs, conversationStageRef.current)) return false;

    const snap = readChatSessionSnapshot();
    const personaKey = reloadPersonaKeyFromSnapshot(
      snap,
      personasMapRef.current,
      personaKeyAliasesRef.current,
    );
    const introTexts = resolveCanonicalIntroSegmentTexts(
      personaKey,
      personasMapRef.current,
      snap,
      msgs,
      language,
    );
    const revealedSet = computeTtsRevealedSet(msgs, ttsRevealedBotIdsRef.current);
    const hasUnrevealed = msgs.some(
      (m) =>
        m.type === 'bot' &&
        botMessageIsRenderable(m) &&
        !revealedSet.has(m.id) &&
        (introTexts.length === 0 || messageIsAwakeningIntroSegment(m, introTexts)),
    );
    if (!hasUnrevealed) return false;
    if (introStaggerInflightRef.current) return true;

    introStaggerCancelRef.current = false;
    introStaggerInflightRef.current = true;
    setIntroStaggerRevealActive(true);

    void revealPrefetchedIntroBotsAfterVoiceOff({
      getMessages: () => messagesRef.current,
      getRevealedIds: () => ttsRevealedBotIdsRef.current,
      setTtsRevealedBotIds,
      setFinishedBotVoiceIds,
      playbackFinishedRef,
      setIsLoading,
      gapMs: INTRO_BOT_BUBBLE_GAP_MS,
      isCancelled: () => introStaggerCancelRef.current,
      syncRevealedIds: (ids) => {
        ttsRevealedBotIdsRef.current = ids;
      },
      introSegmentTexts: introTexts,
      introPhaseOnly: true,
    }).finally(() => {
      introStaggerInflightRef.current = false;
      if (!introStaggerCancelRef.current) {
        setIntroStaggerRevealActive(false);
        // If the conversation has moved past the intro (user already replied), wake the TTS
        // pipeline so it can start processing discussion messages immediately.
        if (
          !isAwakeningIntroPhase(messagesRef.current, conversationStageRef.current) &&
          !showInterfaceGuideRef.current
        ) {
          ttsPipelineWakeRef.current?.();
        }
      }
    });

    return true;
  }, []);

  const resumeIntroPresentationAfterInterrupt = useCallback(() => {
    const msgs = messagesRef.current;
    if (!Array.isArray(msgs) || msgs.length === 0) return false;
    if (!isAwakeningIntroPhase(msgs, conversationStageRef.current)) return false;
    if (flowBeatVisibleInTranscript('naming', msgs, language)) return false;

    const snap = readChatSessionSnapshot();
    const personaKey = reloadPersonaKeyFromSnapshot(
      snap,
      personasMapRef.current,
      personaKeyAliasesRef.current,
    );
    const introTexts = resolveCanonicalIntroSegmentTexts(
      personaKey,
      personasMapRef.current,
      snap,
      msgs,
      language,
    );
    const revealedSet = computeTtsRevealedSet(msgs, ttsRevealedBotIdsRef.current);
    const unrevealedIntro = msgs.filter(
      (m) =>
        m.type === 'bot' &&
        botMessageIsRenderable(m) &&
        messageIsAwakeningIntroSegment(m, introTexts) &&
        !revealedSet.has(m.id),
    );
    if (unrevealedIntro.length === 0) return false;

    chatReloadRestoreRef.current = false;
    if (voiceOutputEnabledRef.current && ttsEnabled && !showInterfaceGuideRef.current) {
      ttsPipelineWakeRef.current?.();
    } else {
      startIntroTextStaggerAfterVoiceOff();
    }
    return true;
  }, [language, ttsEnabled, startIntroTextStaggerAfterVoiceOff]);

  const toggleVoiceOutputPreference = useCallback(() => {
    const turningOff = voiceOutputEnabled;
    const inInterfaceGuide = showInterfaceGuideRef.current;
    if (turningOff) {
      voiceOutputEnabledRef.current = false;
      voiceWasDisabledSinceLastEnableRef.current = true;
      if (!inInterfaceGuide) {
        deferredGuideCloseTtsRef.current = null;
      }
      stopActiveTtsPlayback();
      pauseVoiceOutput();
      if (!inInterfaceGuide) {
        const keptStagger = startIntroTextStaggerAfterVoiceOff();
        if (!keptStagger) {
          markAllBotMessagesVoiceComplete(
            messagesRef.current,
            playbackFinishedRef.current,
            setFinishedBotVoiceIds,
            setTtsRevealedBotIds,
          );
        }
      }
    } else {
      voiceOutputEnabledRef.current = true;
      cancelIntroStaggerReveal();
      if (inInterfaceGuide) {
        deferredGuideCloseTtsRef.current = resolveDeferredGuideCloseTtsMode(
          messagesRef.current,
          conversationStageRef.current,
          guideAutoOpenedRef.current,
        );
      }
      // unlockAudioPlayback uses micCapturePreferred to pick play-and-record vs playback session.
      unlockAudioPlayback({ force: true });
    }
    setVoiceOutputEnabled(!turningOff);
    try {
      localStorage.setItem(VOICE_OUTPUT_PREF_KEY, turningOff ? '0' : '1');
    } catch {
      /* ignore */
    }
  }, [voiceOutputEnabled, pauseVoiceOutput, startIntroTextStaggerAfterVoiceOff, cancelIntroStaggerReveal]);

  const runDeferredGuideCloseTts = useCallback(() => {
    const mode = deferredGuideCloseTtsRef.current;
    deferredGuideCloseTtsRef.current = null;
    if (!mode || !voiceOutputEnabledRef.current) return;

    const msgs = messagesRef.current;
    stopTts();
    voiceWasDisabledSinceLastEnableRef.current = false;
    cancelIntroStaggerReveal();

    if (mode === 'intro-from-start') {
      prepareIntroFromBeginningForVoice(
        msgs,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
      );
      const introBots = collectIntroBotsBeforeUser(msgs);
      if (introBots.length > 0) {
        prefetchBotsForVoice(introBots);
      }
    } else if (isAwakeningIntroPhase(msgs, conversationStageRef.current)) {
      prepareLastBotReplayOnVoiceEnable(
        msgs,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
        ttsRevealedBotIdsRef.current,
      );
    } else {
      prepareLastBotAfterUserForVoice(
        msgs,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
      );
    }

    if (ttsEnabledRef.current) {
      ttsPipelineWakeRef.current?.();
    }
  }, [stopTts, cancelIntroStaggerReveal, prefetchBotsForVoice]);

  const handleCloseInterfaceGuide = useCallback(() => {
    const wasAutoOpenedGuide = guideAutoOpenedRef.current;
    const voiceOn = voiceOutputEnabledRef.current;
    if (
      voiceOn &&
      wasAutoOpenedGuide &&
      isAwakeningIntroPhase(messagesRef.current, conversationStageRef.current)
    ) {
      deferredGuideCloseTtsRef.current = 'intro-from-start';
    }
    setShowInterfaceGuide(false);
    if (voiceOn) {
      unlockAudioPlayback({ force: true });
      runDeferredGuideCloseTts();
    } else {
      deferredGuideCloseTtsRef.current = null;
    }
    if (wasAutoOpenedGuide) {
      guideAutoOpenedRef.current = false;
    }
  }, [runDeferredGuideCloseTts]);

  /** Chat mic: turn on preference only; permission is requested on first hold-to-record. */
  const enableMicInputPreference = useCallback(() => {
    micInputEnabledRef.current = true;
    setMicInputEnabled(true);
    try {
      localStorage.setItem(VOICE_INPUT_PREF_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const applyMicPermissionResult = useCallback(
    ({ granted, error }) => {
      if (granted) {
        micInputEnabledRef.current = true;
        setMicInputEnabled(true);
        try {
          localStorage.setItem(VOICE_INPUT_PREF_KEY, '1');
        } catch {
          /* ignore */
        }
        return;
      }
      releaseMicrophoneAccess();
      setMicInputEnabled(false);
      try {
        localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
      } catch {
        /* ignore */
      }
      if (error) {
        setError(t(micPermissionErrorTranslationKey(error)));
      }
    },
    [t],
  );

  /** Await permission result; prompt must be started via beginMicrophonePermissionPrompt in the same tap. */
  const enableMicFromPreferences = useCallback(
    async (permissionPromise) => {
      setMicPermissionPending(true);
      try {
        const result = await permissionPromise;
        applyMicPermissionResult(result);
      } finally {
        setMicPermissionPending(false);
      }
    },
    [applyMicPermissionResult],
  );

  const toggleMicInputPreference = useCallback(() => {
    stopSpeechInputRef.current({ cancelTranscription: true });
    setMicInputEnabled(false);
    try {
      localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    return () => {
      stopTts();
    };
  }, [stopTts]);

  useEffect(() => {
    const updateWideViewport = () => {
      setIsWideViewport(window.innerWidth >= BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX);
    };
    window.addEventListener('resize', updateWideViewport);
    return () => window.removeEventListener('resize', updateWideViewport);
  }, []);

  const refreshTtsStatus = useCallback(async () => {
    const r = await apiService.getTtsStatus();
    if (r.success && r.enabled) {
      setTtsEnabled(true);
    } else {
      console.warn(
        '[TTS]',
        'Synthesis unavailable:',
        !r.success
          ? 'GET /api/tts/status failed (backend down, CORS, or wrong REACT_APP_API_URL).'
          : 'Backend reports TTS off — set ELEVENLABS_API_KEY and restart the API.',
      );
      setTtsEnabled(false);
    }
  }, []);

  /** Only probe ElevenLabs-backed TTS when the user has voice output enabled. */
  useEffect(() => {
    if (!voiceOutputEnabled) return;
    refreshTtsStatus();
  }, [voiceOutputEnabled, personasMap, refreshTtsStatus]);

  /**
   * When the user turns voice on while bot lines are already visible, queue only the last
   * bot line (awakening: last visible; discussion: last after their latest message).
   * When TTS status arrives later while voice was already on, same last-line catch-up in
   * discussion — intro backlog still plays sequentially.
   */
  useLayoutEffect(() => {
    const voiceOn = voiceOutputEnabled;
    const ttsOn = ttsEnabled;
    const voiceTurnedOn = voiceOn && !prevVoiceOnForTtsSeedRef.current;
    const ttsTurnedOn = ttsOn && !prevTtsOnForTtsSeedRef.current;

    if (voiceTurnedOn) {
      stopTts();
      voiceWasDisabledSinceLastEnableRef.current = false;
      if (showInterfaceGuideRef.current && !audioLanguagePrefsActiveRef.current) {
        deferredGuideCloseTtsRef.current = resolveDeferredGuideCloseTtsMode(
          messagesRef.current,
          conversationStageRef.current,
          guideAutoOpenedRef.current,
        );
        prevVoiceOnForTtsSeedRef.current = voiceOn;
        prevTtsOnForTtsSeedRef.current = ttsOn;
        return;
      }
    }

    if (!voiceOn || !ttsOn) {
      if (!voiceOn) {
        prevVoiceOnForTtsSeedRef.current = false;
      }
      prevTtsOnForTtsSeedRef.current = ttsOn;
      return;
    }

    prevVoiceOnForTtsSeedRef.current = voiceOn;
    prevTtsOnForTtsSeedRef.current = ttsOn;

    if (ttsTurnedOn && !voiceTurnedOn) {
      if (chatReloadRestoreRef.current) {
        return;
      }
      if (audioLanguagePrefsActiveRef.current) return;
      if (showInterfaceGuideRef.current && voiceOutputEnabledRef.current) {
        deferredGuideCloseTtsRef.current = resolveDeferredGuideCloseTtsMode(
          messagesRef.current,
          conversationStageRef.current,
          guideAutoOpenedRef.current,
        );
        return;
      }
      const msgs = messagesRef.current;
      if (lastReloadMessageIsUser(msgs)) {
        markAllBotMessagesVoiceComplete(
          msgs,
          playbackFinishedRef.current,
          setFinishedBotVoiceIds,
          setTtsRevealedBotIds,
        );
        return;
      }
      if (
        isAwakeningIntroPhase(msgs, conversationStageRef.current)
      ) {
        const done = playbackFinishedRef.current;
        const pendingBots = msgs.filter((m) => m.type === 'bot' && !done.has(m.id));
        if (pendingBots.length > 0) {
          ttsPipelineWakeRef.current?.();
        }
        return;
      }
      prepareLastBotAfterUserForVoice(
        msgs,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
      );
      ttsPipelineWakeRef.current?.();
      return;
    }

    if (!voiceTurnedOn) return;

    if (audioLanguagePrefsActiveRef.current) return;

    if (
      isAwakeningIntroPhase(
        messagesRef.current,
        conversationStageRef.current,
      )
    ) {
      cancelIntroStaggerReveal();
      prepareLastBotReplayOnVoiceEnable(
        messagesRef.current,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
        ttsRevealedBotIdsRef.current,
      );
      ttsPipelineWakeRef.current?.();
      return;
    }

    if (isPostAwakeningChatVoicePhase(messagesRef.current, conversationStageRef.current)) {
      prepareLastBotAfterUserForVoice(
        messagesRef.current,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
        ttsStartedIdsRef,
      );
      ttsPipelineWakeRef.current?.();
      return;
    }

    markAllBotMessagesVoiceComplete(
      messagesRef.current,
      playbackFinishedRef.current,
      setFinishedBotVoiceIds,
      setTtsRevealedBotIds,
    );
  }, [voiceOutputEnabled, ttsEnabled, stopTts, startIntroTextStaggerAfterVoiceOff]);

  useEffect(() => {
    if (messages.length === 0) {
      if (isChatViewReloadHydrationPending(location.pathname)) {
        return;
      }
      cancelIntroStaggerReveal();
      setFinishedBotVoiceIds([]);
      setTtsRevealedBotIds([]);
      ttsStartedIdsRef.current = new Set();
      playbackFinishedRef.current = new Set();
      ttsPlaybackTagRef.current = null;
      ttsBlobCacheRef.current.clear();
      ttsPrefetchPromisesRef.current.clear();
    }
  }, [messages.length, cancelIntroStaggerReveal, location.pathname]);

  /** Resume voice reveal/playback when leaving the language & audio overlay or interface guide. */
  useEffect(() => {
    if (audioLanguagePrefsActive) return;
    if (showInterfaceGuide) return;
    if (!voiceOutputEnabled || !ttsEnabled) return;
    if (closedOrStoppedRef.current) {
      return;
    }
    if (parseArtefactSessionPath(location.pathname)?.view === 'chat') {
      chatReloadRestoreRef.current = false;
    }
    ttsPipelineWakeRef.current?.();
  }, [audioLanguagePrefsActive, showInterfaceGuide, voiceOutputEnabled, ttsEnabled, location.pathname]);

  /** Wake the voice pipeline when new bot lines land or voice/TTS becomes active. */
  useEffect(() => {
    if (!voiceOutputEnabled || !ttsEnabled) return;
    if (closedOrStoppedRef.current) {
      return;
    }
    if (audioLanguagePrefsActive) return;
    if (showInterfaceGuide) return;
    if (chatReloadRestoreRef.current) return;
    ttsPipelineWakeRef.current?.();
  }, [messages, voiceOutputEnabled, ttsEnabled, micInputEnabled, audioLanguagePrefsActive, showInterfaceGuide]);

  useEffect(() => {
    if (!voiceOutputEnabled || !ttsEnabled || !personasMap) return;

    // Drop stale "started" ids when the effect re-runs (deps change / Strict Mode remount).
    const done = playbackFinishedRef.current;
    ttsStartedIdsRef.current = new Set(
      [...ttsStartedIdsRef.current].filter((id) => done.has(id)),
    );

    const runId = ++ttsPipelineRunIdRef.current;
    let cancelled = false;
    let abortGestureResume = null;

    const pipelineHelpers = { getPreviousBotMessage, buildBotMessageSpeechText };

    const getDoneSet = () =>
      computeVoiceDoneSet(messagesRef.current, Array.from(playbackFinishedRef.current));

    const revealBotLine = (id) => {
      setTtsRevealedBotIds((f) => (f.includes(id) ? f : [...f, id]));
    };

    const markVoiceDone = (id) => {
      playbackFinishedRef.current.add(id);
      setFinishedBotVoiceIds((f) => (f.includes(id) ? f : [...f, id]));
      if (ttsStateRef.current.playingId === id) {
        ttsStateRef.current = { audio: null, objectUrl: null, playingId: null };
        ttsPlayingMessageIdRef.current = null;
        setTtsPlayingMessageId(null);
      }
    };

    const waitForMoreWork = () =>
      new Promise((resolve) => {
        ttsPipelineWakeRef.current = () => {
          ttsPipelineWakeRef.current = null;
          resolve();
        };
      });

    const ensureTtsBlob = async (message, speechText, voiceId) => {
      const id = message.id;
      const cache = ttsBlobCacheRef.current;
      if (cache.has(id)) return cache.get(id);

      const inflight = ttsPrefetchPromisesRef.current;
      if (inflight.has(id)) return inflight.get(id);

      const personaKey = resolveTtsPersonaKey();
      const promise = (async () => {
        try {
          const blob = await apiService.textToSpeech(speechText, voiceId, language);
          cache.set(id, blob);
          return blob;
        } catch (e) {
          console.warn('[TTS]', 'Synthesis failed', { messageId: id, err: e?.message || e });
          return null;
        } finally {
          inflight.delete(id);
        }
      })();
      inflight.set(id, promise);
      return promise;
    };

    const prefetchFollowingBot = (afterBotId, voiceId) => {
      const follow = findFollowingBotMessage(messagesRef.current, afterBotId);
      if (!follow) return;
      const text = buildBotMessageSpeechText(follow, messagesRef.current);
      if (!text) return;
      if (ttsBlobCacheRef.current.has(follow.id) || ttsPrefetchPromisesRef.current.has(follow.id)) return;
      void ensureTtsBlob(follow, text, voiceId);
    };

    const playBlobWithRetry = async (playFn) => {
      const runPlayback = async () => {
        await playFn();
      };
      try {
        await runPlayback();
      } catch (playErr) {
        if (playErr instanceof TtsPlaybackStoppedError) throw playErr;
        if (playErr?.name === 'AbortError') throw new TtsPlaybackStoppedError();
        if (!isAutoplayBlockedError(playErr)) {
          console.warn('[TTS]', 'audio.play() failed', playErr?.name, playErr?.message || playErr);
          throw playErr;
        }
        console.warn('[TTS]', 'Autoplay blocked; waiting for user gesture.');
        setTtsAutoplayBlocked(true);
        await new Promise((resolve, reject) => {
          let settled = false;
          const resumeFromGesture = async () => {
            if (settled) return;
            settled = true;
            document.removeEventListener('touchstart', resumeFromGesture);
            document.removeEventListener('click', resumeFromGesture);
            abortGestureResume = null;
            ttsAbortGestureRef.current = null;
            setTtsAutoplayBlocked(false);
            unlockAudioPlayback({ force: true });
            try {
              await runPlayback();
              resolve();
            } catch (e2) {
              if (e2 instanceof TtsPlaybackStoppedError || e2?.name === 'AbortError') {
                reject(e2 instanceof TtsPlaybackStoppedError ? e2 : new TtsPlaybackStoppedError());
                return;
              }
              console.warn('[TTS]', 'Playback after gesture failed', e2?.name, e2?.message || e2);
              reject(e2);
            }
          };
          abortGestureResume = () => {
            document.removeEventListener('touchstart', resumeFromGesture);
            document.removeEventListener('click', resumeFromGesture);
            abortGestureResume = null;
            ttsAbortGestureRef.current = null;
            setTtsAutoplayBlocked(false);
            if (!settled) {
              settled = true;
              reject(new TtsPlaybackStoppedError());
            }
          };
          ttsAbortGestureRef.current = abortGestureResume;
          document.addEventListener('touchstart', resumeFromGesture, { passive: true });
          document.addEventListener('click', resumeFromGesture);
        });
      }
    };

    const playMessageTts = async (message, speechText, voiceId, messageId, fetchSignal) => {
      if (!voiceOutputEnabledRef.current) {
        throw new TtsPlaybackStoppedError();
      }
      if (closedOrStoppedRef.current) {
        throw new TtsPlaybackStoppedError();
      }

      let blob = ttsBlobCacheRef.current.get(messageId);
      if (!blob) {
        const inflight = ttsPrefetchPromisesRef.current;
        if (inflight.has(messageId)) {
          blob = await inflight.get(messageId);
        }
      }

      if (!blob) {
        blob = await apiService.textToSpeech(speechText, voiceId, language, fetchSignal);
        if (blob) {
          const normalized = await normalizeTtsBlobForCache(blob);
          blob = normalized || blob;
          ttsBlobCacheRef.current.set(messageId, blob);
        }
      }

      if (!blob) return null;

      await playBlobWithRetry(() => playTtsBlob(blob));
      return blob;
    };

    (async () => {
      const personaKey = resolveTtsPersonaKey();
      let voiceId =
        ttsVoiceIdRef.current ||
        resolveElevenLabsVoiceId(personaKey, personasMapRef.current);
      if (!voiceId) {
        console.warn(
          '[TTS]',
          'No voice_id for this persona yet — waiting for personas map.',
          { persona: personaKey, language },
        );
      }

      while (
        !cancelled &&
        runId === ttsPipelineRunIdRef.current &&
        voiceOutputEnabledRef.current &&
        !closedOrStoppedRef.current
      ) {
        if (audioLanguagePrefsActiveRef.current || showInterfaceGuideRef.current) {
          await waitForMoreWork();
          continue;
        }

        if (chatReloadRestoreRef.current) {
          await waitForMoreWork();
          continue;
        }

        voiceId =
          ttsVoiceIdRef.current ||
          resolveElevenLabsVoiceId(resolveTtsPersonaKey(), personasMapRef.current);

        const msgs = messagesRef.current;

        const doneSet = getDoneSet();

        const next = findNextBotForPlayback(
          msgs,
          doneSet,
          ttsStartedIdsRef.current,
          pipelineHelpers,
        );
        if (!next) {
          await waitForMoreWork();
          continue;
        }

        const id = next.message.id;

        if (!voiceId) {
          await new Promise((r) => setTimeout(r, 250));
          continue;
        }

        ttsStartedIdsRef.current.add(id);

        if (next.empty) {
          revealBotLine(id);
          markVoiceDone(id);
          continue;
        }

        prefetchFollowingBot(id, voiceId);

        const fetchAbort = new AbortController();
        ttsFetchAbortRef.current = fetchAbort;

        revealBotLine(id);
        ttsStateRef.current = { audio: null, objectUrl: null, playingId: id };
        ttsPlayingMessageIdRef.current = id;
        setTtsPlayingMessageId(id);

        let played = null;
        try {
          played = await playMessageTts(
            next.message,
            next.speechText,
            voiceId,
            id,
            fetchAbort.signal,
          );
        } catch (playErr) {
          if (ttsFetchAbortRef.current === fetchAbort) ttsFetchAbortRef.current = null;
          if (playErr instanceof TtsPlaybackStoppedError || playErr?.name === 'AbortError') {
            if (
              cancelled ||
              runId !== ttsPipelineRunIdRef.current ||
              !voiceOutputEnabledRef.current
            ) {
              break;
            }
            markVoiceDone(id);
            continue;
          }
          console.warn('[TTS]', 'Playback failed; advancing queue', { messageId: id, err: playErr?.message || playErr });
          markVoiceDone(id);
          continue;
        } finally {
          if (ttsFetchAbortRef.current === fetchAbort) ttsFetchAbortRef.current = null;
        }

        if (cancelled || runId !== ttsPipelineRunIdRef.current) {
          if (!playbackFinishedRef.current.has(id)) {
            ttsStartedIdsRef.current.delete(id);
          }
          break;
        }

        if (!played) {
          markVoiceDone(id);
          setError(t('errors.failedToSpeak'));
          continue;
        }

        markVoiceDone(id);

        const follow = findFollowingBotMessage(messagesRef.current, id);
        const followSpeech =
          follow && buildBotMessageSpeechText(follow, messagesRef.current);
        if (followSpeech && !playbackFinishedRef.current.has(follow.id)) {
          await waitTtsInterAudioGap(
            () =>
              cancelled ||
              runId !== ttsPipelineRunIdRef.current ||
              !voiceOutputEnabledRef.current,
          );
        }
        continue;
      }
    })();

    return () => {
      cancelled = true;
      ttsPipelineRunIdRef.current += 1;
      stopActiveTtsPlayback();
      ttsFetchAbortRef.current?.abort();
      ttsFetchAbortRef.current = null;
      const finished = playbackFinishedRef.current;
      ttsStartedIdsRef.current = new Set(
        [...ttsStartedIdsRef.current].filter((id) => finished.has(id)),
      );
      ttsAbortGestureRef.current?.();
      ttsAbortGestureRef.current = null;
      ttsPipelineWakeRef.current?.();
      ttsPipelineWakeRef.current = null;
      abortGestureResume?.();
      abortGestureResume = null;
    };
  }, [
    ttsEnabled,
    voiceOutputEnabled,
    personasMap,
    language,
    ttsVoiceId,
    resolveTtsPersonaKey,
    audioLanguagePrefsActive,
    showInterfaceGuide,
  ]);

  const activeOptionsMessage = firstPendingOptionsMessage(messages, answeredOptionMessageIds);
  const activeOptionsMessageId = activeOptionsMessage?.id ?? null;
  const activeOptionsAllowMultiple = activeOptionsMessage?.optionsAllowMultiple ?? false;

  const voiceGateBot = useMemo(
    () => getComposerVoiceGateBot(messages, activeOptionsMessageId),
    [messages, activeOptionsMessageId],
  );

  const visContainerRefMobile = useRef(null);
  const followUpTimerRef = useRef(null); // Timer for 30-second follow-up question
  const waitMessageTimerRef = useRef(null); // 2 min: show "I will wait for you to signal..."
  const stopTimerRef = useRef(null); // 4 min total: stop agents, show Reflect if discussion+
  const stageWhenWaitingRef = useRef('introduction'); // Stage when we showed waiting message (for 4-min action)
  const introDelayRef = useRef(null); // Timer for delay between intro messages (cancellable if user types)
  /** Lock composer during scripted intro until the second (naming) request finishes. */
  const [introAwaitingNamingBubble, setIntroAwaitingNamingBubble] = useState(false);
  const introAwaitingNamingBubbleRef = useRef(false);
  /** Prevents duplicate awakening fetches when showIntroduction and sessionId update in separate renders. */
  const introSequenceInflightRef = useRef(false);
  const autoAdvanceRef = useRef(null); // Timer for auto-advance (e.g. ethical aura → path_choice after 3s)
  const handleAutoAdvanceRef = useRef(null);
  const preThinkTimerRef = useRef(null); // Timer to show thinking indicator before auto-advance
  const inquiryIdleTimerRef = useRef(null); // ~2s after user-led inquiry when last reply was not question-ended
  const memoryIdleTimerRef = useRef(null); // 20s after Observer opening memory if visitor is silent
  const memoryAnotherMemoryIdleTimerRef = useRef(null); // 20s before another-memory offer
  const pendingMemoryBotsRef = useRef([]);
  const memoryOpeningPauseActiveRef = useRef(false);
  const memoryHearMoreShownRef = useRef(false);
  /** Session guard: hear-more UI only once, after the first opening memory. */
  const memoryHearMoreEverShownRef = useRef(false);
  /** True once the first Observer memory segment has been delivered. */
  const firstObserverMemoryDeliveredRef = useRef(false);
  const memoryAnotherMemoryShownRef = useRef(false);
  const pendingMemoryIdleMsRef = useRef(0);
  const memoryIdleAnchorBotIdRef = useRef(null);
  const memoryIdleUserCountAtAnchorRef = useRef(0);
  const memoryIdleTimerUserCountRef = useRef(0);

  const clearPendingMemoryIdleFollowUp = () => {
    pendingMemoryIdleMsRef.current = 0;
    memoryIdleAnchorBotIdRef.current = null;
    memoryIdleUserCountAtAnchorRef.current = 0;
  };

  const clearMemoryIdleTimer = () => {
    if (memoryIdleTimerRef.current) {
      clearTimeout(memoryIdleTimerRef.current);
      memoryIdleTimerRef.current = null;
    }
  };

  const clearMemoryAnotherMemoryIdleTimer = () => {
    if (memoryAnotherMemoryIdleTimerRef.current) {
      clearTimeout(memoryAnotherMemoryIdleTimerRef.current);
      memoryAnotherMemoryIdleTimerRef.current = null;
    }
  };

  const clearAllMemoryIdleTimers = () => {
    clearMemoryIdleTimer();
    clearMemoryAnotherMemoryIdleTimer();
    clearPendingMemoryIdleFollowUp();
  };

  /** Cancel every async conversation timer (memory idle, follow-up, no-response, auto-advance, intro gap). */
  const clearAllConversationTimers = () => {
    clearAllMemoryIdleTimers();
    if (followUpTimerRef.current) {
      clearTimeout(followUpTimerRef.current);
      followUpTimerRef.current = null;
    }
    if (inquiryIdleTimerRef.current) {
      clearTimeout(inquiryIdleTimerRef.current);
      inquiryIdleTimerRef.current = null;
    }
    if (waitMessageTimerRef.current) {
      clearTimeout(waitMessageTimerRef.current);
      waitMessageTimerRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (preThinkTimerRef.current) {
      clearTimeout(preThinkTimerRef.current);
      preThinkTimerRef.current = null;
    }
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    if (typeof introDelayRef.current === 'function') {
      introDelayRef.current();
      introDelayRef.current = null;
    }
  };

  const [conversationStage, setConversationStage] = useState('introduction'); // From API: introduction | discussion | closing
  /** Always current stage for timeout/async callbacks (state in closures is often stale right after setConversationStage). */
  const conversationStageRef = useRef(conversationStage);
  const ttsActive = ttsEnabled && voiceOutputEnabled;
  const ttsAudioPlaying = ttsPlayingMessageId != null;
  /** Pause mic capture while bot TTS plays so speaker output is not recorded (all stages). */
  const ttsSuppressesMic = ttsAudioPlaying && micInputEnabled;
  const awakeningIntroPhase = useMemo(
    () => isAwakeningIntroPhase(messages, conversationStage),
    [messages, conversationStage],
  );
  /**
   * Gate chat reveal on TTS while voice is on. With mic enabled, still gate during awakening
   * intro so bubbles pace with narration; after the visitor's first reply, lines show
   * immediately so the mic path is never stuck waiting on audio.
   */
  const ttsGatesChatReveal =
    ttsActive && (!micInputEnabled || awakeningIntroPhase);
  const voiceBlocksComposer = useMemo(() => {
    // Introduction, discussion, and closing: visitor may type while narration plays.
    if (
      conversationStage === 'introduction' ||
      conversationStage === 'discussion' ||
      conversationStage === 'closing'
    ) {
      return false;
    }
    return voiceBlocksUserComposer({
      ttsActive: ttsGatesChatReveal,
      voiceDoneSet,
      messages,
      gateBot: voiceGateBot,
      ttsPlayingMessageId,
    });
  }, [
    conversationStage,
    ttsGatesChatReveal,
    voiceDoneSet,
    messages,
    voiceGateBot,
    ttsPlayingMessageId,
  ]);

  const speechInputDisabled = useMemo(() => {
    const optionsActive = activeOptionsMessageId != null && !optionsSubmitted;
    const chipOnly =
      optionsActive && messageOptionsRequireChipOnly(activeOptionsMessage);
    const blockWhileLoading =
      isLoading && conversationStage !== 'introduction';
    return blockWhileLoading || chipOnly || voiceBlocksComposer;
  }, [
    activeOptionsMessageId,
    optionsSubmitted,
    activeOptionsMessage,
    conversationStage,
    isLoading,
    voiceBlocksComposer,
  ]);
  const [agentsStoppedDueToTimeout, setAgentsStoppedDueToTimeout] = useState(false);
  const waitingMessageShownRef = useRef(false); // Set when 2min "wait for signal" shown — skip adding follow-up if in flight

  // Memoize onBlobClick callback to prevent unnecessary re-renders of Blob component
  const handleBlobClick = useCallback(() => {
    setShowBlobLegend((prev) => !prev);
  }, []);

  const showAuraBlobOverlay = showBlobLegend || reflectionMode;
  const conversationFinished = conversationClosed || agentsStoppedDueToTimeout;

  /** Chat composer accepts typing/dictation (same gates as the textarea `disabled` prop). */
  const composerAcceptsInput = useMemo(() => {
    if (showDisclaimer || showIntroduction) return false;
    if (conversationFinished) return false;
    if (showAuraBlobOverlay) return false;
    return !speechInputDisabled;
  }, [
    showDisclaimer,
    showIntroduction,
    conversationFinished,
    showAuraBlobOverlay,
    speechInputDisabled,
  ]);

  const handleTalkWithAnotherArtefact = useCallback(() => {
    stopAllVoiceOutput();
    stopSpeechInputRef.current({ cancelTranscription: true });
    setIsLoading(false);
    setPendingAutoAdvance(false);
    clearAllConversationTimers();
    setReflectionMode(false);
    setShowBlobLegend(false);
    setShowReflectModal(false);
    navigate('/');
  }, [navigate, stopAllVoiceOutput]);

  const handleOpenReflectModal = useCallback(() => {
    reflectEngagedRef.current = true;
    setTtsAutoplayBlocked(false);
    ttsAbortGestureRef.current?.();
    ttsAbortGestureRef.current = null;
    ttsFetchAbortRef.current?.abort();
    ttsFetchAbortRef.current = null;
    closureNarrationInflightRef.current.clear();
    stopTts();

    const botIds = messagesRef.current
      .filter((m) => m.type === 'bot')
      .map((m) => m.id);
    botIds.forEach((id) => playbackFinishedRef.current.add(id));
    ttsStartedIdsRef.current = new Set(playbackFinishedRef.current);
    if (botIds.length > 0) {
      setFinishedBotVoiceIds((prev) => {
        const s = new Set(prev);
        botIds.forEach((id) => s.add(id));
        return Array.from(s);
      });
      setTtsRevealedBotIds((prev) => {
        const s = new Set(prev);
        botIds.forEach((id) => s.add(id));
        return Array.from(s);
      });
    }

    ttsPipelineRunIdRef.current += 1;
    setShowReflectModal(true);
  }, [stopTts]);

  const artefactIdleRedirectEnabled = useMemo(() => {
    if (parseArtefactSessionPath(location.pathname)) return true;
    return isBareArtefactLandingPath(location.pathname);
  }, [location.pathname]);

  const artefactIdleActivityKey = useMemo(
    () =>
      `${location.pathname}|${showDisclaimer ? 1 : 0}|${showIntroduction ? 1 : 0}|${countUserMessages(messages)}`,
    [location.pathname, showDisclaimer, showIntroduction, messages],
  );

  const onboardingRouteView = parseArtefactSessionPath(location.pathname)?.view;

  useIdleRedirect({
    enabled: artefactIdleRedirectEnabled,
    timeoutMs: CHAT_IDLE_REDIRECT_MS,
    activityKey: artefactIdleActivityKey,
    events: [],
    onIdle: () => {
      void handleTalkWithAnotherArtefact();
    },
  });

  const [blobGifExportBusy, setBlobGifExportBusy] = useState(false);
  useEffect(() => {
    const onGifExportComplete = () => setBlobGifExportBusy(false);
    window.addEventListener('downloadBlobComplete', onGifExportComplete);
    return () => window.removeEventListener('downloadBlobComplete', onGifExportComplete);
  }, []);

  const talkWithAnotherArtefactButton =
    conversationFinished && showAuraBlobOverlay ? (
      <button
        type='button'
        className='preferences-audio-continue aura-talk-another-artefact-button'
        onClick={() => {
          void handleTalkWithAnotherArtefact();
        }}>
        {t('app.talkWithAnotherArtefact')}
      </button>
    ) : null;

  const blobDownloadButton = (
    <button
      type='button'
      className='blob-download-button-footer blob-download-button-overlay'
      disabled={blobGifExportBusy}
      aria-busy={blobGifExportBusy}
      onClick={() => {
        setBlobGifExportBusy(true);
        window.dispatchEvent(new CustomEvent('downloadBlob'));
      }}
      title={blobGifExportBusy ? t('app.creatingGif') : t('app.downloadAuraGif')}
      aria-label={blobGifExportBusy ? t('app.creatingGif') : t('app.downloadAuraGif')}>
      {blobGifExportBusy ? (
        <span className='spinner blob-download-button__spinner' aria-hidden='true' />
      ) : (
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
          <polyline points='7 10 12 15 17 10' />
          <line x1='12' y1='15' x2='12' y2='3' />
        </svg>
      )}
    </button>
  );


  /** Serialize concurrent creates so React always ends up with exactly one tab session id. */
  const initSessionInflightRef = useRef(null);

  const initSession = useCallback(async () => {
    if (initSessionInflightRef.current) {
      return initSessionInflightRef.current;
    }
    const promise = (async () => {
      const pathname = pathnameForApiRef.current || '';
      const persisted = readPersistedSessionIdForPath(pathname);
      if (persisted) {
        setSessionId(persisted);
        return persisted;
      }

      const parsed = parseArtefactSessionPath(pathname);
      const segment = parsed?.key != null && String(parsed.key).trim() !== '' ? String(parsed.key).trim() : null;
      const pmap = personasMapRef.current;
      const aliases = personaKeyAliasesRef.current;
      let personaKey = currentPersonaRef.current;
      if (pmap && segment) {
        personaKey = canonicalPersonaKey(segment, pmap, aliases);
      } else if (pmap && personaKey) {
        personaKey = canonicalPersonaKey(personaKey, pmap, aliases);
      }
      const result = await apiService.createSession({
        persona: personaKey || null,
        artefact_segment: segment,
        language: languageForSessionRef.current || 'en',
      });
      const serverId =
        result.success && result.session_id && String(result.session_id).trim()
          ? String(result.session_id).trim()
          : null;
      const id =
        serverId ||
        `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      if (!serverId) {
        console.warn(
          '[session] Backend did not return a session id; using client fallback (offline or error):',
          result.error || '(no message)'
        );
      }
      const stillPersisted = readPersistedSessionIdForPath(pathnameForApiRef.current || '');
      if (stillPersisted) {
        setSessionId(stillPersisted);
        return stillPersisted;
      }
      setSessionId(id);
      return id;
    })();
    initSessionInflightRef.current = promise;
    promise.finally(() => {
      if (initSessionInflightRef.current === promise) {
        initSessionInflightRef.current = null;
      }
    });
    return promise;
  }, []);

  /** Routes live inside App; this clears React state when starting a new run from the intro chooser. */
  const resetInMemoryChatForNewArtefactRun = useCallback((options = {}) => {
    const skipInitSession = options.skipInitSession === true;
    /** Must run before other updates so the sessionStorage snapshot effect does not re-save the previous id. */
    setSessionId(null);

    resetMicrophonePermissionState();
    stopSpeechInputRef.current({ cancelTranscription: true });

    stopTts();
    setFinishedBotVoiceIds([]);
    setTtsRevealedBotIds([]);
    cancelIntroStaggerReveal();
    ttsStartedIdsRef.current = new Set();
    playbackFinishedRef.current = new Set();
    ttsPlaybackTagRef.current = null;
    ttsBlobCacheRef.current.clear();
    ttsPrefetchPromisesRef.current.clear();
    ttsPipelineRunIdRef.current += 1;
    prevTtsOnForTtsSeedRef.current = false;
    prevVoiceOnForTtsSeedRef.current = voiceOutputEnabledRef.current;

    setMessages([]);
    setConversationClosed(false);
    setAgentsStoppedDueToTimeout(false);
    closedOrStoppedRef.current = false;
    reflectEngagedRef.current = false;
    setConversationStage('introduction');
    setConversationStartTime(null);
    setFeelingSliders({ excitement: 50, hope: 50 });
    setError('');
    setLocalEthicalCategories({});
    setImmediateClassifications([]);
    setShowDisclaimer(true);
    setShowIntroduction(false);
    setShowReflectModal(false);
    setShowInterfaceGuide(false);
    deferredGuideCloseTtsRef.current = null;
    guideAutoOpenedRef.current = false;
    setReflectionMode(false);
    setShowBlobLegend(false);
    setInputMessage('');
    setIsLoading(false);
    setSelectedOptions([]);
    setOptionsShown(false);
    setOptionsMessageId(null);
    setOptionsSubmitted(false);
    setAnsweredOptionMessageIds([]);
    waitingMessageShownRef.current = false;
    clearAllConversationTimers();
    pendingMemoryBotsRef.current = [];
    memoryOpeningPauseActiveRef.current = false;
    memoryHearMoreShownRef.current = false;
    memoryHearMoreEverShownRef.current = false;
    firstObserverMemoryDeliveredRef.current = false;
    memoryAnotherMemoryShownRef.current = false;
    introAwaitingNamingBubbleRef.current = false;
    setIntroAwaitingNamingBubble(false);
    setArtefactPrefaceDone(false);
    setPreferencesBeforeDisclaimerDone(false);
    introSequenceInflightRef.current = false;
    setIntroSegmentTexts([]);
    navigationEpochRef.current += 1;

    try {
      sessionStorage.removeItem('introduction_completed');
    } catch {
      /* ignore */
    }

    if (!skipInitSession) {
      void initSession();
    }

    applySavedMicAndVoicePreferences();
  }, [stopTts, initSession, cancelIntroStaggerReveal, applySavedMicAndVoicePreferences]);

  /** Any navigation to landing `/` (title link, browser back, etc.) ends the server session, wipes tab snapshot, clears messages in memory. */
  const prevPathnameForHomeResetRef = useRef(null);
  useLayoutEffect(() => {
    const was = prevPathnameForHomeResetRef.current;
    prevPathnameForHomeResetRef.current = location.pathname;

    if (location.pathname !== '/' || was === null || was === '/') return;

    const snap = readChatSessionSnapshot();
    const sid = snap?.sessionId;

    resetInMemoryChatForNewArtefactRun({ skipInitSession: true });
    resetMicAndVoicePreferencesToOff();
    stopSpeechInputRef.current({ cancelTranscription: true });

    try {
      clearPersistedChatSession();
      sessionStorage.removeItem('disclaimer_acknowledged');
      sessionStorage.removeItem('introduction_completed');
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith('aotf_prefs_gate_')) sessionStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }

    if (sid && typeof sid === 'string') {
      void apiService.endSession(sid).catch(() => {});
    }
  }, [location.pathname, resetInMemoryChatForNewArtefactRun, resetMicAndVoicePreferencesToOff]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const parseSnapshot = () => {
        try {
          const raw = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
          if (!raw) return null;
          const data = JSON.parse(raw);
          if (data?.version !== 1 || !data.sessionId) return null;
          return data;
        } catch {
          return null;
        }
      };

      const pathname =
        typeof window !== 'undefined' ? window.location.pathname : '';
      const isLanding = pathname === '/' || pathname === '';

      /** Initial load on `/` must not hydrate chat from a stale tab snapshot (home reset layout skips when `was === null`). */
      if (isLanding) {
        const landingSnap = readChatSessionSnapshot();
        const landingSid = landingSnap?.sessionId;
        try {
          clearPersistedChatSession();
          sessionStorage.removeItem('disclaimer_acknowledged');
          sessionStorage.removeItem('introduction_completed');
          for (const k of Object.keys(sessionStorage)) {
            if (k.startsWith('aotf_prefs_gate_')) sessionStorage.removeItem(k);
          }
        } catch {
          /* ignore */
        }
        if (landingSid && typeof landingSid === 'string') {
          void apiService.endSession(landingSid).catch(() => {});
        }
        resetInMemoryChatForNewArtefactRun({ skipInitSession: true });
      }

      const snapshot = parseSnapshot();
      const openingOnboardingRoute =
        typeof window !== 'undefined' &&
        (() => {
          const view = parseArtefactSessionPath(window.location.pathname)?.view;
          return view === 'disclaimer';
        })();

      const parsedBootstrapPath = parseArtefactSessionPath(pathname);
      const isChatViewReload = parsedBootstrapPath?.view === 'chat';

      const restoreChatVoiceAfterReload = (restoredMessages, snap) => {
        if (!isChatViewReload || !restoredMessages?.length) return;
        stopActiveTtsPlayback();
        applyChatReloadVoiceRestore(
          restoredMessages,
          snap,
          setFinishedBotVoiceIds,
          setTtsRevealedBotIds,
          playbackFinishedRef,
          ttsStartedIdsRef,
          personasMapRef.current,
          personaKeyAliasesRef.current,
        );
        chatReloadRestoreRef.current = true;
      };

      if (snapshot) {
        const restoredMessages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
        const snapMsgs = restoredMessages;

        const applyReloadedTranscript = (rawMessages) => {
          const finalized = finalizeReloadedChatMessages(
            rawMessages,
            snapMsgs,
            snapshot,
            personasMapRef.current,
            personaKeyAliasesRef.current,
            languageForSessionRef.current || 'en',
          );
          setMessages(finalized);
          applyReloadedOptionsUiState(finalized, snapshot, {
            setOptionsShown,
            setOptionsMessageId,
            setOptionsSubmitted,
            setAnsweredOptionMessageIds,
          });
          return finalized;
        };

        setSessionId(snapshot.sessionId);
        if (isChatViewReload && snapMsgs.length > 0) {
          const finalized = applyReloadedTranscript(snapMsgs);
          restoreChatVoiceAfterReload(finalized, snapshot);
        } else {
          setMessages(restoredMessages);
        }
        setConversationStage(snapshot.conversationStage || 'introduction');
        setConversationClosed(Boolean(snapshot.conversationClosed));
        setAgentsStoppedDueToTimeout(Boolean(snapshot.agentsStoppedDueToTimeout));
        setConversationStartTime(
          snapshot.conversationStartTime != null ? snapshot.conversationStartTime : null
        );
        setFeelingSliders(snapshot.feelingSliders || { excitement: 50, hope: 50 });
        setLocalEthicalCategories(snapshot.localEthicalCategories || {});
        setImmediateClassifications(snapshot.immediateClassifications || []);
        if (openingOnboardingRoute) {
          setShowDisclaimer(true);
          const onboardingView = parseArtefactSessionPath(pathname)?.view;
          setArtefactPrefaceDone(onboardingView === 'disclaimer');
          setPreferencesBeforeDisclaimerDone(onboardingView === 'disclaimer');
          setShowIntroduction(false);
        } else if (isChatViewReload && restoredMessages.length > 0) {
          setShowDisclaimer(snapshot.showDisclaimer !== undefined ? snapshot.showDisclaimer : false);
          setShowIntroduction(false);
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('introduction_completed', 'true');
          }
          if (snapshot.preferencesBeforeDisclaimerDone !== undefined) {
            setPreferencesBeforeDisclaimerDone(Boolean(snapshot.preferencesBeforeDisclaimerDone));
          }
          if (snapshot.artefactPrefaceDone !== undefined) {
            setArtefactPrefaceDone(Boolean(snapshot.artefactPrefaceDone));
          }
        } else if (isChatViewReload && snapshot.sessionId) {
          setShowDisclaimer(snapshot.showDisclaimer !== undefined ? snapshot.showDisclaimer : false);
          const pageReload = isChatPageReload(pathname);
          let introDoneOnReload = pageReload;
          if (!introDoneOnReload) {
            try {
              introDoneOnReload = sessionStorage.getItem('introduction_completed') === 'true';
            } catch {
              introDoneOnReload = false;
            }
          }
          setShowIntroduction(introDoneOnReload ? false : Boolean(snapshot.showIntroduction));
          if (snapshot.preferencesBeforeDisclaimerDone !== undefined) {
            setPreferencesBeforeDisclaimerDone(Boolean(snapshot.preferencesBeforeDisclaimerDone));
          }
          if (snapshot.artefactPrefaceDone !== undefined) {
            setArtefactPrefaceDone(Boolean(snapshot.artefactPrefaceDone));
          }
        } else {
          setShowDisclaimer(snapshot.showDisclaimer !== undefined ? snapshot.showDisclaimer : false);
          setShowIntroduction(Boolean(snapshot.showIntroduction));
          if (snapshot.preferencesBeforeDisclaimerDone !== undefined) {
            setPreferencesBeforeDisclaimerDone(Boolean(snapshot.preferencesBeforeDisclaimerDone));
          }
          if (snapshot.artefactPrefaceDone !== undefined) {
            setArtefactPrefaceDone(Boolean(snapshot.artefactPrefaceDone));
          }
        }
        if (snapshot.currentPersona) {
          setCurrentPersona(canonicalPersonaKey(snapshot.currentPersona, null, {}));
        }
        setSelectedOptions([]);
        setPendingAutoAdvance(Boolean(snapshot.pendingAutoAdvance));
        setError('');
        waitingMessageShownRef.current = false;

        conversationStageRef.current = snapshot.conversationStage || 'introduction';
        closedOrStoppedRef.current =
          Boolean(snapshot.conversationClosed) || Boolean(snapshot.agentsStoppedDueToTimeout);

        if (snapshot.sessionId && isChatViewReload) {
          const snapMsgs = Array.isArray(snapshot.messages) ? snapshot.messages : [];
          let apiMessages = [];
          let flowState = null;
          const reloadLanguage = languageForSessionRef.current || 'en';
          try {
            const [apiResult, flowResult] = await Promise.all([
              apiService.getSessionMessages(snapshot.sessionId),
              apiService.getSessionFlowState(snapshot.sessionId),
            ]);
            if (flowResult?.success) {
              flowState = flowResult;
            }
            if (!cancelled && apiResult.success && Array.isArray(apiResult.messages)) {
              apiMessages = uiMessagesFromApiRecords(apiResult.messages);
              const reloadPersonaKey = snapshot?.currentPersona
                ? canonicalPersonaKey(
                    snapshot.currentPersona,
                    personasMapRef.current,
                    personaKeyAliasesRef.current,
                  )
                : null;
              const introTexts = resolveCanonicalIntroSegmentTexts(
                reloadPersonaKey,
                personasMapRef.current,
                snapshot,
                snapMsgs,
                languageForSessionRef.current || language,
              );
              const merged = mergeChatReloadTranscript(
                snapMsgs,
                apiMessages,
                introTexts,
              );
              if (merged.length > 0) {
                const finalized = applyReloadedTranscript(merged);
                restoreChatVoiceAfterReload(finalized, snapshot);
                if (typeof window !== 'undefined') {
                  setShowIntroduction(false);
                  sessionStorage.setItem('introduction_completed', 'true');
                }
              }
              scheduleChatReloadPendingReply({
                isChatViewReload,
                snapshot,
                snapshotMessages: snapMsgs,
                mergedMessages: merged.length > 0 ? merged : snapMsgs,
                apiMessages,
                flowState,
                language: reloadLanguage,
                setPendingReply: setChatReloadPendingReply,
              });
            } else if (!cancelled) {
              scheduleChatReloadPendingReply({
                isChatViewReload,
                snapshot,
                snapshotMessages: snapMsgs,
                mergedMessages: snapMsgs,
                apiMessages: [],
                flowState,
                language: reloadLanguage,
                setPendingReply: setChatReloadPendingReply,
              });
            }
          } catch {
            if (!cancelled) {
              scheduleChatReloadPendingReply({
                isChatViewReload,
                snapshot,
                snapshotMessages: snapMsgs,
                mergedMessages: snapMsgs,
                apiMessages: [],
                flowState,
                language: reloadLanguage,
                setPendingReply: setChatReloadPendingReply,
              });
            }
          }
        } else if (
          (!snapshot.messages || snapshot.messages.length === 0) &&
          snapshot.sessionId
        ) {
          const apiResult = await apiService.getSessionMessages(snapshot.sessionId);
          if (
            !cancelled &&
            apiResult.success &&
            apiResult.messages &&
            apiResult.messages.length > 0
          ) {
            const apiMessages = uiMessagesFromApiRecords(apiResult.messages);
            const finalized = applyReloadedTranscript(apiMessages);
            restoreChatVoiceAfterReload(finalized, snapshot);
            if (typeof window !== 'undefined') {
              setShowIntroduction(false);
              sessionStorage.setItem('introduction_completed', 'true');
            }
          }
        }
      } else if (!isLanding && !readPersistedSessionIdForPath(pathname)) {
        sessionStorage.removeItem('disclaimer_acknowledged');
        sessionStorage.removeItem('introduction_completed');

        resetInMemoryChatForNewArtefactRun();
      }

      checkApiHealth();
      loadPersonas();
      const storedApiKey = localStorage.getItem('openai_api_key');
      if (storedApiKey) {
        setApiKey(storedApiKey);
      }
      const storedSystemPrompt = localStorage.getItem('custom_system_prompt');
      if (storedSystemPrompt) {
        setCustomSystemPrompt(storedSystemPrompt);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once on mount; pathname read inside
  }, [stopTts, resetInMemoryChatForNewArtefactRun]);

  /** Landing/bootstrap often skip initSession; disclaimer requires a session id — create one on artefact routes. */
  useEffect(() => {
    const p = parseArtefactSessionPath(location.pathname);
    if (!p?.key) return;
    if (p.view !== 'introduction' && p.view !== 'preferences' && p.view !== 'chat' && p.view !== 'disclaimer') return;
    if (sessionId) return;
    const persisted = readPersistedSessionIdForPath(location.pathname);
    if (persisted) {
      setSessionId(persisted);
      return;
    }
    void initSession();
  }, [location.pathname, sessionId, initSession]);

  useEffect(() => {
    conversationStageRef.current = conversationStage;
  }, [conversationStage]);

  useEffect(() => {
    introAwaitingNamingBubbleRef.current = introAwaitingNamingBubble;
  }, [introAwaitingNamingBubble]);

  // Keep the same server session + transcript across full page reloads (tab sessionStorage).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!sessionId) return;

    const introGateComplete = !showDisclaimer && !showIntroduction;
    const introSegmentTextsForSnapshot = (() => {
      const personaKey = currentPersona
        ? canonicalPersonaKey(currentPersona, personasMap, personaKeyAliases)
        : null;
      const fromBackend = introSegmentsFromPersonaCatalog(personaKey, personasMap);
      return ensureEthicalAuraIntroSegment(
        mergeIntroSegmentSources(fromBackend, introSegmentTexts),
        language,
      );
    })();
    const payload = {
      version: 1,
      sessionId,
      messages,
      conversationStage,
      conversationClosed,
      conversationStartTime,
      showDisclaimer,
      showIntroduction,
      artefactPrefaceDone,
      preferencesBeforeDisclaimerDone,
      introGateComplete,
      currentPersona,
      agentsStoppedDueToTimeout,
      feelingSliders,
      localEthicalCategories,
      immediateClassifications,
      optionsShown,
      optionsMessageId,
      optionsSubmitted,
      answeredOptionMessageIds,
      ttsRevealedBotIds,
      finishedBotVoiceIds,
      isLoading,
      pendingAutoAdvance,
      language,
      ...(introSegmentTextsForSnapshot?.length ? { introSegmentTexts: introSegmentTextsForSnapshot } : {}),
    };
    try {
      sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      if (e && e.name === 'QuotaExceededError') {
        try {
          const lean = { ...payload, messages: [] };
          sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, JSON.stringify(lean));
        } catch (_) {
          /* ignore */
        }
      }
    }
  }, [
    sessionId,
    messages,
    conversationStage,
    conversationClosed,
    conversationStartTime,
    showDisclaimer,
    showIntroduction,
    artefactPrefaceDone,
    preferencesBeforeDisclaimerDone,
    currentPersona,
    personasMap,
    personaKeyAliases,
    agentsStoppedDueToTimeout,
    feelingSliders,
    localEthicalCategories,
    immediateClassifications,
    optionsShown,
    optionsMessageId,
    optionsSubmitted,
    answeredOptionMessageIds,
    ttsRevealedBotIds,
    finishedBotVoiceIds,
    isLoading,
    pendingAutoAdvance,
    introSegmentTexts,
  ]);

  const loadPersonaInfo = useCallback(async (personaName) => {
    const epochStart = navigationEpochRef.current;
    try {
      const response = await apiService.getPersonas(language);
      if (epochStart !== navigationEpochRef.current) return;
      if (response.success && response.personas) {
        // Find persona by original name or translated name
        let persona = response.personas[personaName];
        let displayName = persona?.display_name || personaName;

        if (!persona) {
          // Try to find by original_name field
          for (const [key, value] of Object.entries(response.personas)) {
            if (value.original_name === personaName) {
              persona = value;
              displayName = value.display_name || key; // Use translated/display name for display
              break;
            }
          }
        }

        if (persona) {
          const shownName =
            typeof persona.display_title === 'string' && persona.display_title.trim()
              ? persona.display_title.trim()
              : displayName;
          setDefaultPersonaInfo({
            name: shownName,
            originalName: persona.original_name || personaName,
            systemPrompt: persona.system_prompt,
            description: persona.description || '',
          });
        }
      }
    } catch (error) {
      console.error('Error loading persona info:', error);
    }
  }, [language]);

  /** Tab snapshots can store pre-rename persona keys; align once real persona keys load from the API. */
  useEffect(() => {
    if (!personasMap) return;
    const next = canonicalPersonaKey(currentPersona, personasMap, personaKeyAliases);
    if (next !== currentPersona) {
      setCurrentPersona(next);
      void loadPersonaInfo(next);
    }
  }, [personasMap, personaKeyAliases, currentPersona, loadPersonaInfo]);

  const displayArtefactName = useMemo(() => {
    const fromInfo = defaultPersonaInfo?.name?.trim();
    if (fromInfo) return fromInfo;
    const dn = personasMap?.[effectiveRoutePersona]?.display_name;
    if (typeof dn === 'string' && dn.trim()) return dn.trim();
    // Before personas load, route segment may be "2" — avoid "2 is thinking..."
    if (/^\d+$/.test(String(effectiveRoutePersona || '').trim())) {
      return t('app.exhibitionCompanion');
    }
    return effectiveRoutePersona;
  }, [defaultPersonaInfo?.name, personasMap, effectiveRoutePersona, t]);

  const loadPersonas = useCallback(async () => {
    const epochStart = navigationEpochRef.current;
    try {
      const response = await apiService.getPersonas(language);
      if (epochStart !== navigationEpochRef.current) return;
      if (response.success && response.personas && response.default) {
        setPersonasMap(response.personas);
        const aliases =
          response.persona_key_aliases && typeof response.persona_key_aliases === 'object'
            ? response.persona_key_aliases
            : {};
        setPersonaKeyAliases(aliases);
        const defaultPersonaKey = response.default;
        const defaultPersona = response.personas[defaultPersonaKey];
        if (defaultPersona) {
          // Use the persona display name when provided, otherwise the key (may be translated)
          const personaName = defaultPersona.display_name || defaultPersonaKey;
          // Use original name for backend operations
          const originalPersonaName = defaultPersona.original_name || defaultPersonaKey;
          const parsedPath = parseArtefactSessionPath(pathnameForApiRef.current);
          const fromPath = parsedPath?.key ?? null;
          const fromState = locationStateRef.current?.selectedPersona;
          const explicit = fromPath || fromState;
          if (explicit) {
            const canon = canonicalPersonaKey(explicit, response.personas, aliases);
            setCurrentPersona(canon);
            await loadPersonaInfo(canon);
          } else {
            setDefaultPersonaInfo({
              name: personaName,
              originalName: originalPersonaName,
              systemPrompt: defaultPersona.system_prompt,
              description: defaultPersona.description || '',
            });
            setCurrentPersona(
              canonicalPersonaKey(originalPersonaName, response.personas, aliases)
            );
          }
        }
      }
    } catch (error) {
      console.error('Error loading personas:', error);
      setPersonasMap(null);
      setPersonaKeyAliases({});
    }
  }, [language, location.pathname, location.state, loadPersonaInfo]);

  const pinChatScrollToBottom = useCallback(() => {
    const container = chatMessagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  const isChatPinnedToBottom = useCallback((container) => {
    const threshold = 48;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      threshold
    );
  }, []);

  /**
   * Scroll so the newest message is fully visible when it fits in the chat viewport;
   * if it is taller than the viewport, align its start to the top of the scroll area.
   */
  const scrollChatToRelevantPosition = useCallback(() => {
    const container = chatMessagesRef.current;
    if (!container) return;

    // Keyboard / composer resize skews getBoundingClientRect on mobile — stay pinned.
    if (composerFocusedRef.current) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    const nodeList = container.querySelectorAll('[data-message-id]');
    const lastEl = nodeList.length ? nodeList[nodeList.length - 1] : null;

    if (!lastEl) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    const available = container.clientHeight;
    const containerRect = container.getBoundingClientRect();
    const messageRect = lastEl.getBoundingClientRect();
    const messageHeight = messageRect.height;

    if (messageHeight > available) {
      container.scrollTop += messageRect.top - containerRect.top;
      return;
    }

    if (messageRect.bottom > containerRect.bottom) {
      container.scrollTop += messageRect.bottom - containerRect.bottom;
    } else if (messageRect.top < containerRect.top) {
      container.scrollTop += messageRect.top - containerRect.top;
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  useLayoutEffect(() => {
    scrollChatToRelevantPosition();
    const raf = requestAnimationFrame(scrollChatToRelevantPosition);
    return () => cancelAnimationFrame(raf);
  }, [
    messages,
    isLoading,
    ttsRevealedBotIds,
    ttsPlayingMessageId,
    scrollChatToRelevantPosition,
  ]);

  const syncMessageInputHeight = useCallback((inputEl) => {
    const el = inputEl || messageInputRef.current;
    if (!el) return;
    const zoom = Number(getComputedStyle(el).zoom) || 1;
    if (el.classList.contains('message-input--chip-only')) {
      const height = CHAT_SEND_EMBED_SIZE_PX / zoom;
      el.style.height = `${height}px`;
      el.style.overflowY = 'hidden';
      el.classList.remove('message-input--placeholder-single');
      return;
    }
    const maxHeight = CHAT_INPUT_MAX_HEIGHT_PX / zoom;
    const minHeight = CHAT_INPUT_EXPANDED_MIN_HEIGHT_PX / zoom;
    if (!el.value.trim()) {
      const placeholder = el.getAttribute('placeholder') || '';
      const minRowHeight = CHAT_SEND_EMBED_SIZE_PX / zoom;
      const singleLine = !placeholder || isPlaceholderSingleLine(el, placeholder);
      el.classList.toggle('message-input--placeholder-single', singleLine);
      const measured = placeholder ? measurePlaceholderBlockHeight(el, placeholder) / zoom : 0;
      const nextHeight = Math.min(Math.max(measured || minRowHeight, minRowHeight), maxHeight);
      el.style.height = `${nextHeight}px`;
      el.style.overflowY = measured > maxHeight ? 'auto' : 'hidden';
      return;
    }
    el.classList.remove('message-input--placeholder-single');
    el.style.height = '0px';
    const scrollHeight = el.scrollHeight;
    const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const syncBlobBackgroundHeight = useCallback(() => {
    const messagesEl = chatMessagesRef.current;
    const bgEl = backgroundVisRef.current;
    if (!messagesEl || !bgEl) return;
    const messagesHeight = messagesEl.offsetHeight;
    // Never clamp taller than the messages column — a fixed min was overlapping the composer in Firefox.
    bgEl.style.height =
      messagesHeight > 0 ? `${messagesHeight}px` : '160px';
  }, []);

  useLayoutEffect(() => {
    const container = chatMessagesRef.current;
    const pinned = container ? isChatPinnedToBottom(container) : true;
    syncMessageInputHeight();
    syncBlobBackgroundHeight();
    if (pinned) {
      pinChatScrollToBottom();
    }
  }, [
    inputMessage,
    activeOptionsMessageId,
    optionsSubmitted,
    syncMessageInputHeight,
    syncBlobBackgroundHeight,
    isChatPinnedToBottom,
    pinChatScrollToBottom,
  ]);

  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return undefined;
    const resizeObserver = new ResizeObserver(() => {
      syncMessageInputHeight();
    });
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [syncMessageInputHeight]);

  /** When aura / reflection hid the transcript, coming back often leaves scroll at the top — jump to latest. */
  const chatHiddenByAuraRef = useRef(false);
  useEffect(() => {
    const hidden = showBlobLegend || reflectionMode;
    if (chatHiddenByAuraRef.current && !hidden) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollChatToRelevantPosition();
        });
      });
    }
    chatHiddenByAuraRef.current = hidden;
  }, [showBlobLegend, reflectionMode, scrollChatToRelevantPosition]);

  // Sync background visualization height with chat-messages height
  useEffect(() => {
    syncBlobBackgroundHeight();
    const onLayoutChange = () => {
      syncBlobBackgroundHeight();
      if (composerFocusedRef.current) {
        pinChatScrollToBottom();
      }
    };

    window.addEventListener('resize', onLayoutChange);
    window.visualViewport?.addEventListener('resize', onLayoutChange);
    window.visualViewport?.addEventListener('scroll', onLayoutChange);

    const resizeObserver = new ResizeObserver(onLayoutChange);
    if (chatMessagesRef.current) {
      resizeObserver.observe(chatMessagesRef.current);
    }
    if (chatBodyRef.current) {
      resizeObserver.observe(chatBodyRef.current);
    }

    return () => {
      window.removeEventListener('resize', onLayoutChange);
      window.visualViewport?.removeEventListener('resize', onLayoutChange);
      window.visualViewport?.removeEventListener('scroll', onLayoutChange);
      resizeObserver.disconnect();
    };
  }, [messages, syncBlobBackgroundHeight, pinChatScrollToBottom]);

  // Fit the mobile app shell to the visible viewport when the keyboard shrinks it (Firefox).
  useEffect(() => {
    const root = document.querySelector('.App.mobile-layout');
    const vv = window.visualViewport;
    if (!root || !vv) return;

    const syncMobileShellHeight = () => {
      const layoutHeight = window.innerHeight;
      const visibleHeight = Math.round(vv.height);
      if (visibleHeight > 0 && visibleHeight < layoutHeight - 8) {
        root.style.height = `${visibleHeight}px`;
        root.style.maxHeight = `${visibleHeight}px`;
      } else {
        root.style.height = '';
        root.style.maxHeight = '';
      }
      syncBlobBackgroundHeight();
      if (composerFocusedRef.current) {
        requestAnimationFrame(pinChatScrollToBottom);
      }
    };

    syncMobileShellHeight();
    vv.addEventListener('resize', syncMobileShellHeight);
    vv.addEventListener('scroll', syncMobileShellHeight);
    return () => {
      vv.removeEventListener('resize', syncMobileShellHeight);
      vv.removeEventListener('scroll', syncMobileShellHeight);
      root.style.height = '';
      root.style.maxHeight = '';
    };
  }, [syncBlobBackgroundHeight, pinChatScrollToBottom]);

  // Keep transcript pinned while the mobile keyboard or growing composer shifts layout.
  useEffect(() => {
    const keepPinnedWhileComposing = () => {
      if (!composerFocusedRef.current) return;
      pinChatScrollToBottom();
    };
    window.visualViewport?.addEventListener('resize', keepPinnedWhileComposing);
    window.visualViewport?.addEventListener('scroll', keepPinnedWhileComposing);
    return () => {
      window.visualViewport?.removeEventListener('resize', keepPinnedWhileComposing);
      window.visualViewport?.removeEventListener('scroll', keepPinnedWhileComposing);
    };
  }, [pinChatScrollToBottom]);

  // Timer for automatic closure after MAX_CONVERSATION_TIME_MS
  useEffect(() => {
    if (!conversationStartTime || conversationClosed) return;

    const timeElapsed = Date.now() - conversationStartTime;
    const timeRemaining = MAX_CONVERSATION_TIME_MS - timeElapsed;

    if (timeRemaining <= 0) {
      if (triggerClosureRef.current) {
        triggerClosureRef.current();
      }
      return;
    }

    const timer = setTimeout(() => {
      if (triggerClosureRef.current) {
        triggerClosureRef.current();
      }
    }, timeRemaining);

    return () => clearTimeout(timer);
  }, [conversationStartTime, conversationClosed]);

  const checkApiHealth = async () => {
    try {
      await apiService.getHealth();
    } catch (error) {
      console.error('API health check failed:', error);
    }
  };


  // Clear auto follow-up / inquiry idle timers (not memory-opening idle — see clearMemoryIdleTimer).
  const clearFollowUpTimer = () => {
    if (followUpTimerRef.current) {
      clearTimeout(followUpTimerRef.current);
      followUpTimerRef.current = null;
    }
    if (inquiryIdleTimerRef.current) {
      clearTimeout(inquiryIdleTimerRef.current);
      inquiryIdleTimerRef.current = null;
    }
  };

  const lastRenderableArtefactBot = (messages) => {
    if (!Array.isArray(messages)) return null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m?.type !== 'bot') continue;
      if (m.memoryHearMorePrompt || m.memoryAnotherMemoryPrompt) continue;
      if (!botMessageIsRenderable(m)) continue;
      return m;
    }
    return null;
  };

  const startMemoryIdleFollowUpTimer = useCallback(
    (idleMs, userCountAtSchedule) => {
      if (!idleMs || idleMs <= 0) return;
      if (conversationStageRef.current === 'introduction') return;
      if (introAwaitingNamingBubbleRef.current) return;
      if (memoryHearMoreEverShownRef.current) return;
      if (memoryIdleTimerRef.current) {
        clearTimeout(memoryIdleTimerRef.current);
        memoryIdleTimerRef.current = null;
      }
      memoryIdleTimerUserCountRef.current = userCountAtSchedule;
      memoryIdleTimerRef.current = setTimeout(() => {
        memoryIdleTimerRef.current = null;
        if (closedOrStoppedRef.current) return;
        if (conversationStageRef.current === 'introduction') return;
        if (introAwaitingNamingBubbleRef.current) return;
        if (memoryHearMoreEverShownRef.current) return;
        if (memoryHearMoreShownRef.current) return;
        let addedHearMore = false;
        let hearMoreBot = null;
        setMessages((prev) => {
          if (hasUserMessageSince(prev, memoryIdleTimerUserCountRef.current)) return prev;
          if (prev.some((m) => m.memoryHearMorePrompt)) return prev;
          const lastBot = lastRenderableArtefactBot(prev);
          if (lastBot && memoryHearMoreIdleSuppressed(lastBot.content, language)) return prev;
          hearMoreBot = makeMemoryHearMoreBotMessage(t);
          addedHearMore = true;
          return [...prev, hearMoreBot];
        });
        if (addedHearMore) {
          memoryHearMoreShownRef.current = true;
          memoryHearMoreEverShownRef.current = true;
          if (hearMoreBot) prefetchBotsForVoice([hearMoreBot]);
        }
      }, idleMs);
    },
    [t, language, prefetchBotsForVoice],
  );

  const tryScheduleMemoryIdleFollowUp = useCallback(() => {
    const idleMs = pendingMemoryIdleMsRef.current;
    const anchorId = memoryIdleAnchorBotIdRef.current;
    if (!idleMs || idleMs <= 0 || !anchorId) return;
    if (conversationStageRef.current === 'introduction') return;
    if (introAwaitingNamingBubbleRef.current) return;
    if (memoryHearMoreEverShownRef.current) return;
    if (memoryIdleTimerRef.current) return;

    const msgs = messagesRef.current;
    const userCountAtAnchor = memoryIdleUserCountAtAnchorRef.current;
    if (hasUserMessageSince(msgs, userCountAtAnchor)) {
      clearPendingMemoryIdleFollowUp();
      return;
    }

    const voicePipelineActive = voiceOutputEnabledRef.current && ttsEnabledRef.current;
    const revealedSet = computeTtsRevealedSet(msgs, ttsRevealedBotIdsRef.current);
    const doneSet = computeVoiceDoneSet(msgs, Array.from(playbackFinishedRef.current));
    if (!memoryIdleAnchorBotReady(anchorId, msgs, voicePipelineActive, revealedSet, doneSet)) {
      return;
    }

    clearPendingMemoryIdleFollowUp();
    startMemoryIdleFollowUpTimer(idleMs, userCountAtAnchor);
  }, [startMemoryIdleFollowUpTimer]);

  const queueMemoryIdleFollowUp = useCallback(
    (idleMs, anchorBot, userCountAtAnchor) => {
      if (!idleMs || idleMs <= 0 || !anchorBot?.id) return;
      pendingMemoryIdleMsRef.current = idleMs;
      memoryIdleAnchorBotIdRef.current = anchorBot.id;
      memoryIdleUserCountAtAnchorRef.current = userCountAtAnchor;
      tryScheduleMemoryIdleFollowUp();
    },
    [tryScheduleMemoryIdleFollowUp],
  );

  const scheduleMemoryAnotherMemoryIdle = useCallback(
    (idleMs) => {
      if (!idleMs || idleMs <= 0) return;
      if (conversationStageRef.current === 'introduction') return;
      if (introAwaitingNamingBubbleRef.current) return;
      if (memoryAnotherMemoryIdleTimerRef.current) {
        clearTimeout(memoryAnotherMemoryIdleTimerRef.current);
        memoryAnotherMemoryIdleTimerRef.current = null;
      }
      memoryAnotherMemoryIdleTimerRef.current = setTimeout(() => {
        memoryAnotherMemoryIdleTimerRef.current = null;
        if (closedOrStoppedRef.current) return;
        if (conversationStageRef.current === 'introduction') return;
        if (introAwaitingNamingBubbleRef.current) return;
        if (memoryAnotherMemoryShownRef.current) return;
        memoryAnotherMemoryShownRef.current = true;
        setMessages((prev) => {
          if (prev.some((m) => m.memoryAnotherMemoryPrompt)) return prev;
          const lastBot = lastRenderableArtefactBot(prev);
          if (lastBot && assistantTextEndsWithQuestion(lastBot.content, language)) return prev;
          return [...prev, makeMemoryAnotherMemoryBotMessage(t)];
        });
      }, idleMs);
    },
    [t, language],
  );

  const flushPendingMemoryBots = useCallback(async (dedupeContext, explicitQueue) => {
    const pending = explicitQueue ?? pendingMemoryBotsRef.current;
    if (!pending.length) return false;
    clearAllMemoryIdleTimers();
    pendingMemoryBotsRef.current = [];
    memoryOpeningPauseActiveRef.current = false;
    memoryHearMoreShownRef.current = false;
    memoryAnotherMemoryShownRef.current = false;
    const existing = dedupeContext || messagesRef.current;
    const deduped = filterDuplicateBotMessages(existing, pending);
    if (!deduped.length) return true;
    const voicePipeline = voiceOutputEnabledRef.current && ttsEnabledRef.current;
    await appendBotResponseMessages(
      setMessages,
      setIsLoading,
      deduped,
      voicePipeline,
      prefetchBotsForVoice,
      existing,
      closedOrStoppedRef,
    );
    const optBubble = [...deduped]
      .reverse()
      .find((m) => Array.isArray(m.options) && m.options.length > 0);
    if (optBubble) {
      setOptionsShown(true);
      setOptionsMessageId(optBubble.id);
      setOptionsSubmitted(false);
    }
    return true;
  }, [prefetchBotsForVoice]);

  const deliverDiscussionBotMessages = useCallback(
    async (response, newBots) => {
      const { immediate, deferred, idleMs } = partitionMemoryOpeningBots(
        response,
        newBots,
        language,
      );
      if (response?.stage) {
        conversationStageRef.current = response.stage;
      }
      pendingMemoryBotsRef.current = deferred;
      memoryOpeningPauseActiveRef.current = deferred.length > 0;
      memoryHearMoreShownRef.current = false;
      memoryAnotherMemoryShownRef.current = false;
      clearAllMemoryIdleTimers();

      const stageForIdle = response?.stage || conversationStageRef.current;
      const mayScheduleIdle =
        stageForIdle !== 'introduction' &&
        mayScheduleMemoryIdlePrompts(response, newBots, language);
      const isFirstObserverMemoryOpening =
        !firstObserverMemoryDeliveredRef.current &&
        (response?.entry_mode === 'memory' ||
          Boolean(response?.memory_pause_after_first_segment));
      if (isFirstObserverMemoryOpening && immediate.length > 0) {
        firstObserverMemoryDeliveredRef.current = true;
      }
      const idleToSchedule =
        mayScheduleIdle && idleMs > 0 && isFirstObserverMemoryOpening && !memoryHearMoreEverShownRef.current
          ? idleMs
          : 0;
      if (idleToSchedule <= 0) {
        clearPendingMemoryIdleFollowUp();
      }

      let didStagger = false;
      if (immediate.length > 0) {
        const voicePipeline = voiceOutputEnabled && ttsEnabled;
        didStagger = await appendBotResponseMessages(
          setMessages,
          setIsLoading,
          immediate,
          voicePipeline,
          prefetchBotsForVoice,
          null,
          closedOrStoppedRef,
        );
      }
      if (idleToSchedule > 0) {
        queueMemoryIdleFollowUp(
          idleToSchedule,
          resolveMemoryIdleAnchorBot(response, immediate),
          countUserMessages(messagesRef.current),
        );
      }
      const anotherMemoryIdleMs = Number(response?.memory_another_memory_idle_ms) || 0;
      scheduleMemoryAnotherMemoryIdle(mayScheduleIdle ? anotherMemoryIdleMs : 0);
      return didStagger;
    },
    [
      voiceOutputEnabled,
      ttsEnabled,
      prefetchBotsForVoice,
      queueMemoryIdleFollowUp,
      scheduleMemoryAnotherMemoryIdle,
      language,
    ],
  );

  useEffect(() => {
    tryScheduleMemoryIdleFollowUp();
  }, [
    tryScheduleMemoryIdleFollowUp,
    finishedBotVoiceIds,
    ttsRevealedBotIds,
    messages,
    voiceOutputEnabled,
    ttsEnabled,
  ]);

  // Clear auto-advance timer and its pre-thinking indicator
  const clearAutoAdvanceTimer = () => {
    if (preThinkTimerRef.current) {
      clearTimeout(preThinkTimerRef.current);
      preThinkTimerRef.current = null;
    }
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    setPendingAutoAdvance(false);
  };

  /** Pause scheduled auto-advance without clearing the pending beat flag (prefs overlay). */
  const pauseAutoAdvanceTimerOnly = useCallback(() => {
    if (preThinkTimerRef.current) {
      clearTimeout(preThinkTimerRef.current);
      preThinkTimerRef.current = null;
    }
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  }, []);

  const restoreChatUiAfterFlowInterrupt = useCallback((messageRows) => {
    const rows = Array.isArray(messageRows) ? messageRows : messagesRef.current;
    if (!rows.length) return;
    const snap = readChatSessionSnapshot();
    stopActiveTtsPlayback();
    applyChatReloadVoiceRestore(
      rows,
      snap,
      setFinishedBotVoiceIds,
      setTtsRevealedBotIds,
      playbackFinishedRef,
      ttsStartedIdsRef,
      personasMapRef.current,
      personaKeyAliasesRef.current,
    );
    chatReloadRestoreRef.current = true;
    applyReloadedOptionsUiState(rows, snap, {
      setOptionsShown,
      setOptionsMessageId,
      setOptionsSubmitted,
      setAnsweredOptionMessageIds,
    });
  }, []);

  const hydrateInMemoryTranscriptFromSnapshot = useCallback(() => {
    if (messagesRef.current.length > 0) {
      return messagesRef.current;
    }
    const snap = readChatSessionSnapshot();
    const snapMsgs = Array.isArray(snap?.messages) ? snap.messages : [];
    if (snapMsgs.length === 0) {
      return [];
    }
    const finalized = finalizeReloadedChatMessages(
      snapMsgs,
      snapMsgs,
      snap,
      personasMapRef.current,
      personaKeyAliasesRef.current,
      languageForSessionRef.current || language,
    );
    setMessages(finalized);
    setShowIntroduction(false);
    try {
      sessionStorage.setItem('introduction_completed', 'true');
    } catch {
      /* ignore */
    }
    restoreChatUiAfterFlowInterrupt(finalized);
    return finalized;
  }, [language, restoreChatUiAfterFlowInterrupt]);

  const fetchAndScheduleConversationFlowResume = useCallback(async () => {
    const activeSessionId = sessionId || readChatSessionSnapshot()?.sessionId;
    let localMessages = messagesRef.current;
    if (!Array.isArray(localMessages) || localMessages.length === 0) {
      localMessages = hydrateInMemoryTranscriptFromSnapshot();
    }
    if (!activeSessionId || !Array.isArray(localMessages) || localMessages.length === 0) {
      return;
    }
    if (flowResumeInflightRef.current) return;
    flowResumeInflightRef.current = true;
    try {
      const snap = readChatSessionSnapshot();
      const snapMsgs =
        localMessages.length > 0
          ? localMessages
          : Array.isArray(snap?.messages)
            ? snap.messages
            : [];
      let apiMessages = [];
      let flowState = null;
      const reloadLanguage = languageForSessionRef.current || language;
      try {
        const [apiResult, flowResult] = await Promise.all([
          apiService.getSessionMessages(activeSessionId),
          apiService.getSessionFlowState(activeSessionId),
        ]);
        if (flowResult?.success) {
          flowState = flowResult;
        }
        if (apiResult.success && Array.isArray(apiResult.messages)) {
          apiMessages = uiMessagesFromApiRecords(apiResult.messages);
        }
      } catch {
        /* resume from local transcript only */
      }
      const reloadPersonaKey = snap?.currentPersona
        ? canonicalPersonaKey(
            snap.currentPersona,
            personasMapRef.current,
            personaKeyAliasesRef.current,
          )
        : null;
      const introTexts = resolveCanonicalIntroSegmentTexts(
        reloadPersonaKey,
        personasMapRef.current,
        snap,
        snapMsgs,
        reloadLanguage,
      );
      const merged = mergeChatReloadTranscript(
        snapMsgs,
        apiMessages,
        introTexts,
      );
      const mergedMessages = merged.length > 0 ? merged : localMessages;
      scheduleConversationFlowResume({
        snapshot: snap,
        snapshotMessages: snapMsgs,
        mergedMessages,
        apiMessages,
        flowState,
        language: reloadLanguage,
        setPendingReply: setChatReloadPendingReply,
      });
    } finally {
      flowResumeInflightRef.current = false;
    }
  }, [sessionId, language, hydrateInMemoryTranscriptFromSnapshot]);

  const resumeConversationAfterPrefsReturn = useCallback(() => {
    if (parseArtefactSessionPath(location.pathname)?.view !== 'chat') return;
    if (!hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())) {
      return;
    }
    if (prefsReturnResumePathRef.current === location.pathname) return;
    prefsReturnResumePathRef.current = location.pathname;

    setShowDisclaimer(false);
    setShowIntroduction(false);
    if (messagesRef.current.length === 0) {
      hydrateInMemoryTranscriptFromSnapshot();
    } else {
      restoreChatUiAfterFlowInterrupt(messagesRef.current);
    }
    chatReloadRestoreRef.current = false;

    // Voice enabled on prefs return: existing transcript stays visible/text-only; TTS only for the next new bot line.
    if (voiceOutputEnabledRef.current) {
      const msgs = messagesRef.current;
      markAllBotMessagesVoiceComplete(
        msgs,
        playbackFinishedRef.current,
        setFinishedBotVoiceIds,
        setTtsRevealedBotIds,
      );
      const allBotIds = msgs.filter((m) => m.type === 'bot').map((m) => m.id);
      ttsStartedIdsRef.current = new Set(allBotIds);
      ttsRevealedBotIdsRef.current = allBotIds;
    }

    if (resumeIntroPresentationAfterInterrupt()) return;
    void fetchAndScheduleConversationFlowResume();
  }, [
    location.pathname,
    hydrateInMemoryTranscriptFromSnapshot,
    restoreChatUiAfterFlowInterrupt,
    resumeIntroPresentationAfterInterrupt,
    fetchAndScheduleConversationFlowResume,
  ]);

  const handleSpeechInputResult = useCallback((text) => {
    setInputMessage((prev) => {
      const base = prev.trimEnd();
      const next = base ? `${base} ${text}` : text;
      if (introDelayRef.current && next.trim().length > 0) {
        introDelayRef.current();
      }
      clearAutoAdvanceTimer();
      return next;
    });
  }, []);

  const handleSpeechInputError = useCallback((code) => {
    if (code === 'no-speech') {
      return;
    }
    if (code === 'not-allowed' || code === 'service-not-allowed') {
      releaseMicrophoneAccess();
      setError(t('errors.micPermissionDenied'));
      setMicInputEnabled(false);
      try {
        localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
      } catch {
        /* ignore */
      }
      return;
    }
    if (code === 'system-blocked') {
      releaseMicrophoneAccess();
      setError(t('errors.micSystemBlocked'));
      setMicInputEnabled(false);
      try {
        localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
      } catch {
        /* ignore */
      }
      return;
    }
    if (code === 'audio-capture') {
      setError(t('errors.micNotFound'));
      return;
    }
    if (code === 'not-readable') {
      setError(t('errors.micNotReadable'));
      return;
    }
    if (code === 'stt-not-configured') {
      setError(t('errors.sttNotConfigured'));
      return;
    }
    if (code === 'transcription-failed' || code === 'recording-failed') {
      setError(t('errors.transcriptionFailed'));
      return;
    }
    if (code === 'unsupported' || code === 'insecure') {
      setError(t('errors.micInsecure'));
      return;
    }
    setError(t('errors.voiceInputFailed'));
  }, [t]);

  const {
    isListening: speechInputListening,
    isTranscribing: speechInputTranscribing,
    supported: speechInputSupported,
    usesServerStt: speechInputUsesServerStt,
    start: startSpeechInput,
    stop: stopSpeechInput,
  } = useSpeechInput({
    locale: speechLocaleForAppLanguage(language),
    disabled: !composerAcceptsInput || ttsSuppressesMic,
    cancelCaptureOnDisable: ttsSuppressesMic,
    onResult: handleSpeechInputResult,
    onError: handleSpeechInputError,
  });
  stopSpeechInputRef.current = stopSpeechInput;
  speechInputListeningRef.current = speechInputListening;

  useLayoutEffect(() => {
    syncMessageInputHeight();
  }, [
    speechInputListening,
    speechInputTranscribing,
    isWideViewport,
    language,
    activeOptionsMessageId,
    optionsSubmitted,
    syncMessageInputHeight,
  ]);

  useEffect(() => {
    if (!composerAcceptsInput && speechInputListening) {
      stopSpeechInput({
        transcribe: !ttsAudioPlaying && speechInputUsesServerStt,
        cancelTranscription: ttsAudioPlaying,
        resumeAfter: false,
      });
      return;
    }
    if (!composerAcceptsInput && !speechInputTranscribing && !speechInputListening) {
      stopSpeechInput();
    }
  }, [
    composerAcceptsInput,
    speechInputListening,
    speechInputTranscribing,
    ttsAudioPlaying,
    stopSpeechInput,
  ]);

  /**
   * Safari / mobile: getUserMedia must run in the same tap as the permission prompt.
   * Dictation warmup lives in useSpeechInput.start(); prefs-only enable still probes here.
   */
  const requestMicPermissionAndMaybeStart = useCallback(
    (startListening) => {
      ensureRecordAudioSession();
      if (startListening) {
        enableMicInputPreference();
        return;
      }
      if (shouldUseGetUserMediaForPermission()) {
        const streamPromise = beginGetUserMediaPermission();
        if (!streamPromise) {
          if (typeof window !== 'undefined' && window.isSecureContext === false) {
            applyMicPermissionResult({ granted: false, error: 'insecure' });
          } else {
            applyMicPermissionResult({ granted: false, error: 'unsupported' });
          }
          return;
        }
        void (async () => {
          setMicPermissionPending(true);
          try {
            const result = await finishGetUserMediaPermission(streamPromise);
            applyMicPermissionResult(result);
          } finally {
            setMicPermissionPending(false);
          }
        })();
        return;
      }
      const permissionPromise = beginMicrophonePermissionPrompt(
        speechLocaleForAppLanguage(language),
      );
      void enableMicFromPreferences(permissionPromise);
    },
    [
      language,
      applyMicPermissionResult,
      enableMicFromPreferences,
      enableMicInputPreference,
    ],
  );

  useEffect(() => {
    setMicCapturePreferred(micInputEnabled);
    if (!micInputEnabled) {
      stopSpeechInput({ cancelTranscription: true });
      if (!isIOSDevice()) {
        releaseMicrophoneAccess();
      }
      if (voiceOutputEnabledRef.current) {
        restorePlaybackAudioSession();
        unlockAudioPlayback({ force: isIOSDevice() });
      }
      return;
    }
    ensureRecordAudioSession();
    if (voiceOutputEnabledRef.current) {
      unlockAudioPlayback({ force: isIOSDevice() });
      cancelIntroStaggerReveal();
      if (!skipTtsWakeOnMicEnableRef.current && !showInterfaceGuideRef.current) {
        ttsPipelineWakeRef.current?.();
      }
      skipTtsWakeOnMicEnableRef.current = false;
    }
  }, [micInputEnabled, stopSpeechInput]);

  const prevSpeechListeningRef = useRef(false);
  useEffect(() => {
    if (prevSpeechListeningRef.current && !speechInputListening && !speechInputTranscribing) {
      if (voiceOutputEnabledRef.current) {
        if (isIOSDevice()) {
          if (!micInputEnabledRef.current) {
            restorePlaybackAudioSession();
            unlockAudioPlayback();
          } else {
            // Avoid re-unlock after capture — it retriggers iOS route/AGC drift on the next clip.
            noteIosPostMicCaptureForPlayback();
          }
        }
        ttsPipelineWakeRef.current?.();
      }
    }
    prevSpeechListeningRef.current = speechInputListening;
  }, [speechInputListening, speechInputTranscribing]);

  useEffect(() => {
    if (!composerAcceptsInput || speechInputDisabled) {
      if (speechInputListening) {
        stopSpeechInput({
          transcribe: speechInputUsesServerStt,
          resumeAfter: false,
        });
      } else if (!speechInputTranscribing) {
        stopSpeechInput();
      }
      return;
    }
    // Ref (not state) so a mic tap that stops TTS does not race and cancel the new capture.
    if (ttsPlayingMessageIdRef.current != null && speechInputListening) {
      stopSpeechInput({
        transcribe: speechInputUsesServerStt,
        resumeAfter: false,
      });
    }
  }, [
    composerAcceptsInput,
    speechInputDisabled,
    speechInputListening,
    speechInputTranscribing,
    ttsAudioPlaying,
    stopSpeechInput,
  ]);

  const micUsesHoldToRecord = !isWideViewport;

  const startChatMicRecording = useCallback(() => {
    const interruptingTts = ttsPlayingMessageIdRef.current != null;
    ensureRecordAudioSession();
    if (interruptingTts) {
      skipTtsWakeOnMicEnableRef.current = true;
      stopTtsOnUserInterrupt({ forMicCapture: true });
    }
    startSpeechInput({
      force: true,
      holdMode: true,
    });
    if (!micInputEnabled) {
      setMicCapturePreferred(true);
      enableMicInputPreference();
    }
  }, [
    micInputEnabled,
    stopTtsOnUserInterrupt,
    enableMicInputPreference,
    startSpeechInput,
  ]);

  const beginChatMicHold = useCallback(() => {
    startChatMicRecording();
  }, [startChatMicRecording]);

  const endChatMicHold = useCallback(() => {
    if (!micHoldActiveRef.current) return;
    micHoldActiveRef.current = false;
    micHoldPointerIdRef.current = null;
    stopSpeechInput({
      transcribe: true,
      resumeAfter: false,
    });
  }, [stopSpeechInput]);

  const handleChatMicClick = useCallback(() => {
    if (micUsesHoldToRecord) return;
    if (speechInputListening) {
      stopSpeechInput({
        transcribe: true,
        resumeAfter: false,
      });
      return;
    }
    startChatMicRecording();
  }, [
    micUsesHoldToRecord,
    speechInputListening,
    stopSpeechInput,
    startChatMicRecording,
  ]);

  useEffect(() => {
    if (!micUsesHoldToRecord) return undefined;
    const onGlobalPointerEnd = (event) => {
      if (!micHoldActiveRef.current) return;
      if (
        micHoldPointerIdRef.current != null &&
        event.pointerId !== micHoldPointerIdRef.current
      ) {
        return;
      }
      endChatMicHold();
    };
    window.addEventListener('pointerup', onGlobalPointerEnd);
    window.addEventListener('pointercancel', onGlobalPointerEnd);
    return () => {
      window.removeEventListener('pointerup', onGlobalPointerEnd);
      window.removeEventListener('pointercancel', onGlobalPointerEnd);
    };
  }, [endChatMicHold, micUsesHoldToRecord]);

  const handleChatMicPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      micHoldActiveRef.current = true;
      micHoldPointerIdRef.current = event.pointerId;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      beginChatMicHold();
    },
    [beginChatMicHold],
  );

  const handleChatMicPointerUp = useCallback(
    (event) => {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          /* ignore */
        }
      }
      endChatMicHold();
    },
    [endChatMicHold],
  );

  const showChatMicButton = speechInputSupported;

  // Clear no-response timers (2 min wait message, 4 min stop)
  const clearNoResponseTimers = () => {
    if (waitMessageTimerRef.current) {
      clearTimeout(waitMessageTimerRef.current);
      waitMessageTimerRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  };

  const responseAwaitsUserReply = (response) => {
    const content = String(response?.content || '').trim();
    const hasOptions = Array.isArray(response?.options) && response.options.length > 0;
    return hasOptions || content.includes('?');
  };

  /** Keyword extraction reads LangGraph checkpoint visitor lines — must run before endSession clears it. */
  const finalizeSessionWithKeywordExtraction = useCallback(
    async (sid) => {
      if (!sid) return;
      try {
        const result = await apiService.triggerKeywordExtraction(
          sid,
          apiKey,
          languageForSessionRef.current || 'en'
        );
        if (!result.success) {
          console.error('Keyword extraction failed:', result.error);
        }
      } catch (error) {
        console.error('Error triggering keyword extraction:', error);
      }
      try {
        await apiService.endSession(sid);
      } catch (err) {
        console.warn('endSession after keyword extraction:', err);
      }
    },
    [apiKey]
  );

  /** Orchestrator routed CLOSURE → Agent 6 on the visitor's last message: mirror manual ``triggerClosure`` session end. */
  const applyVisitorInitiatedClosureEffects = useCallback(
    (response, closureBots = []) => {
      if (!response?.visitor_initiated_closure) return;
      clearFollowUpTimer();
      clearAllMemoryIdleTimers();
      clearNoResponseTimers();
      clearAutoAdvanceTimer();
      closedOrStoppedRef.current = true;
      setConversationClosed(true);
      const closureBot = closureBots.find((b) => b?.isClosure) ?? closureBots[0] ?? null;
      haltPostSessionOutput({ pendingClosureBot: closureBot });
      if (!sessionId) return;
      void finalizeSessionWithKeywordExtraction(sessionId);
    },
    [sessionId, finalizeSessionWithKeywordExtraction, haltPostSessionOutput]
  );

  /** Fetch Agent 6 farewell from the backend, or append the local fallback ritual line. */
  const requestClosureMessage = useCallback(async () => {
    const closureEpochStart = navigationEpochRef.current;
    const appendFallbackClosure = () => {
      const fallbackClosure = {
        id: Date.now(),
        type: 'bot',
        content: CLOSURE_FALLBACK_RITUAL[language === 'el' ? 'el' : 'en'],
        timestamp: new Date().toLocaleTimeString(),
        isClosure: true,
      };
      setMessages((prev) => [...prev, fallbackClosure]);
      return fallbackClosure;
    };

    if (!sessionId) {
      return appendFallbackClosure();
    }

    try {
      const response = await apiService.generateText(
        '__closure__',
        null,
        apiKey,
        vectorstoreType,
        customSystemPrompt,
        sessionId,
        resolvePersonaForApi(),
        language,
      );
      if (closureEpochStart !== navigationEpochRef.current) return null;
      if (response.success) {
        const closureMessage = {
          id: Date.now(),
          type: 'bot',
          content: String(response.content ?? '').trim(),
          timestamp: new Date().toLocaleTimeString(),
          isClosure: true,
        };
        if (botMessageIsRenderable(closureMessage)) {
          setMessages((prev) => [...prev, closureMessage]);
          return closureMessage;
        }
      }
    } catch (error) {
      console.error('Error generating closure:', error);
      if (closureEpochStart !== navigationEpochRef.current) return null;
    }

    return appendFallbackClosure();
  }, [
    sessionId,
    apiKey,
    vectorstoreType,
    customSystemPrompt,
    language,
    resolvePersonaForApi,
  ]);

  // Called when 4 min elapsed with no user response: stop agents; show Reflect only if discussion or closing
  const onStopNoResponse = useCallback(async () => {
    closedOrStoppedRef.current = true;
    clearFollowUpTimer();
    clearAllMemoryIdleTimers();
    clearNoResponseTimers();
    setAgentsStoppedDueToTimeout(true);
    const stage = stageWhenWaitingRef.current;
    if (stage !== 'discussion' && stage !== 'closing') return;

    haltPostSessionOutput();

    if (!transcriptHasClosureMessage(messagesRef.current)) {
      setIsLoading(true);
      try {
        await requestClosureMessage();
      } finally {
        setIsLoading(false);
      }
    }

    setConversationClosed(true);
    if (sessionId) {
      void finalizeSessionWithKeywordExtraction(sessionId);
    }
  }, [
    sessionId,
    finalizeSessionWithKeywordExtraction,
    haltPostSessionOutput,
    requestClosureMessage,
  ]);

  const applyClassificationToUi = useCallback(async (response) => {
    const topCategory = response?.top_3_categories?.[0];
    const rawCategory =
      topCategory?.category ||
      response?.ethical_category ||
      null;
    const category = normalizePoleLabel(rawCategory);

    if (!category || category === 'None') return;
    if (!isPoleValidForArtefact(category, effectiveRoutePersona)) return;

    const classificationData = {
      category,
      score: topCategory?.score ?? 1.0,
      description: topCategory?.description || undefined,
      timestamp: new Date().toISOString(),
      isPending: true,
    };

    setImmediateClassifications((prev) => [...prev, classificationData]);
    setLocalEthicalCategories((prev) => {
      const next = { ...prev };
      next[category] = (next[category] || 0) + 1;
      return next;
    });
  }, [effectiveRoutePersona]);

  const resumePendingBotReplyAfterReload = useCallback(
    async ({ prompt, isAutoFollowUp, skipUserPersist }) => {
      if (closedOrStoppedRef.current) return;
      if (!sessionId) return;
      const trimmed = String(prompt ?? '').trim();
      if (!trimmed) return;

      if (trimmed === '__auto_advance__') {
        setPendingAutoAdvance(false);
      }

      const epochStart = navigationEpochRef.current;
      setIsLoading(true);
      setError('');
      chatReloadRestoreRef.current = false;

      try {
        const response = await apiService.generateText(
          trimmed,
          null,
          apiKey,
          vectorstoreType,
          customSystemPrompt || null,
          sessionId,
          resolvePersonaForApi(),
          language,
          isAutoFollowUp,
          false,
          false,
          null,
          null,
          false,
          '',
          false,
          false,
          false,
          skipUserPersist,
        );
        if (epochStart !== navigationEpochRef.current) return;
        if (closedOrStoppedRef.current) return;
        if (!response.success) {
          setError(response.error || 'Failed to generate response');
          return;
        }
        await applyClassificationToUi(response);
        if (response.persona) {
          setCurrentPersona(response.persona);
        }
        if (response.stage) {
          setConversationStage(response.stage);
        }

        const snap = readChatSessionSnapshot();
        const reloadPersonaKey = reloadPersonaKeyFromSnapshot(
          snap,
          personasMapRef.current,
          personaKeyAliasesRef.current,
        );
        const newBots = discussionBotsFromApiResponse(
          response,
          reloadPersonaKey,
          personasMapRef.current,
          language,
        );
        const hasOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        const existingKeys = new Set(
          messagesRef.current.map(reloadMessageContentKey).filter(Boolean),
        );
        const introTexts = resolveCanonicalIntroSegmentTexts(
          reloadPersonaKey,
          personasMapRef.current,
          snap,
          messagesRef.current,
          language,
        );
        const novelBots = filterIntroReplayBots(
          newBots.filter((m) => {
            const key = reloadMessageContentKey(m);
            return key && !existingKeys.has(key);
          }),
          messagesRef.current,
          introTexts,
        );
        const skipIntroOnlyReplay =
          novelBots.length > 0 &&
          introTexts?.length > 0 &&
          novelBots.every((m) => contentMatchesIntroCatalog(m?.content, introTexts)) &&
          (transcriptHasVisitorMessage(messagesRef.current) ||
            transcriptHasAwakeningProgressBeyondStep(messagesRef.current, 'naming', language));
        if (novelBots.length > 0 && !skipIntroOnlyReplay) {
          await deliverDiscussionBotMessages(response, novelBots);
          if (trimmed === '__auto_advance__') {
            setPendingAutoAdvance(false);
          }
          if (hasOptions) {
            const optBubble = [...newBots]
              .reverse()
              .find((m) => Array.isArray(m.options) && m.options.length > 0);
            if (optBubble) {
              setOptionsShown(true);
              setOptionsMessageId(optBubble.id);
              setOptionsSubmitted(false);
            }
          }
        } else if (skipIntroOnlyReplay && trimmed === '__auto_advance__') {
          setChatReloadPendingReply(makeChatReloadAutoAdvanceAction());
        }

        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));
        clearAutoAdvanceTimer();
        if (response.auto_advance_ms && !response.visitor_initiated_closure) {
          setPendingAutoAdvance(true);
          const thinkDelay = Math.min(2000, response.auto_advance_ms);
          preThinkTimerRef.current = setTimeout(() => setIsLoading(true), thinkDelay);
          autoAdvanceRef.current = setTimeout(() => {
            preThinkTimerRef.current = null;
            autoAdvanceRef.current = null;
            handleAutoAdvanceRef.current?.();
          }, response.auto_advance_ms);
        }
      } catch (err) {
        setError(err?.message || 'Failed to generate response');
      } finally {
        setIsLoading(false);
      }
    },
    [
      sessionId,
      apiKey,
      vectorstoreType,
      customSystemPrompt,
      resolvePersonaForApi,
      language,
      applyClassificationToUi,
      deliverDiscussionBotMessages,
    ],
  );

  /** After chat reload / prefs return: continue the in-flight flow step. */
  useEffect(() => {
    if (!chatReloadPendingReply) return;
    if (parseArtefactSessionPath(location.pathname)?.view !== 'chat') return;
    const snap = readChatSessionSnapshot();
    const returningFromPrefs =
      preferencesBeforeDisclaimerDone &&
      hasExistingChatTranscript(messagesRef.current, snap);
    if ((showDisclaimer || showIntroduction) && !returningFromPrefs) return;
    if (!sessionId) return;

    const pending = chatReloadPendingReply;
    setChatReloadPendingReply(null);
    void resumePendingBotReplyAfterReload(pending);
  }, [
    chatReloadPendingReply,
    location.pathname,
    sessionId,
    showDisclaimer,
    showIntroduction,
    preferencesBeforeDisclaimerDone,
    resumePendingBotReplyAfterReload,
  ]);

  // Auto-advance: send a silent advance message so the backend moves to the next awakening step
  // (e.g. after ethical aura response → path_choice with 3s delay)
  const handleAutoAdvance = async () => {
    if (closedOrStoppedRef.current || isLoading) return;

    setPendingAutoAdvance(false);
    const epochStart = navigationEpochRef.current;
    setIsLoading(true);
    let didStaggerMultiBot = false;
    try {
      const response = await apiService.generateText(
        '__auto_advance__',  // special marker — backend recognizes as auto-advance (no user input)
        null,
        apiKey,
        vectorstoreType,
        customSystemPrompt || null,
        sessionId,
        resolvePersonaForApi(),
        language
      );
      if (epochStart !== navigationEpochRef.current) return;
      if (closedOrStoppedRef.current) return;
      if (response.success) {
        const snap = readChatSessionSnapshot();
        const reloadPersonaKey = reloadPersonaKeyFromSnapshot(
          snap,
          personasMapRef.current,
          personaKeyAliasesRef.current,
        );
        const introTexts = resolveCanonicalIntroSegmentTexts(
          reloadPersonaKey,
          personasMapRef.current,
          snap,
          messagesRef.current,
          language,
        );
        const newBots = filterIntroReplayBots(
          discussionBotsFromApiResponse(
            response,
            reloadPersonaKey,
            personasMapRef.current,
            language,
          ),
          messagesRef.current,
          introTexts,
        );
        const hasOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        const skipIntroOnlyReplay =
          newBots.length > 0 &&
          introTexts?.length > 0 &&
          newBots.every((m) => contentMatchesIntroCatalog(m?.content, introTexts)) &&
          (transcriptHasVisitorMessage(messagesRef.current) ||
            transcriptHasAwakeningProgressBeyondStep(messagesRef.current, 'naming', language));
        if (newBots.length > 0 && !skipIntroOnlyReplay) {
          const voicePipeline = voiceOutputEnabled && ttsEnabled;
          if (!voicePipeline) {
            didStaggerMultiBot = await appendBotResponseMessages(
              setMessages,
              setIsLoading,
              newBots,
              false,
              prefetchBotsForVoice,
              null,
              closedOrStoppedRef,
            );
          } else {
            await appendBotResponseMessages(
              setMessages,
              setIsLoading,
              newBots,
              true,
              prefetchBotsForVoice,
              null,
              closedOrStoppedRef,
            );
          }
          if (hasOptions) {
            const optBubble = [...newBots]
              .reverse()
              .find((m) => Array.isArray(m.options) && m.options.length > 0);
            if (optBubble) {
              setOptionsShown(true);
              setOptionsMessageId(optBubble.id);
              setOptionsSubmitted(false);
            }
            setPendingAutoAdvance(false);
          }
        }
        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));
        if (response.stage) {
          setConversationStage(response.stage);
        }
      }
    } catch (error) {
      console.error('[AUTO-ADVANCE] Error:', error);
    } finally {
      if (!didStaggerMultiBot) setIsLoading(false);
    }
  };
  handleAutoAdvanceRef.current = handleAutoAdvance;

  // Function to send a follow-up question automatically (backend controls the prompt)
  const sendFollowUpQuestion = async () => {
    // Don't send follow-ups during introduction — awakening flow handles silence
    if (conversationStageRef.current === 'introduction') return;
    if (closedOrStoppedRef.current || isLoading) return;

    const currentUserMessageCount = countUserMessages(messages);
    if (currentUserMessageCount >= MAX_USER_INTERACTIONS) return;

    const epochStart = navigationEpochRef.current;
    setIsLoading(true);
    setError('');
    let didStaggerMultiBot = false;

    try {
      // Send trigger — backend handles all prompting logic
      const response = await apiService.generateText(
        '__follow_up__',
        null,
        apiKey,
        vectorstoreType,
        customSystemPrompt || null,
        sessionId,
        resolvePersonaForApi(),
        language,
        true  // isAutoFollowUp — do not store messages or update conversation state
      );

      if (epochStart !== navigationEpochRef.current) return;
      if (closedOrStoppedRef.current) return;

      if (response.success) {
        // Don't add follow-up if we already showed the waiting message
        if (waitingMessageShownRef.current) return;

        if (response.persona) setCurrentPersona(response.persona);
        if (response.stage) setConversationStage(response.stage);

        const newBots = discussionBotsFromApiResponse(
          response,
          resolvePersonaKeyForCatalog(response),
          personasMapRef.current,
          language,
        );
        const followUpHasOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        if (newBots.length > 0) {
          const voicePipeline = voiceOutputEnabled && ttsEnabled;
          if (!voicePipeline) {
            didStaggerMultiBot = await appendBotResponseMessages(
              setMessages,
              setIsLoading,
              newBots,
              false,
              prefetchBotsForVoice,
              null,
              closedOrStoppedRef,
            );
          } else {
            await appendBotResponseMessages(
              setMessages,
              setIsLoading,
              newBots,
              true,
              prefetchBotsForVoice,
              null,
              closedOrStoppedRef,
            );
          }
          if (followUpHasOptions) {
            const optBubble = [...newBots]
              .reverse()
              .find((m) => Array.isArray(m.options) && m.options.length > 0);
            if (optBubble) {
              setOptionsShown(true);
              setOptionsMessageId(optBubble.id);
              setOptionsSubmitted(false);
            }
          }
        }

        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));

        clearFollowUpTimer();
        clearNoResponseTimers();
        if (!response.visitor_initiated_closure && !responseAwaitsUserReply(response)) {
          followUpTimerRef.current = setTimeout(() => {
            if (closedOrStoppedRef.current) return;
            sendFollowUpQuestion();
          }, 120000);

          stageWhenWaitingRef.current = response.stage || conversationStage;
          const twoMinMs = 2 * 60 * 1000;
          waitMessageTimerRef.current = setTimeout(() => {
            if (closedOrStoppedRef.current) return;
            waitingMessageShownRef.current = true;
            clearFollowUpTimer();
            setMessages((prev) => [...prev, {
              id: Date.now(),
              type: 'bot',
              content: t('app.waitingForSignal'),
              timestamp: new Date().toLocaleTimeString(),
              waitForSignal: true,
            }]);
            stopTimerRef.current = setTimeout(() => {
              onStopNoResponse();
            }, twoMinMs);
          }, twoMinMs);
        }
      } else {
        setError(response.error || t('errors.failedToGenerateFollowUp'));
      }
    } catch (error) {
      console.error('Error generating follow-up question:', error);
      setError('Failed to generate follow-up question');
    } finally {
      if (!didStaggerMultiBot) setIsLoading(false);
    }
  };

  /** Short idle after user-led inquiry: LangGraph continues same thread (synthetic user turn not stored in DB). */
  async function sendInquirySilencePing() {
    if (conversationStageRef.current === 'introduction') return;
    if (closedOrStoppedRef.current || isLoading) return;

    const currentUserMessageCount = countUserMessages(messages);
    if (currentUserMessageCount >= MAX_USER_INTERACTIONS) return;

    const epochStart = navigationEpochRef.current;
    setIsLoading(true);
    setError('');
    let didStaggerMultiBot = false;
    try {
      const response = await apiService.generateText(
        INQUIRY_SILENCE_TRIGGER,
        null,
        apiKey,
        vectorstoreType,
        customSystemPrompt || null,
        sessionId,
        resolvePersonaForApi(),
        language,
        false,
        true
      );

      if (epochStart !== navigationEpochRef.current) return;
      if (closedOrStoppedRef.current) return;

      if (response.success) {
        if (waitingMessageShownRef.current) return;

        await applyClassificationToUi(response);
        if (response.persona) setCurrentPersona(response.persona);
        if (response.stage) setConversationStage(response.stage);

        const newBots = discussionBotsFromApiResponse(
          response,
          resolvePersonaKeyForCatalog(response),
          personasMapRef.current,
          language,
        );
        const hasOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        if (newBots.length > 0) {
          didStaggerMultiBot = await deliverDiscussionBotMessages(response, newBots);
          if (hasOptions) {
            const optBubble = [...newBots]
              .reverse()
              .find((m) => Array.isArray(m.options) && m.options.length > 0);
            if (optBubble) {
              setOptionsShown(true);
              setOptionsMessageId(optBubble.id);
              setOptionsSubmitted(false);
            }
          }
        }

        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));

        clearFollowUpTimer();
        clearNoResponseTimers();
        const stage = response.stage || conversationStage;
        if (!response.visitor_initiated_closure && stage !== 'introduction') {
          if (response.inquiry_idle_follow_up_ms) {
            inquiryIdleTimerRef.current = setTimeout(() => {
              inquiryIdleTimerRef.current = null;
              if (closedOrStoppedRef.current) return;
              void sendInquirySilencePing();
            }, response.inquiry_idle_follow_up_ms);
          }
          if (!responseAwaitsUserReply(response) && !memoryOpeningPauseActiveRef.current) {
            followUpTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              sendFollowUpQuestion();
            }, 120000);

            stageWhenWaitingRef.current = stage;
            const twoMinMs = 2 * 60 * 1000;
            waitMessageTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              waitingMessageShownRef.current = true;
              clearFollowUpTimer();
              setMessages((prev) => [...prev, {
                id: Date.now(),
                type: 'bot',
                content: t('app.waitingForSignal'),
                timestamp: new Date().toLocaleTimeString(),
                waitForSignal: true,
              }]);
              stopTimerRef.current = setTimeout(() => {
                onStopNoResponse();
              }, twoMinMs);
            }, twoMinMs);
          }
        }
      } else {
        setError(response.error || t('errors.failedToGenerate'));
      }
    } catch (error) {
      console.error('Error sending inquiry silence ping:', error);
      setError(t('errors.failedToConnect'));
    } finally {
      if (!didStaggerMultiBot) setIsLoading(false);
    }
  }

  const handleSendMessage = async () => {
    if (!sessionId) return;
    if (conversationClosed || agentsStoppedDueToTimeout) return;
    const optionsActiveForSend = activeOptionsMessageId != null && !optionsSubmitted;
    if (voiceBlocksComposer && !optionsActiveForSend) {
      return;
    }
    if (!inputMessage.trim()) {
      setError(t('errors.enterMessage'));
      return;
    }

    stopTtsOnUserInterrupt();
    unlockAudioPlayback();

    // Visitor replied after the first memory — forfeit the silent hear-more prompt forever.
    if (firstObserverMemoryDeliveredRef.current && !memoryHearMoreShownRef.current) {
      memoryHearMoreEverShownRef.current = true;
    }

    // Clear the follow-up, no-response, and auto-advance timers when user sends a message
    clearFollowUpTimer();
    clearAllMemoryIdleTimers();
    clearNoResponseTimers();
    clearAutoAdvanceTimer();
    setAgentsStoppedDueToTimeout(false); // Allow conversation to continue
    closedOrStoppedRef.current = conversationClosed;
    waitingMessageShownRef.current = false;

    const trimmedSend = inputMessage.trim();
    const hearMorePrompt = findMemoryHearMorePrompt(messages);
    const anotherMemoryPrompt = findMemoryAnotherMemoryPrompt(messages);
    const lowEnergyAck = isLowEnergyAcknowledgment(trimmedSend);
    const hearMoreAffirmative =
      Boolean(hearMorePrompt) && isAffirmativeMemoryHearMoreReply(trimmedSend);
    const anotherMemoryAffirmative =
      Boolean(anotherMemoryPrompt) && isAffirmativeMemoryHearMoreReply(trimmedSend);
    const memoryPromptAffirmative = hearMoreAffirmative || anotherMemoryAffirmative;
    const answersPriorTurn = shortReplyAnswersPriorArtefactTurn(
      messages,
      trimmedSend,
      {
        activeOptionsMessageId,
        optionsSubmitted,
      },
      language,
    );
    const lowEnergyRoutesToFallback =
      lowEnergyAck && !memoryPromptAffirmative && !answersPriorTurn;
    const priorLowEnergyStreak = countConsecutiveLowEnergyUserMessages(
      messages,
      {
        activeOptionsMessageId,
        optionsSubmitted,
      },
      language,
    );
    const lowEnergyStreak = lowEnergyRoutesToFallback ? priorLowEnergyStreak + 1 : 0;
    const useAgent4Fallback =
      lowEnergyRoutesToFallback && lowEnergyStreak >= LOW_ENERGY_FALLBACK_AFTER_STREAK;
    const hasPendingMemoryQueue = pendingMemoryBotsRef.current.length > 0;
    const memoryOpeningPause = memoryOpeningPauseActiveRef.current;
    const deferQueueForFallback =
      useAgent4Fallback && (hasPendingMemoryQueue || memoryOpeningPause);
    const savedPendingMemoryQueue = deferQueueForFallback
      ? pendingMemoryBotsRef.current.slice()
      : null;

    let didFlushPendingMemory = false;
    if (hasPendingMemoryQueue && !deferQueueForFallback) {
      didFlushPendingMemory = await flushPendingMemoryBots();
    }
    const sendWords = trimmedSend.split(/\s+/).filter(Boolean);
    const minimalContent =
      sendWords.length <= 5 || trimmedSend.length <= 40;
    const resumeAfterLongSilenceMinimal =
      !lowEnergyAck &&
      !hearMoreAffirmative &&
      !anotherMemoryAffirmative &&
      messages.length > 0 &&
      Boolean(messages[messages.length - 1]?.waitForSignal) &&
      minimalContent;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: trimmedSend,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');

    // Check if adding this message will reach the interaction limit
    const currentUserMessageCount = countUserMessages(messages);
    const willReachLimit = (currentUserMessageCount + 1) >= MAX_USER_INTERACTIONS;

    const epochStart = navigationEpochRef.current;
    setIsLoading(true);
    setError('');
    let didStaggerMultiBot = false;

    try {
      // If we've reached the limit, skip text generation but still classify
      if (willReachLimit && !conversationClosed) {
        // Trigger closure immediately without waiting for text generation
        setTimeout(() => {
          if (triggerClosureRef.current) {
            triggerClosureRef.current();
          }
        }, 100);
        setIsLoading(false);
        return;
      }

      // Affirmative reply to hear-more — queue flushed; no fallback / API turn needed.
      if (hearMoreAffirmative && didFlushPendingMemory) {
        memoryHearMoreShownRef.current = false;
        setIsLoading(false);
        return;
      }

      // Low-energy ack after a memory pause: queue continuation only — skip a second LLM turn that repeats it.
      if (
        didFlushPendingMemory &&
        lowEnergyRoutesToFallback &&
        !deferQueueForFallback
      ) {
        setIsLoading(false);
        return;
      }

      const systemPromptToUse = customSystemPrompt || null;
      const memoryHearMoreContinue =
        hearMoreAffirmative ||
        (lowEnergyRoutesToFallback &&
          lowEnergyStreak > 0 &&
          lowEnergyStreak < LOW_ENERGY_FALLBACK_AFTER_STREAK &&
          memoryOpeningPause &&
          !hasPendingMemoryQueue);
      const memoryHearMorePromptText = hearMorePrompt?.content || t('app.memoryHearMore');
      const memoryAnotherMemoryAccept = anotherMemoryAffirmative;

      const textGenerationPromise = apiService.generateText(
        trimmedSend,
        null,
        apiKey,
        vectorstoreType,
        systemPromptToUse,
        sessionId,
        resolvePersonaForApi(),
        language,
        false,
        false,
        resumeAfterLongSilenceMinimal,
        null,
        null,
        memoryHearMoreContinue,
        memoryHearMorePromptText,
        memoryAnotherMemoryAccept,
        deferQueueForFallback,
        lowEnergyRoutesToFallback,
      );
      
      // Wait for text generation (this is the main operation)
      const response = await textGenerationPromise;
      if (epochStart !== navigationEpochRef.current) return;
      if (closedOrStoppedRef.current) return;
      if (response.success) {
        await applyClassificationToUi(response);
        // Update current persona if provided in response
        if (response.persona) {
          // Use the persona key as the display name
          setCurrentPersona(response.persona);
        }

        // Track conversation stage for no-response timeout (introduction → stop only; discussion/closing → stop + Reflect)
        if (response.stage) {
          setConversationStage(response.stage);
        }

        const newBots = discussionBotsFromApiResponse(
          response,
          resolvePersonaKeyForCatalog(response),
          personasMapRef.current,
          language,
        );
        const hasOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        const dedupeContext = [...messagesRef.current, userMessage];
        let deferResult = null;
        if (newBots.length > 0) {
          if (deferQueueForFallback) {
            deferResult = await deliverAgent4ThenAgent2AfterDefer({
              newBots,
              savedPendingMemoryQueue,
              dedupeContext,
              voicePipelineActive: voiceOutputEnabled && ttsEnabled,
              flushPendingMemoryBots,
              clearAllMemoryIdleTimers,
              setMessages,
              setIsLoading,
              prefetchBotsForVoice,
              sessionBlockedRef: closedOrStoppedRef,
            });
            didStaggerMultiBot = deferResult.didStagger;
            if (deferResult.didFlushMemory) {
              didFlushPendingMemory = true;
              pendingMemoryBotsRef.current = [];
              memoryOpeningPauseActiveRef.current = false;
            }
          } else {
            didStaggerMultiBot = await deliverDiscussionBotMessages(response, newBots);
          }
          if (hasOptions && !deferQueueForFallback) {
            const optBubble = [...newBots]
              .reverse()
              .find((m) => Array.isArray(m.options) && m.options.length > 0);
            if (optBubble) {
              setOptionsShown(true);
              setOptionsMessageId(optBubble.id);
              setOptionsSubmitted(false);
            }
          }
        }

        if (
          deferQueueForFallback &&
          deferResult &&
          !deferResult.didFlushMemory &&
          !pendingMemoryBotsRef.current.length
        ) {
          if (!deferResult.didWaitForAgent2Gap) {
            await new Promise((resolve) => setTimeout(resolve, AGENT4_TO_AGENT2_GAP_MS));
          }
          clearAllMemoryIdleTimers();
          setIsLoading(true);
          const contResponse = await apiService.generateText(
            trimmedSend,
            null,
            apiKey,
            vectorstoreType,
            systemPromptToUse,
            sessionId,
            resolvePersonaForApi(),
            language,
            false,
            false,
            false,
            null,
            null,
            true,
            memoryHearMorePromptText,
            false,
            false,
            false,
          );
          if (epochStart !== navigationEpochRef.current) return;
          if (closedOrStoppedRef.current) return;
          if (contResponse.success) {
            const contBots = tagAgent2MemoryBots(
              discussionBotsFromApiResponse(
                contResponse,
                resolvePersonaKeyForCatalog(contResponse),
                personasMapRef.current,
                language,
              ),
            );
            if (contBots.length) {
              const contStagger = await appendBotResponseMessages(
                setMessages,
                setIsLoading,
                contBots,
                voiceOutputEnabled && ttsEnabled,
                prefetchBotsForVoice,
                dedupeContext,
                closedOrStoppedRef,
              );
              didStaggerMultiBot = didStaggerMultiBot || contStagger || true;
              didFlushPendingMemory = true;
              pendingMemoryBotsRef.current = [];
              memoryOpeningPauseActiveRef.current = false;
              clearAllMemoryIdleTimers();
            }
          }
        }

        // Clear any selected options from previous message
        setSelectedOptions([]);

        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));
        
        // Auto-advance: if backend says to auto-send after N ms (e.g. ethical aura → path_choice)
        clearAutoAdvanceTimer();
        if (response.auto_advance_ms && !response.visitor_initiated_closure) {
          setPendingAutoAdvance(true);
          const thinkDelay = Math.min(2000, response.auto_advance_ms);
          preThinkTimerRef.current = setTimeout(() => setIsLoading(true), thinkDelay);
          autoAdvanceRef.current = setTimeout(() => {
            preThinkTimerRef.current = null;
            autoAdvanceRef.current = null;
            handleAutoAdvance();
          }, response.auto_advance_ms);
        }

        // During introduction phase: no follow-up or waiting timers — just wait for user to respond
        // The awakening step flow handles silence gracefully; timers only start post-introduction
        const currentStage = response.stage || conversationStage;
        if (currentStage !== 'introduction' && !closedOrStoppedRef.current) {
          clearFollowUpTimer();
          clearNoResponseTimers();
          if (response.inquiry_idle_follow_up_ms) {
            inquiryIdleTimerRef.current = setTimeout(() => {
              inquiryIdleTimerRef.current = null;
              if (closedOrStoppedRef.current) return;
              void sendInquirySilencePing();
            }, response.inquiry_idle_follow_up_ms);
          }
          if (!responseAwaitsUserReply(response) && !memoryOpeningPauseActiveRef.current) {
            followUpTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              sendFollowUpQuestion();
            }, 120000);

            stageWhenWaitingRef.current = currentStage;
            const twoMinMs = 2 * 60 * 1000;
            waitMessageTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              waitingMessageShownRef.current = true;
              clearFollowUpTimer();
              setMessages((prev) => [...prev, {
                id: Date.now(),
                type: 'bot',
                content: t('app.waitingForSignal'),
                timestamp: new Date().toLocaleTimeString(),
                waitForSignal: true,
              }]);
              stopTimerRef.current = setTimeout(() => {
                onStopNoResponse();
              }, twoMinMs);
            }, twoMinMs);
          }
        }
        
        // Don't clear immediate classification - keep it for blob visualization
        // The blob should not update after text generation completes
        } else {
          setError(response.error || t('errors.failedToGenerate'));
          // Clear immediate classification on error
          // Don't clear classifications - keep them accumulated
      }
    } catch (error) {
      console.error('Error in message handling:', error);
      setError(t('errors.failedToConnect'));
      if (savedPendingMemoryQueue?.length) {
        pendingMemoryBotsRef.current = savedPendingMemoryQueue;
        memoryOpeningPauseActiveRef.current = true;
      }
      // Don't clear classifications on error - keep accumulated data
    } finally {
      if (!didStaggerMultiBot) setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const optionsActive = activeOptionsMessageId != null && !optionsSubmitted;
      if (voiceBlocksComposer && !optionsActive) return;
      if (optionsActive) {
        handleSubmitOptions();
      } else {
        handleSendMessage();
      }
    }
  };

  // Handler for option button clicks.
  const handleOptionClick = (option) => {
    if (conversationClosed || agentsStoppedDueToTimeout || isLoading) return;
    if (activeOptionsMessageId == null || optionsSubmitted) return;

    if (activeOptionsAllowMultiple) {
      const isSelected = selectedOptions.some((o) => o.key === option.key);
      const next = isSelected
        ? selectedOptions.filter((o) => o.key !== option.key)
        : [...selectedOptions, option];
      setSelectedOptions(next);
      setInputMessage(formatSelectedOptionsForInput(next));
      return;
    }

    stopSpeechInput();
    handleSubmitOptions({ chosenOptions: [option] });
  };

  // Handler to submit selected options
  const handleSubmitOptions = async (submitOverrides = {}) => {
    const optionsSource = firstPendingOptionsMessage(messages, answeredOptionMessageIds);
    if (!optionsSource) {
      setOptionsSubmitted(false);
      return;
    }
    if (conversationClosed || agentsStoppedDueToTimeout) return;

    stopTtsOnUserInterrupt();
    unlockAudioPlayback();

    // Mark options as submitted and reset shown flag so new options can appear
    setOptionsSubmitted(true);
    setOptionsShown(false);
    
    // Snapshot selection for the outgoing user line and for persisting chips on the bot message
    const selectionFromState =
      selectedOptions.length === 0
        ? []
        : [...selectedOptions].sort((a, b) => a.key.localeCompare(b.key));
    const chosenSnapshot =
      Array.isArray(submitOverrides.chosenOptions) && submitOverrides.chosenOptions.length > 0
        ? [...submitOverrides.chosenOptions].sort((a, b) => a.key.localeCompare(b.key))
        : selectionFromState;
    const optionIdFromChip =
      chosenSnapshot.length === 1 && chosenSnapshot[0].id != null && String(chosenSnapshot[0].id).trim() !== ''
        ? String(chosenSnapshot[0].id).trim()
        : null;
    const pathChoiceIdFromOption =
      optionIdFromChip && /^(observer|inquirer)$/i.test(optionIdFromChip)
        ? optionIdFromChip
        : null;

    const trimmedFreeText =
      submitOverrides.freeText != null
        ? String(submitOverrides.freeText).trim()
        : inputMessage.trim();
    let optionText;
    if (chosenSnapshot.length === 0) {
      optionText = trimmedFreeText || 'none';
    } else {
      optionText =
        trimmedFreeText ||
        formatSelectedOptionsForInput(chosenSnapshot);
    }
    
    // Create the user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: optionText,
      timestamp: new Date().toLocaleTimeString(),
    };
    
    if (firstObserverMemoryDeliveredRef.current && !memoryHearMoreShownRef.current) {
      memoryHearMoreEverShownRef.current = true;
    }

    clearFollowUpTimer();
    clearAllMemoryIdleTimers();
    clearNoResponseTimers();
    clearAutoAdvanceTimer();
    setAgentsStoppedDueToTimeout(false);
    closedOrStoppedRef.current = conversationClosed;
    waitingMessageShownRef.current = false;

    const lowEnergyAckOpts = isLowEnergyAcknowledgment(optionText);
    const answersPriorTurnOpts = shortReplyAnswersPriorArtefactTurn(
      messages,
      optionText,
      {
        activeOptionsMessageId,
        optionsSubmitted,
      },
      language,
    );
    const lowEnergyRoutesToFallbackOpts =
      lowEnergyAckOpts && !answersPriorTurnOpts;
    const priorLowEnergyStreakOpts = countConsecutiveLowEnergyUserMessages(
      messages,
      {
        activeOptionsMessageId,
        optionsSubmitted,
      },
      language,
    );
    const lowEnergyStreakOpts = lowEnergyRoutesToFallbackOpts
      ? priorLowEnergyStreakOpts + 1
      : 0;
    const useAgent4FallbackOpts =
      lowEnergyRoutesToFallbackOpts &&
      lowEnergyStreakOpts >= LOW_ENERGY_FALLBACK_AFTER_STREAK;
    const hasPendingMemoryQueueOpts = pendingMemoryBotsRef.current.length > 0;
    const memoryOpeningPauseOpts = memoryOpeningPauseActiveRef.current;
    const deferQueueForFallbackOpts =
      useAgent4FallbackOpts &&
      (hasPendingMemoryQueueOpts || memoryOpeningPauseOpts);
    const savedPendingMemoryQueueOpts = deferQueueForFallbackOpts
      ? pendingMemoryBotsRef.current.slice()
      : null;

    let didFlushPendingMemoryOpts = false;
    if (hasPendingMemoryQueueOpts && !deferQueueForFallbackOpts) {
      didFlushPendingMemoryOpts = await flushPendingMemoryBots();
    }

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    
    const submitEpochStart = navigationEpochRef.current;
    setIsLoading(true);
    setError('');
    let didStaggerMultiBot = false;

    try {
      const systemPromptToUse = customSystemPrompt || null;

      if (
        didFlushPendingMemoryOpts &&
        lowEnergyRoutesToFallbackOpts &&
        !deferQueueForFallbackOpts
      ) {
        setIsLoading(false);
        return;
      }
      
      const optWords = optionText.split(/\s+/).filter(Boolean);
      const optMinimal = optWords.length <= 5 || optionText.length <= 40;
      const resumeAfterLongSilenceMinimalOpts =
        !lowEnergyAckOpts &&
        messages.length > 0 &&
        Boolean(messages[messages.length - 1]?.waitForSignal) &&
        optMinimal;

      const memoryHearMoreContinueOpts =
        lowEnergyRoutesToFallbackOpts &&
        lowEnergyStreakOpts > 0 &&
        lowEnergyStreakOpts < LOW_ENERGY_FALLBACK_AFTER_STREAK &&
        memoryOpeningPauseOpts &&
        !hasPendingMemoryQueueOpts;

      const response = await apiService.generateText(
        optionText,
        null,
        apiKey,
        vectorstoreType,
        systemPromptToUse,
        sessionId,
        resolvePersonaForApi(),
        language,
        false,
        false,
        resumeAfterLongSilenceMinimalOpts,
        pathChoiceIdFromOption,
        optionIdFromChip,
        memoryHearMoreContinueOpts,
        '',
        false,
        deferQueueForFallbackOpts,
        lowEnergyRoutesToFallbackOpts,
      );
      
      if (submitEpochStart !== navigationEpochRef.current) return;
      if (closedOrStoppedRef.current) return;

      if (response.success) {
        await applyClassificationToUi(response);
        if (response.persona) {
          setCurrentPersona(response.persona);
        }
        if (response.stage) {
          setConversationStage(response.stage);
        }

        setAnsweredOptionMessageIds((prev) =>
          prev.includes(optionsSource.id) ? prev : [...prev, optionsSource.id]
        );

        setSelectedOptions([]);
        
        const newBots = discussionBotsFromApiResponse(
          response,
          resolvePersonaKeyForCatalog(response),
          personasMapRef.current,
          language,
        );
        const hasNewOptions = newBots.some(
          (m) => Array.isArray(m.options) && m.options.length > 0,
        );
        const dedupeContextOpts = [...messagesRef.current, userMessage];
        let deferResultOpts = null;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optionsSource.id
              ? { ...m, optionsResolvedSelection: chosenSnapshot }
              : m
          )
        );
        if (newBots.length > 0) {
          if (deferQueueForFallbackOpts) {
            deferResultOpts = await deliverAgent4ThenAgent2AfterDefer({
              newBots,
              savedPendingMemoryQueue: savedPendingMemoryQueueOpts,
              dedupeContext: dedupeContextOpts,
              voicePipelineActive: voiceOutputEnabled && ttsEnabled,
              flushPendingMemoryBots,
              clearAllMemoryIdleTimers,
              setMessages,
              setIsLoading,
              prefetchBotsForVoice,
              sessionBlockedRef: closedOrStoppedRef,
            });
            didStaggerMultiBot = deferResultOpts.didStagger;
            if (deferResultOpts.didFlushMemory) {
              didFlushPendingMemoryOpts = true;
              pendingMemoryBotsRef.current = [];
              memoryOpeningPauseActiveRef.current = false;
            }
          } else {
            didStaggerMultiBot = await deliverDiscussionBotMessages(response, newBots);
          }
        }

        if (
          deferQueueForFallbackOpts &&
          deferResultOpts &&
          !deferResultOpts.didFlushMemory &&
          !pendingMemoryBotsRef.current.length
        ) {
          if (!deferResultOpts.didWaitForAgent2Gap) {
            await new Promise((resolve) => setTimeout(resolve, AGENT4_TO_AGENT2_GAP_MS));
          }
          clearAllMemoryIdleTimers();
          setIsLoading(true);
          const contResponse = await apiService.generateText(
            optionText,
            null,
            apiKey,
            vectorstoreType,
            systemPromptToUse,
            sessionId,
            resolvePersonaForApi(),
            language,
            false,
            false,
            false,
            pathChoiceIdFromOption,
            optionIdFromChip,
            true,
            '',
            false,
            false,
            false,
          );
          if (submitEpochStart !== navigationEpochRef.current) return;
          if (closedOrStoppedRef.current) return;
          if (contResponse.success) {
            const contBots = tagAgent2MemoryBots(
              discussionBotsFromApiResponse(
                contResponse,
                resolvePersonaKeyForCatalog(contResponse),
                personasMapRef.current,
                language,
              ),
            );
            if (contBots.length) {
              const contStagger = await appendBotResponseMessages(
                setMessages,
                setIsLoading,
                contBots,
                voiceOutputEnabled && ttsEnabled,
                prefetchBotsForVoice,
                dedupeContextOpts,
                closedOrStoppedRef,
              );
              didStaggerMultiBot = didStaggerMultiBot || contStagger || true;
              didFlushPendingMemoryOpts = true;
              pendingMemoryBotsRef.current = [];
              memoryOpeningPauseActiveRef.current = false;
              clearAllMemoryIdleTimers();
            }
          }
        }

        if (
          hasNewOptions &&
          newBots.some((m) => botMessageIsRenderable(m) && Array.isArray(m.options) && m.options.length > 0)
        ) {
          const optBubble = [...newBots]
            .reverse()
            .find((m) => Array.isArray(m.options) && m.options.length > 0);
          if (optBubble) {
            setOptionsShown(true);
            setOptionsMessageId(optBubble.id);
          }
        }
        setOptionsSubmitted(false);

        applyVisitorInitiatedClosureEffects(response, pickClosureBots(newBots));

        // Auto-advance: if backend says to auto-send after N ms (e.g. ethical aura → path_choice)
        clearAutoAdvanceTimer();
        if (response.auto_advance_ms && !response.visitor_initiated_closure) {
          setPendingAutoAdvance(true);
          const thinkDelay = Math.min(2000, response.auto_advance_ms);
          preThinkTimerRef.current = setTimeout(() => setIsLoading(true), thinkDelay);
          autoAdvanceRef.current = setTimeout(() => {
            preThinkTimerRef.current = null;
            autoAdvanceRef.current = null;
            handleAutoAdvance();
          }, response.auto_advance_ms);
        }

        // During introduction phase: no follow-up or waiting timers — awakening handles silence
        const currentStage = response.stage || conversationStage;
        if (currentStage !== 'introduction' && !closedOrStoppedRef.current) {
          clearFollowUpTimer();
          clearNoResponseTimers();
          if (response.inquiry_idle_follow_up_ms) {
            inquiryIdleTimerRef.current = setTimeout(() => {
              inquiryIdleTimerRef.current = null;
              if (closedOrStoppedRef.current) return;
              void sendInquirySilencePing();
            }, response.inquiry_idle_follow_up_ms);
          }
          if (!responseAwaitsUserReply(response) && !memoryOpeningPauseActiveRef.current) {
            followUpTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              sendFollowUpQuestion();
            }, 120000);

            stageWhenWaitingRef.current = currentStage;
            const twoMinMs = 2 * 60 * 1000;
            waitMessageTimerRef.current = setTimeout(() => {
              if (closedOrStoppedRef.current) return;
              waitingMessageShownRef.current = true;
              clearFollowUpTimer();
              setMessages((prev) => [...prev, {
                id: Date.now(),
                type: 'bot',
                content: t('app.waitingForSignal'),
                timestamp: new Date().toLocaleTimeString(),
                waitForSignal: true,
              }]);
              stopTimerRef.current = setTimeout(() => {
                onStopNoResponse();
              }, twoMinMs);
            }, twoMinMs);
          }
        }
      } else {
        setError(response.error || t('errors.failedToGenerate'));
        setOptionsSubmitted(false);
      }
    } catch (error) {
      console.error('Error submitting options:', error);
      setError(t('errors.failedToConnect'));
      setOptionsSubmitted(false);
    } finally {
      if (!didStaggerMultiBot) setIsLoading(false);
    }
  };

  const handleDisclaimerAcknowledge = async () => {
    const hasExistingChat = messagesRef.current.length > 0;
    if (!hasExistingChat) {
      applySavedMicAndVoicePreferences();
    }
    setMicCapturePreferred(micInputEnabledRef.current);
    if (micInputEnabledRef.current) {
      ensureRecordAudioSession();
    }
    unlockAudioPlayback({ force: true });
    if (!sessionId) {
      await initSession();
    }
    sessionStorage.setItem('disclaimer_acknowledged', 'true');
    setShowDisclaimer(false);
    if (!hasExistingChat) {
      setShowInterfaceGuide(true);
      guideAutoOpenedRef.current = true;
    }
    const chatPath = artefactPath(currentPersonaRef.current, 'chat', personasMap);
    if (location.pathname !== chatPath) {
      navigate(chatPath);
    }
    const introAlreadyDone =
      typeof window !== 'undefined' &&
      sessionStorage.getItem('introduction_completed') === 'true';
    if (!introAlreadyDone && !hasExistingChat) {
      setShowIntroduction(true);
    }
    if (!hasExistingChat) {
      requestAnimationFrame(() => {
        ttsPipelineWakeRef.current?.();
      });
    }
  };

  const handleIntroductionComplete = async () => {
    if (introSequenceInflightRef.current) return;
    if (
      isChatPageReload(location.pathname) &&
      hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())
    ) {
      hydrateInMemoryTranscriptFromSnapshot();
      setShowIntroduction(false);
      return;
    }
    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('introduction_completed') === 'true'
    ) {
      setShowIntroduction(false);
      return;
    }
    if (typeof window !== 'undefined') {
      const snap = readChatSessionSnapshot();
      const snapMsgs = Array.isArray(snap?.messages) ? snap.messages : [];
      if (snap?.sessionId && snapMsgs.length > 0) {
        hydrateInMemoryTranscriptFromSnapshot();
        setShowIntroduction(false);
        if (sessionStorage.getItem('introduction_completed') !== 'true') {
          sessionStorage.setItem('introduction_completed', 'true');
        }
        return;
      }
    }
    if (messagesRef.current.length > 0) {
      setShowIntroduction(false);
      if (
        typeof window !== 'undefined' &&
        sessionStorage.getItem('introduction_completed') !== 'true'
      ) {
        sessionStorage.setItem('introduction_completed', 'true');
      }
      return;
    }
    const snap = readChatSessionSnapshot();
    const reloadPersonaKey = reloadPersonaKeyFromSnapshot(
      snap,
      personasMapRef.current,
      personaKeyAliasesRef.current,
    );
    if (
      transcriptHasIntroDelivery(
        messagesRef.current,
        resolveCanonicalIntroSegmentTexts(
          reloadPersonaKey,
          personasMapRef.current,
          snap,
          messagesRef.current,
          language,
        ),
      ) ||
      transcriptHasVisitorMessage(messagesRef.current)
    ) {
      setShowIntroduction(false);
      if (
        typeof window !== 'undefined' &&
        sessionStorage.getItem('introduction_completed') !== 'true'
      ) {
        sessionStorage.setItem('introduction_completed', 'true');
      }
      return;
    }
    introSequenceInflightRef.current = true;

    try {
    let activeSessionId = sessionId;
    if (!activeSessionId) {
      try {
        activeSessionId = await initSession();
      } catch (e) {
        console.warn('[intro] Could not create session before awakening:', e);
      }
    }
    if (!activeSessionId) return;

    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('introduction_completed') === 'true'
    ) {
      setShowIntroduction(false);
      return;
    }
    setShowIntroduction(false);

    clearAllConversationTimers();
    memoryHearMoreShownRef.current = false;
    memoryHearMoreEverShownRef.current = false;
    firstObserverMemoryDeliveredRef.current = false;
    memoryAnotherMemoryShownRef.current = false;
    pendingMemoryBotsRef.current = [];
    memoryOpeningPauseActiveRef.current = false;

    setIntroAwaitingNamingBubble(true);
    try {
    const introEpochStart = navigationEpochRef.current;
    const wakeVoicePipeline = () => ttsPipelineWakeRef.current?.();

    if (hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())) {
      hydrateInMemoryTranscriptFromSnapshot();
      setIntroAwaitingNamingBubble(false);
      introSequenceInflightRef.current = false;
      return;
    }

    // Start with empty messages — all content comes from backend awakening flow
    setMessages([]);

    // Small opening beat so the first line does not appear unnaturally fast.
    await new Promise((resolve) => setTimeout(resolve, 1200));
    if (introEpochStart !== navigationEpochRef.current) return;

    if (hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())) {
      hydrateInMemoryTranscriptFromSnapshot();
      setIntroAwaitingNamingBubble(false);
      introSequenceInflightRef.current = false;
      return;
    }

    // --- Opening beats: intro segments + naming question shown together ---
    setIsLoading(true);
    let openingMessagesDelivered = false;
    try {
      const firstResponse = await apiService.generateText(
        '__start__',
        null,
        apiKey,
        vectorstoreType,
        customSystemPrompt,
        activeSessionId,
        resolvePersonaForApi(),
        language
      );
      if (introEpochStart !== navigationEpochRef.current) return;

      let namingResponse = null;
      if (firstResponse.success) {
        const firstOpts = firstResponse.options;
        const firstHasOptions = Array.isArray(firstOpts) && firstOpts.length > 0;
        if (!firstHasOptions) {
          namingResponse = await apiService.generateText(
            '__auto_advance__',
            null,
            apiKey,
            vectorstoreType,
            customSystemPrompt,
            activeSessionId,
            resolvePersonaForApi(),
            language
          );
          if (introEpochStart !== navigationEpochRef.current) return;
        }
      }

      if (firstResponse.success) {
        if (firstResponse.persona) {
          setCurrentPersona(firstResponse.persona);
        }
        const firstOpts = firstResponse.options;
        const firstHasOptions = Array.isArray(firstOpts) && firstOpts.length > 0;
        const baseId = Date.now();
        const openingMessages = [];

        if (firstHasOptions) {
          const firstBotMessage = {
            id: baseId,
            type: 'bot',
            content: String(firstResponse.content ?? '').trim(),
            timestamp: new Date().toLocaleTimeString(),
            options: firstResponse.options,
            optionsFooter: firstResponse.options_footer ?? null,
            optionsAllowMultiple: Boolean(firstResponse.options_allow_multiple),
          };
          if (botMessageIsRenderable(firstBotMessage)) {
            openingMessages.push(firstBotMessage);
          }
        } else {
          const introParts = introPartsFromFirstResponse(firstResponse);
          const personaKey = firstResponse.persona
            ? canonicalPersonaKey(
                firstResponse.persona,
                personasMapRef.current,
                personaKeyAliasesRef.current,
              )
            : resolvePersonaForApi();
          const fromPersonaCatalog = introSegmentsFromPersonaCatalog(
            personaKey,
            personasMapRef.current,
          );
          const canonicalIntro = ensureEthicalAuraIntroSegment(
            mergeIntroSegmentSources(introParts, fromPersonaCatalog),
            language,
          );
          if (canonicalIntro.length > 0) {
            setIntroSegmentTexts(canonicalIntro);
          }
          canonicalIntro.forEach((part, i) => {
            openingMessages.push({
              id: baseId + i,
              type: 'bot',
              content: part,
              timestamp: new Date().toLocaleTimeString(),
              awakeningIntroSegment: true,
            });
          });

          if (namingResponse?.success) {
            if (namingResponse.persona) {
              setCurrentPersona(namingResponse.persona);
            }
            const namingOpts = namingResponse.options;
            const namingHasOptions = Array.isArray(namingOpts) && namingOpts.length > 0;
            const namingMessage = {
              id: baseId + canonicalIntro.length,
              type: 'bot',
              content: String(namingResponse.content ?? '').trim(),
              timestamp: new Date().toLocaleTimeString(),
              ...(namingHasOptions && {
                options: namingResponse.options,
                optionsFooter: namingResponse.options_footer ?? null,
                optionsAllowMultiple: Boolean(namingResponse.options_allow_multiple),
              }),
            };
            if (botMessageIsRenderable(namingMessage)) {
              openingMessages.push(namingMessage);
            }
            if (namingResponse.stage) {
              setConversationStage(namingResponse.stage);
            }
          }
        }

        const renderableOpening = openingMessages.filter(botMessageIsRenderable);
        if (renderableOpening.length > 0) {
          setMessages(renderableOpening);
          const revealIds = renderableOpening.map((m) => m.id);
          setTtsRevealedBotIds((prev) => {
            const next = [...prev];
            for (const id of revealIds) {
              if (!next.includes(id)) next.push(id);
            }
            return next;
          });
          prefetchBotsForVoice(renderableOpening);
          if (voiceOutputEnabledRef.current) {
            if (showInterfaceGuideRef.current) {
              deferredGuideCloseTtsRef.current = resolveDeferredGuideCloseTtsMode(
                renderableOpening,
                conversationStageRef.current,
                guideAutoOpenedRef.current,
              );
            } else {
              wakeVoicePipeline();
            }
          }
          openingMessagesDelivered = true;
        }
        if (openingMessagesDelivered) {
          sessionStorage.setItem('introduction_completed', 'true');
        }
      }
    } catch (error) {
      console.error('Error generating opening introduction messages:', error);
    } finally {
      setIsLoading(false);
    }

    setConversationStartTime(Date.now());
    } finally {
      setIntroAwaitingNamingBubble(false);
    }
    } finally {
      introSequenceInflightRef.current = false;
    }
  };

  const triggerClosure = useCallback(async () => {
    if (conversationClosed) return;

    clearAllConversationTimers();

    const stoppedBefore = agentsStoppedDueToTimeout;
    closedOrStoppedRef.current = true;
    haltPostSessionOutput();

    let didClose = false;
    const closureEpochStart = navigationEpochRef.current;
    setIsLoading(true);

    try {
      if (!transcriptHasClosureMessage(messagesRef.current)) {
        await requestClosureMessage();
      }
      if (closureEpochStart !== navigationEpochRef.current) return;
      setConversationClosed(true);
      didClose = true;
    } finally {
      setIsLoading(false);

      if (didClose && sessionId) {
        await finalizeSessionWithKeywordExtraction(sessionId);
      }
    }
    if (!didClose) {
      closedOrStoppedRef.current = stoppedBefore;
    }
  }, [
    conversationClosed,
    agentsStoppedDueToTimeout,
    sessionId,
    finalizeSessionWithKeywordExtraction,
    requestClosureMessage,
    haltPostSessionOutput,
  ]);

  // Update ref whenever triggerClosure changes
  useEffect(() => {
    triggerClosureRef.current = triggerClosure;
  }, [triggerClosure]);

  // Keep ref in sync for timeout callbacks (avoid adding waiting message or stopping after already closed)
  useEffect(() => {
    closedOrStoppedRef.current = conversationClosed || agentsStoppedDueToTimeout;
  }, [conversationClosed, agentsStoppedDueToTimeout]);

  // Cleanup: Clear follow-up, no-response, and auto-advance timers on unmount or when conversation closes
  useEffect(() => {
    return () => {
      clearAllConversationTimers();
    };
  }, []);

  useEffect(() => {
    if (!conversationClosed) return;
    if (reflectEngagedRef.current) return;
    clearAllConversationTimers();
    if (!voiceOutputEnabled || !ttsEnabled) return;
    const pending = findUnplayedClosureBot(messages, playbackFinishedRef.current);
    if (pending) {
      void playClosureNarrationNow(pending);
    }
  }, [conversationClosed, messages, voiceOutputEnabled, ttsEnabled, playClosureNarrationNow]);

  const handleStartNewConversation = async () => {
    if (sessionId) {
      try {
        await apiService.endSession(sessionId);
      } catch (err) {
        console.warn('endSession before new conversation:', err);
      }
    }
    clearPersistedChatSession();
    try {
      sessionStorage.removeItem('disclaimer_acknowledged');
      sessionStorage.removeItem('introduction_completed');
      sessionStorage.removeItem(prefsGateStorageKey(currentPersonaRef.current));
    } catch {
      /* ignore */
    }

    resetInMemoryChatForNewArtefactRun({ skipInitSession: true });
    await initSession();
    navigate(artefactPath(currentPersonaRef.current, 'disclaimer', personasMap), { replace: true });
  };

  /** Bare `/1` etc. (mobile single-artefact landing): new server + tab session. `/1/preferences` does not reset here. */
  const prevBareArtefactLandingRef = useRef(null);
  useLayoutEffect(() => {
    if (!isBareArtefactLandingPath(location.pathname)) {
      prevBareArtefactLandingRef.current = null;
      return;
    }

    const prev = prevBareArtefactLandingRef.current;
    prevBareArtefactLandingRef.current = location.pathname;
    if (prev === location.pathname) return;

    const snap = readChatSessionSnapshot();
    const sid = snap?.sessionId;
    if (sid && typeof sid === 'string') {
      void apiService.endSession(sid).catch(() => {});
    }
    try {
      clearPersistedChatSession();
      sessionStorage.removeItem('disclaimer_acknowledged');
      sessionStorage.removeItem('introduction_completed');
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith('aotf_prefs_gate_')) sessionStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }

    resetInMemoryChatForNewArtefactRun({ skipInitSession: true });
    void initSession();
  }, [location.pathname, resetInMemoryChatForNewArtefactRun, initSession]);

  /** Apply artefact key from URL; sync onboarding modals from path (browser back uses history stack). Aura is not routed. */
  useLayoutEffect(() => {
    const parsed = parseArtefactSessionPath(location.pathname);
    const fromState = location.state?.selectedPersona;
    const rawKey = parsed?.key || fromState;
    if (!rawKey) {
      setPreferencesBeforeDisclaimerDone(true);
      return;
    }
    const personaKeyForSession =
      personasMap && rawKey
        ? canonicalPersonaKey(rawKey, personasMap, personaKeyAliases)
        : rawKey;
    setCurrentPersona(personaKeyForSession);
    loadPersonaInfo(personaKeyForSession);
    if (parsed) {
      const snap = readChatSessionSnapshot();
      const snapshotHasTranscript =
        (Array.isArray(snap?.messages) && snap.messages.length > 0) ||
        Boolean(snap?.conversationClosed);
      const hasTranscript =
        messages.length > 0 || conversationClosed || snapshotHasTranscript;

      if (parsed.view === 'disclaimer') {
        if (consumeFreshArtefactFromIntro()) {
          resetInMemoryChatForNewArtefactRun({ skipInitSession: true });
          void initSession();
        }
        setShowDisclaimer(true);
        setArtefactPrefaceDone(true);
        setPreferencesBeforeDisclaimerDone(true);
        setShowBlobLegend(false);
      } else if (parsed.view === 'aura') {
        navigate(artefactPath(personaKeyForSession, 'chat', personasMap), { replace: true });
        setShowBlobLegend(true);
        return;
      } else if (parsed.view === 'chat') {
        setArtefactPrefaceDone(true);
        let ack = false;
        try {
          ack = sessionStorage.getItem('disclaimer_acknowledged') === 'true';
        } catch {
          ack = false;
        }
        if (!ack && !hasTranscript) {
          navigate(artefactPath(personaKeyForSession, 'disclaimer', personasMap), { replace: true });
          return;
        }
        setPreferencesBeforeDisclaimerDone(true);
        setShowDisclaimer(false);
        const introAlreadyDone =
          typeof window !== 'undefined' &&
          sessionStorage.getItem('introduction_completed') === 'true';
        const reloadSessionPending = isChatViewReloadSessionRestorePending(location.pathname);
        if (hasTranscript) {
          setShowIntroduction(false);
        } else if (ack && !introAlreadyDone && !reloadSessionPending) {
          setShowIntroduction(true);
        }
        setReflectionMode(false);
      }
    }
  }, [
    location.pathname,
    location.state,
    loadPersonaInfo,
    navigate,
    messages.length,
    conversationClosed,
    resetInMemoryChatForNewArtefactRun,
    personasMap,
    personaKeyAliases,
    pauseAutoAdvanceTimerOnly,
  ]);

  const handleDisclaimerBack = useCallback(() => {
    const parsed = parseArtefactSessionPath(location.pathname);
    const key =
      parsed?.key && personasMap
        ? canonicalPersonaKey(parsed.key, personasMap, personaKeyAliases)
        : null;

    if (key && personasMap?.[key]) {
      const seg = artefactRouteSegmentFromPersona(key, personasMap);
      const baseSeg = seg || encodeArtefactPathSegment(key);
      const narrow =
        typeof window !== 'undefined' &&
        window.innerWidth < BARE_ARTEFACT_TABLET_DESKTOP_MIN_WIDTH_PX;
      navigate(narrow ? `/${baseSeg}` : '/');
      return;
    }

    navigate('/');
  }, [navigate, personasMap, personaKeyAliases, location.pathname]);

  /** If tab snapshot has a transcript but in-memory state is empty, hydrate on chat view. */
  useLayoutEffect(() => {
    if (parseArtefactSessionPath(location.pathname)?.view !== 'chat') return;
    if (messages.length > 0) return;
    if (!hasExistingChatTranscript([], readChatSessionSnapshot())) return;
    hydrateInMemoryTranscriptFromSnapshot();
  }, [location.pathname, messages.length, hydrateInMemoryTranscriptFromSnapshot]);

  /** Rebuild intro rows from current backend persona intro_segments after reload. */
  useEffect(() => {
    if (!personasMap) return;
    if (parseArtefactSessionPath(location.pathname)?.view !== 'chat') return;
    const snap = readChatSessionSnapshot();
    const rows = messagesRef.current;
    if (!Array.isArray(rows) || rows.length === 0) return;
    if (transcriptHasVisitorMessage(rows)) return;
    if ((snap?.conversationStage || 'introduction') !== 'introduction') return;

    const personaKey = reloadPersonaKeyFromSnapshot(
      snap,
      personasMap,
      personaKeyAliasesRef.current,
    );
    const backendIntro = resolveCanonicalIntroSegmentTexts(
      personaKey,
      personasMap,
      snap,
      rows,
      language,
    );
    if (backendIntro.length === 0) return;

    const prevIntro = dedupeIntroSegmentTexts(introSegmentTextsRef.current);
    const introChanged =
      backendIntro.map(introSegmentContentKey).join('|') !==
      prevIntro.map(introSegmentContentKey).join('|');
    if (introChanged) {
      setIntroSegmentTexts(backendIntro);
    }

    const snapMsgs = Array.isArray(snap?.messages) ? snap.messages : rows;
    const catalog = resolveFullIntroCatalog(snapMsgs, rows, backendIntro);
    const rowIntroKeys = rows
      .filter((m) => m?.type === 'bot' && messageIsAwakeningIntroSegment(m, backendIntro))
      .map(reloadMessageContentKey)
      .join('|');
    const catalogKeys = catalog.map(reloadMessageContentKey).join('|');
    if (!introChanged && (catalog.length === 0 || rowIntroKeys === catalogKeys)) return;

    const finalized = finalizeReloadedChatMessages(
      rows,
      snapMsgs,
      snap,
      personasMap,
      personaKeyAliasesRef.current,
      languageForSessionRef.current || language,
    );
    setMessages(finalized);
    restoreChatUiAfterFlowInterrupt(finalized);
  }, [personasMap, location.pathname, language, restoreChatUiAfterFlowInterrupt]);

  /**
   * Canonical artefact URLs use route_number (e.g. /1/chat). Legacy slugs (/artificial-womb/chat)
   * redirect once personas load.
   */
  useLayoutEffect(() => {
    if (!personasMap) return;
    const pathname = location.pathname;
    const desired = canonicalArtefactPathFromPathname(pathname, personasMap, personaKeyAliases);
    if (!desired || pathname === desired) return;

    const parsed = parseArtefactSessionPath(pathname);
    if (parsed?.key) {
      const resolved = canonicalPersonaKey(parsed.key, personasMap, personaKeyAliases);
      if (personasMap[resolved] && resolved !== currentPersonaRef.current) {
        setCurrentPersona(resolved);
        void loadPersonaInfo(resolved);
      }
    }

    navigate(desired, { replace: true });
  }, [navigate, location.pathname, personasMap, personaKeyAliases, loadPersonaInfo]);

  // Auto-proceed when introduction state is triggered (modal removed).
  // Re-run when sessionId becomes available — initSession is async on artefact routes.
  useEffect(() => {
    if (!showIntroduction) return;
    if (
      isChatPageReload(location.pathname) &&
      hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())
    ) {
      hydrateInMemoryTranscriptFromSnapshot();
      setShowIntroduction(false);
      return;
    }
    if (hasExistingChatTranscript(messagesRef.current, readChatSessionSnapshot())) {
      hydrateInMemoryTranscriptFromSnapshot();
      setShowIntroduction(false);
      return;
    }
    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('introduction_completed') === 'true'
    ) {
      setShowIntroduction(false);
      return;
    }
    if (isChatViewReloadSessionRestorePending(location.pathname)) {
      setShowIntroduction(false);
      return;
    }
    if (messagesRef.current.length > 0) {
      setShowIntroduction(false);
      return;
    }
    void handleIntroductionComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleIntroductionComplete reads refs + latest sessionId
  }, [showIntroduction, sessionId]);

  // Reload personas when language changes
  useEffect(() => {
    loadPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language changes only; URL persona handled in useLayoutEffect
  }, [language]);

  const chatPage = (
          <div className='App mobile-layout'>
            <header className='App-header overview-page-header'>
              <div className='header-left overview-page-header__brand'>
                <button
                  type='button'
                  className='chat-header-back-link'
                  onClick={() => {
                    void handleTalkWithAnotherArtefact();
                  }}
                  aria-label={t('app.backToOverviewAria')}>
                  <IconArrowLeft className='chat-header-back-link__icon' />
                  <span>{t('app.overview')}</span>
                </button>
              </div>
              <div className='header-right'>
                {!reflectionMode && (
                <div className='layout-toggle-wrapper' role='group' aria-label={`${t('app.chat')} / ${t('app.aura')}`}>
                  <div className='segmented-control'>
                    <button
                      type='button'
                      className={`segmented-control__segment${!showBlobLegend ? ' segmented-control__segment--active' : ''}`}
                      onClick={() => setShowBlobLegend(false)}
                      aria-pressed={!showBlobLegend}>
                      {t('app.chat')}
                    </button>
                    <button
                      type='button'
                      className={`segmented-control__segment${showBlobLegend ? ' segmented-control__segment--active' : ''}`}
                      onClick={() => setShowBlobLegend(true)}
                      aria-pressed={showBlobLegend}>
                      {t('app.aura')}
                    </button>
                  </div>
                </div>
                )}
              </div>
            </header>

            <main className='main-content mobile-layout'>
              {showDisclaimer && onboardingRouteView === 'disclaimer' && (
                <div className='disclaimer-modal'>
                  <div className='disclaimer-content preferences-audio-card onboarding-modal-card'>
                    <button
                      type='button'
                      className='preferences-audio-card__close'
                      onClick={handleDisclaimerBack}
                      aria-label={t('app.backToArtefactLandingAria')}>
                      <span aria-hidden='true'>&times;</span>
                    </button>
                    <h3 className='onboarding-modal-card__title--legal'>{t('disclaimer.title')}</h3>
                    <p className='disclaimer-text'>
                      {t('disclaimer.text')}
                    </p>
                    <div className='preferences-audio-footer'>
                      <button
                        type='button'
                        className='preferences-audio-continue'
                        onClick={handleDisclaimerAcknowledge}>
                        {t('app.understood')}
                      </button>
                    </div>
                  </div>
                </div>
              )}


              {showReflectModal && (
                <div className='introduction-modal'>
                  <div className='introduction-content onboarding-modal-card'>
                    <p className='introduction-text'>
                      {t('introduction.reflectionPrompt')}
                    </p>
                    
                    <p className='slider-suggestion'>{t('introduction.slideMe')}</p>

                    <div className='feeling-sliders'>
                      <div className='slider-group'>
                        <span className='slider-label-left'>{t('introduction.uneasy')}</span>
                        <input
                          type='range'
                          min='0'
                          max='100'
                          value={feelingSliders.excitement}
                          onChange={(e) =>
                            setFeelingSliders((prev) => ({
                              ...prev,
                              excitement: parseInt(e.target.value),
                            }))
                          }
                          className='feeling-slider'
                        />
                        <span className='slider-label-right'>{t('introduction.excited')}</span>
                      </div>

                      <div className='slider-group'>
                        <span className='slider-label-left'>{t('introduction.concerned')}</span>
                        <input
                          type='range'
                          min='0'
                          max='100'
                          value={feelingSliders.hope}
                          onChange={(e) =>
                            setFeelingSliders((prev) => ({
                              ...prev,
                              hope: parseInt(e.target.value),
                            }))
                          }
                          className='feeling-slider'
                        />
                        <span className='slider-label-right'>{t('introduction.hopeful')}</span>
                      </div>
                    </div>

                    <div className='preferences-audio-footer'>
                      <button
                        type='button'
                        className='preferences-audio-continue'
                        onClick={async () => {
                        // Store reflection in database
                        try {
                          const result = await apiService.storeReflection(
                            sessionId,
                            feelingSliders.excitement,
                            feelingSliders.hope
                          );
                          if (!result.success) {
                            console.error('Failed to store reflection:', result.error);
                          }
                        } catch (error) {
                          console.error('Error storing reflection:', error);
                        }
                        
                        setShowReflectModal(false);
                        setShowBlobLegend(true);
                        setReflectionMode(true);
                      }}>
                        {t('app.done')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <>
              <div className='chat-container'>
                <div
                  ref={backgroundVisRef}
                  className='ethical-dimensions-visualization-background'
                >
                  <EthicalDimensionVis
                    key={`${effectiveRoutePersona}:${sessionId ?? 'pending'}`}
                    ref={visContainerRefMobile}
                    sessionId={sessionId}
                    userMessageCount={messages.length}
                    localCategories={localEthicalCategories}
                    immediateClassifications={immediateClassifications}
                    onBlobClick={reflectionMode ? undefined : handleBlobClick}
                    showLegend={showBlobLegend}
                    variant='landing'
                    artefactName={effectiveRoutePersona}
                    downloadHeaderTitle={t('app.title')}
                    downloadArtefactLabel={displayArtefactName}
                    enableCapture={showAuraBlobOverlay}
                  />
                  {showAuraBlobOverlay && blobDownloadButton}
                </div>
                <div
                  ref={chatBodyRef}
                  className={`chat-body${showAuraBlobOverlay ? ' chat-body--aura-pass-through' : ''}`}
                >
                <div
                  ref={chatMessagesRef}
                  className={`chat-messages${showAuraBlobOverlay ? ' chat-messages--aura-pass-through' : ''}`}
                >
                      {messages.length === 0 ? null : (
                    messages.map((message) => {
                      if (
                        message.type === 'user' &&
                        !isDisplayableVisitorMessage(message.content)
                      ) {
                        return null;
                      }
                      if (!botMessageIsRenderable(message)) return null;
                      if (
                        !botMessageVisibleInChat(
                          message,
                          ttsGatesChatReveal,
                          ttsRevealedSet,
                          ttsPlayingMessageId,
                          introStaggerRevealActive,
                        )
                      ) {
                        return null;
                      }
                      return (
                      <div
                        key={message.id}
                        data-message-id={message.id}
                        className={`message ${message.type}`}>
                        <div className='message-content'>
                          <div
                            className={`message-text${
                              message.awakeningIntroMuted ? ' message-text--muted' : ''
                            }`}>
                            {message.type === 'user'
                              ? sanitizeVisitorMessageForDisplay(message.content) ?? ''
                              : message.content}
                          </div>
                          {/* Option chips stay on every bot row that had options; only the FIFO row stays clickable until answered. */}
                          {message.type === 'bot' &&
                        message.options &&
                        message.options.length > 0 && (
                            <div className={`message-options${answeredOptionMessageIds.includes(message.id) ? ' message-options--resolved' : ''}`}>
                              {message.options.map((option) => {
                                const resolved = message.optionsResolvedSelection;
                                const isSelected = Array.isArray(resolved) && resolved.length > 0
                                  ? resolved.some((o) => o.key === option.key)
                                  : selectedOptions.some((o) => o.key === option.key);
                                const isAnswered = answeredOptionMessageIds.includes(message.id);
                                const isFifoActive = message.id === activeOptionsMessageId;
                                const optionsInteractive =
                                  !isLoading && !optionsSubmitted && isFifoActive;
                                return (
                                  <button
                                    key={option.key}
                                    className={`option-button ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleOptionClick(option)}
                                    disabled={isAnswered || !optionsInteractive}
                                  >
                                    <span className='option-key'>{option.key}</span>
                                    <span className='option-label'>{option.label}</span>
                                    {isSelected && (
                                      <span className='option-check'>✓</span>
                                    )}
                                  </button>
                                );
                              })}
                              {message.optionsFooter && (
                                <div className='options-footer'>{message.optionsFooter}</div>
                              )}
                            </div>
                          )}
                          <div className='message-time'>
                            {message.timestamp}
                          </div>
                        </div>
                      </div>
                    );
                    })
                  )}

                  {shouldShowArtefactThinking({
                    isLoading,
                    ttsActive: ttsGatesChatReveal,
                    messages,
                    ttsRevealedSet,
                    ttsPlayingMessageId,
                    pendingMoreBotMessages: memoryOpeningPauseActiveRef.current,
                    conversationFinished,
                    introStaggerRevealActive,
                  }) && (
                    <div className='message bot'>
                      <div className='message-content'>
                        <div className='typing-indicator'>
                          <div className='spinner'></div>
                          <span>{displayArtefactName} {t('app.thinking')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {ttsAutoplayBlocked && (
                  <div className='error-message' role='status'>
                    {t('errors.tapToEnableVoice')}
                  </div>
                )}

                {error && <div className='error-message'>{error}</div>}

                </div>

                <div
                  className={`chat-input-section${showAuraBlobOverlay ? ' chat-input-section--aura-only' : ''}${showAuraBlobOverlay && conversationFinished ? ' chat-input-section--aura-finished' : ''}`}>
                  {showAuraBlobOverlay && conversationFinished ? (
                    <div className='aura-finished-actions'>{talkWithAnotherArtefactButton}</div>
                  ) : !showAuraBlobOverlay && conversationClosed ? (
                    <>
                      <div className='input-container'>
                        <button
                          type='button'
                          className='preferences-audio-continue reflect-open-button'
                          onClick={handleOpenReflectModal}>
                          {t('app.reflect')}
                        </button>
                      </div>
                    </>
                  ) : !showAuraBlobOverlay ? (() => {
                    const optionsActive = activeOptionsMessageId != null && !optionsSubmitted;
                    const chipOnlyOptions =
                      optionsActive &&
                      messageOptionsRequireChipOnly(activeOptionsMessage);
                    const messageInputDisabled =
                      chipOnlyOptions ||
                      voiceBlocksComposer ||
                      (isLoading && conversationStage !== 'introduction');
                    const micButtonDisabled = messageInputDisabled;
                    const messageInputPlaceholder = speechInputListening
                      ? micUsesHoldToRecord
                        ? t('app.voiceRecording')
                        : t('app.voiceRecordingStopClick')
                      : speechInputTranscribing
                        ? t('app.voiceTranscribing')
                        : chipOnlyOptions
                          ? t('app.chooseOne')
                          : micUsesHoldToRecord
                            ? t('app.typeMessage')
                            : t('app.typeMessageClick');
                    const micButtonLabel = micUsesHoldToRecord
                      ? speechInputListening
                        ? t('app.voiceInputStop')
                        : t('app.voiceInputStart')
                      : speechInputListening
                        ? t('app.voiceInputStopClick')
                        : t('app.voiceInputStartClick');
                    return (
                    <>
                      <div className='input-container input-container--chat-voice-row'>
                        <div className='chat-input-voice-wrap'>
                          <button
                            type='button'
                            role='switch'
                            aria-checked={voiceOutputEnabled}
                            className={`chat-voice-circle-button${voiceOutputEnabled ? ' chat-voice-circle-button--on' : ' chat-voice-circle-button--muted'}`}
                            onClick={toggleVoiceOutputPreference}
                            title={voiceOutputEnabled ? t('app.voiceOn') : t('app.voiceOff')}
                            aria-label={voiceOutputEnabled ? t('app.voiceOn') : t('app.voiceOff')}>
                            {voiceOutputEnabled ? (
                              <IconChatVoiceOn className='chat-voice-circle-button__icon' />
                            ) : (
                              <IconChatVoiceOff className='chat-voice-circle-button__icon' />
                            )}
                          </button>
                        </div>
                        <div
                          className={`message-input-pill message-input-pill--expanded${
                            chipOnlyOptions ? ' message-input-pill--chip-only' : ''
                          }`}>
                          <textarea
                            ref={messageInputRef}
                            className={`message-input${
                              chipOnlyOptions ? ' message-input--chip-only' : ''
                            }`}
                            placeholder={messageInputPlaceholder}
                            value={inputMessage}
                            onFocus={() => {
                              composerFocusedRef.current = true;
                              setComposerFocused(true);
                              requestAnimationFrame(() => {
                                syncMessageInputHeight();
                                pinChatScrollToBottom();
                              });
                            }}
                            onBlur={() => {
                              composerFocusedRef.current = false;
                              setComposerFocused(false);
                            }}
                            onChange={(e) => {
                              const next = e.target.value;
                              setInputMessage(next);
                              // Cancel intro delay only on real input (avoids spurious cancels; empty trim = no skip of naming)
                              if (introDelayRef.current && next.trim().length > 0) {
                                introDelayRef.current();
                              }
                              // Cancel auto-advance if user starts typing
                              clearAutoAdvanceTimer();
                              syncMessageInputHeight(e.currentTarget);
                            }}
                            onKeyPress={handleKeyPress}
                            rows={1}
                            disabled={messageInputDisabled}
                          />
                          <div className='message-input-pill__actions'>
                            {showChatMicButton ? (
                              <button
                                type='button'
                                className={`message-input-mic-button send-button--embed${
                                  speechInputListening && !micButtonDisabled
                                    ? ' message-input-mic-button--on'
                                    : micInputEnabled
                                      ? ''
                                      : ' message-input-mic-button--prefs-off'
                                }`}
                                onClick={micUsesHoldToRecord ? undefined : handleChatMicClick}
                                onPointerDown={
                                  micUsesHoldToRecord ? handleChatMicPointerDown : undefined
                                }
                                onPointerUp={
                                  micUsesHoldToRecord ? handleChatMicPointerUp : undefined
                                }
                                onPointerCancel={
                                  micUsesHoldToRecord ? handleChatMicPointerUp : undefined
                                }
                                disabled={micButtonDisabled}
                                aria-pressed={speechInputListening}
                                aria-label={micButtonLabel}
                                title={micButtonLabel}>
                                {micInputEnabled ? (
                                  <IconChatMic className='message-input-mic-button__icon' />
                                ) : (
                                  <IconChatMicOff className='message-input-mic-button__icon' />
                                )}
                              </button>
                            ) : null}
                            <button
                              type='button'
                              className='send-button send-button--embed'
                              onClick={() => {
                                stopSpeechInput();
                                if (optionsActive) handleSubmitOptions();
                                else handleSendMessage();
                              }}
                              disabled={
                                isLoading ||
                                (!optionsActive && voiceBlocksComposer) ||
                                (optionsActive
                                  ? !inputMessage.trim() && selectedOptions.length === 0
                                  : !inputMessage.trim())
                              }
                              aria-label={optionsActive ? t('app.submit') : t('app.send')}
                              title={optionsActive ? t('app.submit') : t('app.send')}>
                              <IconSendUp className='send-button__icon' />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                    );
                  })() : null}
                </div>
              </div>
                </>

            </main>

            {!showDisclaimer && !reflectionMode && !showAuraBlobOverlay && (
              <footer
                className='chat-how-it-works-footer chat-how-it-works-footer--mobile'>
                <button
                  type='button'
                  className='chat-how-it-works-footer__link'
                  onClick={() => {
                    guideAutoOpenedRef.current = false;
                    setShowInterfaceGuide(true);
                  }}>
                  {t('chatGuide.howDoesItWork')}
                </button>
              </footer>
            )}

            <ChatInterfaceGuideSheet
              open={
                showInterfaceGuide &&
                !showDisclaimer &&
                !reflectionMode &&
                !showAuraBlobOverlay
              }
              onClose={handleCloseInterfaceGuide}
              t={t}
              voiceOutputEnabled={voiceOutputEnabled}
              onToggleVoiceOutput={toggleVoiceOutputPreference}
              micInputEnabled={micInputEnabled}
              micPermissionPending={micPermissionPending}
              onToggleMicInput={() => {
                if (micInputEnabled) {
                  toggleMicInputPreference();
                } else if (!micPermissionPending) {
                  requestMicPermissionAndMaybeStart(false);
                }
              }}
            />
          </div>
  );

  return (
    <Routes>
      <Route path='/' element={<IntroPage />} />
      <Route path='/analytics' element={<Navigate to='/' replace />} />
      <Route path='/overview' element={<Navigate to='/' replace />} />
      <Route path='/blob/:code' element={<BlobSnapshotPage />} />
      <Route path='/blob-snapshot/:code' element={<BlobSnapshotPage />} />
      <Route
        path='/chat/:artefactKey'
        element={
          <LegacyChatToV2Redirect
            personasMap={personasMap}
            personaKeyAliases={personaKeyAliases}
          />
        }
      />
      <Route path='/chat' element={<Navigate to='/' replace />} />
      <Route
        path='/:artefactsKey/introduction'
        element={
          <ArtefactOnboardingRedirect
            personasMap={personasMap}
            personaKeyAliases={personaKeyAliases}
          />
        }
      />
      <Route
        path='/:artefactsKey/preferences'
        element={
          <ArtefactOnboardingRedirect
            personasMap={personasMap}
            personaKeyAliases={personaKeyAliases}
          />
        }
      />
      <Route path='/:artefactsKey/disclaimer' element={chatPage} />
      <Route path='/:artefactsKey/chat' element={chatPage} />
      <Route path='/:artefactsKey/aura' element={chatPage} />
      <Route
        path='/:artefactsKey'
        element={
          <ArtefactBareKeyLanding
            personasMap={personasMap}
            personaKeyAliases={personaKeyAliases}
          />
        }
      />
    </Routes>
  );
}

export default App;
